// Lofi Cities: boots the display, switches scenes, drives the UI, the recorder and the audio player.
import { W, H, LOOP, makeCanvas, clamp, pitchOnShow } from './engine/core.js';
import { createDisplay, createHealth, MOTION_FPS, motionFps } from './engine/display.js';
import { WEATHERS, forgetSprites } from './engine/weather.js';
import { exportLoop, savePNG } from './engine/recorder.js';
import { createPip, pipSupported } from './engine/pip.js';
import { createMiniPlayer, miniPlayerSupported, MINI_PRESET } from './ui/miniplayer.js';
import { SCENES, pageTitle, looksOf } from './scenes/registry.js';
import { looksPicked, lookOf, withLook, stepLook } from './ui/looks.js';
import { GUIDES, MAPS, guideHtml, placeLink } from './ui/guide.js';
import { nightCity } from './ui/tour.js';
import { tonightOf } from './ui/tonight.js';
import { FAV_FILTER, createFavourites, favouritesTour, tourPool, stepPool, heartSvg, heartLabel, tourFavsNote, heartToast, tourFavsToast } from './ui/favourites.js';
import { liveUrl, liveParts, allTime, cityCounts, cityLabels, topCity, byPeople, createLive, REACTIONS } from './live/live.js';
import { addLantern, drawLanterns, lanternWait, roomIn, releaseX } from './live/lanterns.js';
import { createBucket, spawnReaction, drawReactions, pickedKind, isTap, sceneSpot, spotOf, COLOURS, TAP_SLOP } from './live/reactions.js';
import {
  SAY_MAX, SAY_CAP, SAY_EVERY_MS, HIDE_CAP, HIDE_EVERY_MS, cleanSay, sayLength, linkIn, isAuthor, nameOf, namesOf, hueOf, ago, messageOf,
  withMessage, historyOf, shown, unreadIn, inChat, inChatLabel, mentionsIn, partsOf, mentionsMe, replyTo, NICK_MIN, NICK_MAX,
  nickRefusal, nickAt, nickOf, isPost, POSTS, SITE, SITE_NAME, RE_EMOJI, RE_CAP, RE_EVERY_MS, reactionsOf, reactorNicks, withReactions,
  withoutReactor, chipsOf, whoLabel, MENTION_DING, dingsFor,
} from './live/chat.js';
import { createEmojiKit, emojiCount } from './live/emoji.js';
import { TR_IDS_MAX, TR_CAP, TR_EVERY_MS, TR_TRIES, TR_RETRY_MS, readersOf, wantsTranslation, translationOf, langName } from './live/translate.js';
import { createChatActivity, messageVisible, chatSnapshot } from './live/chat-analytics.js';
import { shopLinks, shopSale, saleOn } from './ui/shop.js';
import { shareOf, shownLink, SHARE_TARGETS } from './ui/share.js';
import { drawPostcard, trackLine, postcardName, pngFile, sharesPostcards, sendPostcard } from './ui/postcard.js';
import { accountUrl, createAccount } from './live/account.js';
import { BOARD_EVERY_MS, BOARD_TYPES, boardType, fetchBoard, boardRows, boardCells, boardInfo, boardNote } from './live/board.js';
import { STAMP_S, createPassport, passportOf, stampsOf, duration, passportRows, progressLine, hintOf, welcomeBack } from './ui/passport.js';
import { createRequestBoard } from './ui/request-board.js';
import { createGlobe } from './ui/globe.js';
import { regionsOf, cityMatches, fold, pickerOverDock } from './ui/regions.js';
import { createCityRoll } from './ui/city-roll.js';
import { voteView, createVoteCard } from './ui/radio-vote.js';
import { nudgeGroup, weatherChoices, nudgesLeft, nudgeClock, closeNudge, stepNudge, createNudgeCard, TOUR_MINUTES } from './ui/nudge.js';
import { radioChipState } from './ui/radio-chip.js';
import { alertsUrl, createAlerts } from './ui/alerts.js';
import { paintStyleIcon, paintStyleIcons } from './ui/style-icons.js';
import { VOTE_CAP, VOTE_EVERY_MS } from './live/vote.js';
import { NEWS_CHECK_MS, NEWS_TYPES, newsLines, thanksTo, seenOf, seenNow, unseenLines, cameLater, topType, shortDay, byDay } from './ui/news.js';
import { perchOf, pigeonPose, pigeonRect, drawPigeon, DOCK, dockScale, dockPerch } from './ui/pigeon.js';
import { planePose, planeRect, drawPlane } from './ui/plane.js';
import { HALF_MS, slipOf, slotAt, momentNear, countdownOf, minutesTo } from './ui/moments.js';
import { drawMoment, passerPose, passerSpan, drawPasser } from './ui/moments-draw.js';
import { KINDS, SECRET, spotsOf, padOf, hitSpot, answering, answerSpot, drawSpots, drawLayers } from './ui/spots.js';
import { createLayerUi } from './ui/layers.js';
import { ART, UNFOUND, citySecrets, tappable, createFinds, foundOf, foundLine, whereLine, secretHint, paintSticker } from './ui/secrets.js';
import { jobsUrl } from './jobs/jobs.js';
import { createJobsTicker } from './jobs/jobs-ticker.js';
import { BILLBOARDS, LIVE_FEATURES } from './flags.js';
import { initAnalytics, track, trackError, setContext, trackOnExit, trackContentLinks } from './analytics.js';
import { createClock } from './audio/clock.js';
import {
  FOCUS_PRESETS, SLEEP_MINUTES, startFocus, advanceFocus, skipPhase, pauseFocus, resumeFocus, focusLeft, roundsDone, startSleep, sleepStep, countdown,
} from './ui/timers.js';
import { TASKS_MAX, cleanTasks, cleanNotes, tasksFull, addTask, toggleTask, removeTask, clearDone, doneCount, tasksLine } from './ui/notes.js';

const $ = sel => document.querySelector(sel);
// Read before installing any popup/idle callbacks, including on a partially initialized page.
const saveMenu = $('#saveMenu');
function saveOpen() { return saveMenu?.hidden === false; }
const shareMenu = $('#shareMenu');
function shareOpen() { return shareMenu?.hidden === false; }
const settingsEl = $('#settings'), settingsBtn = $('#settingsBtn');
function settingsOpen() { return settingsEl?.hidden === false; }
// Every city has its own page at <root>/<id>/ (written by tools/seo/seo.mjs); the home page is the site root.
const ROOT = new URL('../', import.meta.url);
const pageCity = document.documentElement.dataset.city || null;
const OBS = document.documentElement.classList.contains('obs');   // ?obs: OBS / embed mode, flagged by index.html
const CREATOR = new URLSearchParams(location.search).has('creator');   // ?creator: video export (R, save menu), hidden from visitors for now
const SPONSOR_PITCH = CREATOR;        // the billboard pitch (below): hidden from visitors for now, the own ad invites them to follow on X instead
// The billboard pitch ("Your business here?" over each billboard's own ad, and the card it opens): Netlify's BILLBOARDS
// variable (site/js/flags.js, tools/build/flags.mjs). ?billboards=off|on overrides it for a test. Off, or while hidden
// (SPONSOR_PITCH), the own ad opens the site's X account (@loficitiesapp) instead; in OBS it has nothing to click. It leaves the job face (below) alone.
const billboardsParam = new URLSearchParams(location.search).get('billboards');
const PITCH = SPONSOR_PITCH && !OBS && (billboardsParam == null ? BILLBOARDS : !/^(off|false|0|no|hide)$/i.test(billboardsParam));
// On the website every billboard takes turns between its city's own ad (12 s), "new cities on X" (4 s) and "post a
// job for free" (8 s; render(t, { pitch: 'job' })). The billboard clicks through to what is on show: the X account, the
// Post a job page, or for the own ad the pitch's card or the X account (placePitchSpot). OBS, PNG saves and the loop
// videos keep the city's own ad. With the billboards' job pages switched off on the admin page (siteSwitches, below)
// the face is 'x': the city's own ad in "post a job"'s turn.
const billboardFace = () => (OBS || shownBillboards() !== 'all' ? null : siteSwitches.billboardJobs ? 'job' : 'x');   // render(t, { pitch })
let baseTitle = document.title;                     // the page's own title; a running timer is shown ahead of it
let stored = () => {};                              // a signed-in visitor's account hears each setting saved (account, below)
const store = {
  get(k, d) { try { const v = localStorage.getItem('lofi-cities:' + k); return v == null ? d : JSON.parse(v); } catch { return d; } },
  set(k, v) { try { localStorage.setItem('lofi-cities:' + k, JSON.stringify(v)); } catch { /* storage unavailable */ } stored(k); },
};
// The billboards are the visitor's pick (Settings, or B; kept for the next visit like the weather): 'all' as the site
// runs them (the city's own made-up ad and the job face, and the pitch with their clicks, above), 'city' the city's own ad
// alone with nothing to click, 'off' no billboard at all (render(t, { billboard: false }), as in the loop videos; PNG saves
// too), where a portrait view frames the landmark alone. OBS shows the city's own ad either way: ?obs&nobillboard
// leaves them out (B still switches them through Interact).
const BILLBOARD_MODES = ['all', 'city', 'off'];
const savedBillboards = store.get('billboards', 'all');
let billboards = OBS && new URLSearchParams(location.search).has('nobillboard') ? 'off'
  : BILLBOARD_MODES.includes(savedBillboards) ? savedBillboards : 'all';
// Reactions are the visitor's pick too (Settings; kept for the next visit): shown, or hidden with all of Together, the
// other people's reactions and lanterns over the scene, and sending: no Together button, G and L do nothing and a tap
// on the scene drops nothing. The live count and the chat stay.
let reactionsOn = store.get('reactions', 'on') !== 'off';
// So is the job strip across the top (Settings; kept for the next visit): off leaves it out and asks the worker for no
// jobs. index.html's <head> reads the same key before the first paint (.jobs-off), so the title never jumps up.
let jobStripOn = store.get('job-strip', 'on') !== 'off';
// And the chat's mention ding (Settings, where the chat is; kept for the next visit): on, a message that mentions you
// while you're away from the chat plays a soft ding (dingsFor in site/js/live/chat.js); off, only the Chat button rings.
let mentionSoundOn = store.get('mention-sound', 'on') !== 'off';
// And how many people are out in the cities that can thin their crowd (a scene that exports `crowd`, the share of its
// people it shows by itself; Settings, kept for the next visit): 'calm' as the city has them, 'busy' everyone, 'quiet' a
// few (render(t, { crowd }), withCrowd below; PNG saves too, the loop videos keep the city's own). OBS has no menu:
// ?obs&people=quiet picks one.
const PEOPLE_MODES = ['busy', 'calm', 'quiet'];
const peopleParam = OBS ? new URLSearchParams(location.search).get('people') : null;
let people = [peopleParam, store.get('people', null)].find(m => PEOPLE_MODES.includes(m)) ?? 'calm';
// And how much of it moves (Settings, kept for the next visit): 'smooth' every frame, 'light' a third of them, for a
// slow computer or a battery, 'still' one moment of the loop that stays (stillAt: the one on screen as it is picked,
// STILL_AT s in on a later visit), drawn again once a second so the city's clock, a new pick and what others send over
// it still show (motionFps in site/js/engine/display.js, asked by the render loop). Still also stops whatever reduced
// motion stops (calm()), in the interface too (css .motion-still). It is about what this machine can take, so it stays
// this device's and follows no account. OBS has no menu: ?obs&motion=light picks one.
const MOTION_MODES = Object.keys(MOTION_FPS);
const STILL_AT = 40;
const motionParam = OBS ? new URLSearchParams(location.search).get('motion') : null;
let motion = [motionParam, store.get('motion', null)].find(m => MOTION_MODES.includes(m)) ?? 'smooth';
let stillAt = STILL_AT;
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
const calm = () => motion === 'still' || reduceMotion.matches;
document.documentElement.classList.toggle('motion-still', motion === 'still');
// And how the Vibe panel lays out the styles (the switch beside its Style legend; kept for the next visit): 'grouped', icon tiles
// in three groups; 'list', two columns, icon beside name; 'card', the picked style's card over a grid of icons.
const STYLE_VIEWS = ['grouped', 'list', 'card'];
const savedStyleView = store.get('style-view', 'grouped');
let styleView = STYLE_VIEWS.includes(savedStyleView) ? savedStyleView : 'grouped';
// The admin page can switch the job strip, the billboards' "post a job" pages, the visitors' Billboards setting, the
// billboards themselves and the sale's plane off for everyone (worker/switches.js):
// the live worker says so on connect and whenever it changes. The last word heard is kept for the next visit, which
// starts from it (the <head> script too, for the strip); only a switch on the site's side shows the strip and its pick.
const siteSwitches = { jobStrip: true, billboardJobs: true, billboardSetting: true, billboards: true, salePlane: true };
for (const [k, v] of Object.entries(store.get('switches', {}) ?? {})) if (k in siteSwitches && typeof v === 'boolean') siteSwitches[k] = v;
const stripShown = () => jobStripOn && siteSwitches.jobStrip;
// The billboards on show: the visitor's pick while the admin page lets them pick (billboardsPick), else all of them, or
// none while it has the billboards off (OBS keeps its own: ?obs&nobillboard, B through Interact).
const billboardsPick = () => siteSwitches.billboards && siteSwitches.billboardSetting;
const shownBillboards = () => (OBS || billboardsPick() ? billboards : siteSwitches.billboards ? 'all' : 'off');
document.documentElement.classList.toggle('jobs-off', !stripShown());
const byId = id => SCENES.find(s => s.id === id);

/* ---------- display + the shared scene clock ---------- */
// The scene can go missing while the music and the interface carry on (#81): this file stopped at an error half-way
// through starting up (nothing after that line runs, the boot at its end included), a scene failed to load or throws
// as it draws, the browser lost its graphics context and doesn't bring it back, or something else on the page hides
// the canvas. `health` (site/js/engine/display.js) hears of every frame that reached the screen (sceneShown) and, once
// a second, of the time the page spent drawing: 10 s without a first frame, or 5 s without one later, and a line in the
// middle of the screen says so, with a Reload button (sceneFailed; scene_failed says why). The first frame after that
// takes the line away (sceneBack, scene_recovered). OBS shows no line. All of it is set up here, ahead of everything
// that could stop this file.
const health = createHealth();
let booted = false;                                 // this file ran to its end (the boot)
let startError = null;                              // its first uncaught error: PostHog's own handler isn't listening yet while it runs
addEventListener('error', e => { startError ??= e.error ?? e.message; });
let trouble = null;                                 // { city, name, error }: the scene that failed to load or to draw, for the line
let sceneNote = null, failedAt = 0, veiled = false;
function sceneFailed(reason) {
  const { city, name, error } = trouble ?? {};
  if (!booted) reason = 'boot';
  const why = reason === 'boot' ? startError : error;
  track('scene_failed', { reason, ...(city && { city }), ...(why != null && { error: String(why.message ?? why) }) });
  if (reason === 'boot' && startError) trackError(startError, { failed: 'startup' });   // with its stack trace
  failedAt = performance.now();
  if (reason === 'render') display.blank();           // not the city before, still, under this one's name
  if (OBS) return;
  if (!sceneNote) {
    const reload = Object.assign(document.createElement('button'), { type: 'button', textContent: 'Reload' });
    reload.addEventListener('click', () => location.reload());
    sceneNote = Object.assign(document.createElement('p'), { className: 'pill scene-note' });
    sceneNote.setAttribute('role', 'status');
    sceneNote.append(document.createElement('span'), reload);
    document.body.append(sceneNote);
  }
  sceneNote.firstChild.textContent = reason === 'hidden' ? 'Something is hiding the scene, perhaps a browser extension.'
    : name && (reason === 'load' || reason === 'render') ? `${name} isn't showing. Try another city, or reload.`
    : `The scene isn't showing. A reload may bring it back.`;
  sceneNote.hidden = false;
}
function sceneBack(reason) {
  track('scene_recovered', { reason, after_s: Math.round((performance.now() - failedAt) / 1000) });
  if (sceneNote) sceneNote.hidden = true;
  veiled = false;
}
function sceneShown() {
  const was = health.shown();
  trouble = null;
  if (was) sceneBack(was);
}
// Only time the page spends drawing counts: a hidden tab, or a page that isn't painted, gets no animation frames.
setInterval(() => {
  if (!document.hidden) requestAnimationFrame(() => {
    const reason = health.waited(1000);
    if (reason) sceneFailed(reason);
    if (health.failed || !booted) return;
    // Frames can't tell that the canvas itself is off the page: a stylesheet from outside can hide it, as ad blockers
    // hid the billboard's card (#43).
    const css = $('#screen') && getComputedStyle($('#screen')), hidden = !!css && (css.display === 'none' || css.visibility === 'hidden');
    if (hidden === veiled) return;
    if (hidden) sceneFailed('hidden');
    else sceneBack('hidden');
    veiled = hidden;
  });
}, 1000);
const display = createDisplay($('#screen'), { onLost: contextLost, onRestored: contextRestored });
display.resize();                                   // the window's size from the start: a page that never reaches its boot (fitScreen) would draw every city into one pixel
display.frameBillboard(shownBillboards() !== 'off');   // billboards off: a portrait view frames the landmark alone
// A portrait view that zooms out to fit its landmark and billboard sits between the city title and the dock.
let titleFoot = 0;                                  // CSS px from the top of the page the city title covers
function fitScreen() {
  const title = $('.hud-city').getBoundingClientRect(), dock = measureDock();
  titleFoot = title.height ? title.bottom : 0;
  display.resize({ top: titleFoot, bottom: dock.height ? innerHeight - dock.top : 0 });
}
/**
 * Where the dock starts: the city picker's room above it (--dock-top). Measured again as the picker opens too. With too
 * little room there (pickerOverDock: a phone on its side, a keyboard that shortens the page) the picker lies over the
 * dock instead (.over-dock), down to where the dock ends (--dock-bottom).
 */
function measureDock() {
  const dock = $('.hud-bottom').getBoundingClientRect();
  if (dock.height) {
    document.documentElement.style.setProperty('--dock-top', `${Math.round(dock.top)}px`);
    document.documentElement.style.setProperty('--dock-bottom', `${Math.round(dock.bottom)}px`);
    $('#cityPicker').classList.toggle('over-dock', pickerOverDock($('.hud-city').getBoundingClientRect().top, dock.top));
  }
  return dock;
}
addEventListener('resize', fitScreen);
// iOS Safari moves the dock with its toolbars (shown, hidden, back from the lock screen) without always telling the
// window: the visual viewport hears it.
window.visualViewport?.addEventListener('resize', fitScreen);
// Phones show the city title as a card across the top: the running timers stand under it, however tall its tagline
// and live count make it (--title-h, css #corner).
new ResizeObserver(([{ target }]) => document.documentElement.style.setProperty('--title-h', `${target.offsetHeight}px`))
  .observe($('.hud-city'));
// The P button's window: the desk mini player where the browser has Document Picture-in-Picture (site/js/ui/miniplayer.js:
// the scene with the music's, the cities' and the focus timer's controls), else the scene alone as a video
// (site/js/engine/pip.js). The render loop and the play button treat both alike.
const deskPlayer = miniPlayerSupported();
const pip = deskPlayer
  ? createMiniPlayer({ onChange: onPipChange, read: miniState, toggle: () => togglePlay('mini_player'), step: s => stepCity(s, 'mini_player'), timer: miniTimer })
  : createPip({ onChange: onPipChange });
const orientation = () => (innerWidth < innerHeight ? 'portrait' : 'landscape');
const isTouch = matchMedia('(pointer: coarse)').matches;
const standalone = matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;   // opened as the installed app
// The suggestion cards' group (site/js/ui/nudge.js, below): 'on' or 'holdout', drawn on the first visit and kept (?nudges=on|holdout
// sets it, for a test). None in OBS or ?creator, which never show a card, nor where the browser can't keep it.
let nudges = null;
if (!OBS && !CREATOR) {
  store.set('nudges', nudgeGroup(new URLSearchParams(location.search).get('nudges'), store.get('nudges', null)));
  nudges = store.get('nudges', null);
}
// Every event carries the visitor's job strip, billboards and motion picks (Settings) and the Vibe panel's style view, so
// a chart can split any of them by what was on screen; setJobStrip, setBillboards, setMotion and setStyleView keep them
// current. The cards' group too, so listening splits by it.
initAnalytics({ viewport: orientation(), touch: isTouch, obs: OBS, standalone, job_strip: jobStripOn ? 'on' : 'off', billboards, motion, style_view: styleView, ...(nudges && { nudges }) });
trackContentLinks(() => ({ source_page: 'player', city: target()?.id, music_playing: lastState === 'playing', listened_s: listenedS() }));
// Keep `viewport` current when a phone rotates; the rotation itself shows whether portrait visitors turn sideways to see the whole scene.
let lastOrientation = orientation();
addEventListener('resize', () => {
  const now = orientation();
  if (now === lastOrientation) return;
  lastOrientation = now;
  setContext({ viewport: now });
  if (isTouch) track('orientation_changed', { viewport: now });
});
const clock0 = performance.now();
const sceneTime = () => (performance.now() - clock0) / 1000;      // unbounded seconds (audio syncs events to this)
// What the scene shows at page time `ms`: the loop's own second, or the one a still stays on (Settings' Motion).
const loopAt = ms => (motion === 'still' ? stillAt : (ms - clock0) / 1000 % LOOP);
const loopTime = () => loopAt(performance.now());

/* ---------- scenes (lazy, cached) ---------- */
// A city with more than one look (site/js/ui/looks.js: other scenes of the same city) shows the one picked on its
// title (section "a city's looks", below), kept per city for the next visit. `id` is always the city's; `look` is the
// scene module's id, the same as the city's for its first look.
let looks = looksPicked(store.get('looks', null), SCENES);   // city id -> the look picked, where it isn't the city's first
const loaded = new Map();                       // look id -> Promise<{ id, look, scene, buf, render }>
/** Builds a scene: a buffer of its own and all the scene draws ahead (create). Again for one built before, once the
 *  browser has lost its canvases (contextRestored). */
function buildScene(inst) {
  const [buf, ctx] = makeCanvas(W, H);
  return Object.assign(inst, { buf, render: inst.scene.create(ctx).render });
}
function loadScene(id, look = lookOf(byId(id), looks)) {
  if (!loaded.has(look.id)) {
    const p = look.load().then(mod => buildScene({ id, look: look.id, scene: mod.default }));
    p.catch(() => loaded.delete(look.id));      // allow a retry later
    loaded.set(look.id, p);
  }
  return loaded.get(look.id);
}
// The browser lost its graphics context (site/js/engine/display.js): nothing reaches the screen until it is back,
// usually within a second (sceneFailed's line only shows if it stays away). Back, every <canvas> of the page is blank.
// What the scenes drew ahead (their skies, buildings and sprites) is on canvases that keep their picture where the
// engine has them (core.js makeCanvas: Chrome), and the next frame is whole again. Where it hasn't, they are blank too
// (a black frame with a few stray lights; `drawnAhead`, a dot made like them, tells): the scenes on screen are then
// built again where they are, the others when they are next shown. A scene isn't made to be built twice (some come
// back with brighter lights, or without a sprite their module drew as it loaded), but it is a city again.
const drawnAhead = makeCanvas(1, 1)[1];
drawnAhead.fillRect(0, 0, 1, 1);
let lostAt = 0;
function contextLost() {
  lostAt = performance.now();
  health.blame('context');
}
function contextRestored() {
  const rebuilt = !drawnAhead.getImageData(0, 0, 1, 1).data[3];
  track('scene_context_restored', { lost_ms: Math.round(performance.now() - lostAt), rebuilt });
  if (!rebuilt) return;
  drawnAhead.fillRect(0, 0, 1, 1);
  forgetSprites();
  for (const [look, p] of loaded) p.then(inst => {
    if (inst !== current && inst !== fade?.from && inst !== fade?.to) { if (loaded.get(look) === p) loaded.delete(look); return; }
    try { buildScene(inst); } catch (e) { inst.render = () => { throw e; }; }   // the render loop reports it (frameFailed)
  }, () => {});
}
/** Fetch a city's module (the look it would show) ahead of a pick; building its canvases waits until it's shown. */
const preloadCity = id => { const city = byId(id); return city ? lookOf(city, looks).load().catch(() => {}) : null; };
// Once the first city is on screen, the other cities' code loads too, one per idle moment. A deploy replaces every file,
// so a city first opened after one would bring new scene code into a tab still running the old engine (an import the
// old core.js doesn't export fails every switch to it). Loaded up front, a tab keeps one deploy's copy until it reloads.
// Only the modules load here; a scene is still built the first time it's shown.
function preloadScenes() {
  const idle = window.requestIdleCallback ? cb => requestIdleCallback(cb, { timeout: 2000 }) : cb => setTimeout(cb, 100);
  const rest = SCENES.flatMap(looksOf).filter(look => !loaded.has(look.id));   // every look of every city
  const next = () => { const look = rest.shift(); if (look) idle(() => look.load().catch(() => {}).then(next)); };   // a failure retries when shown
  next();
}

let current = null, fade = null, switchToken = 0, recording = false, cancelRec = false;
const FADE_MS = 1200;
const target = () => (fade ? fade.to : current);

// `look` (setLook) shows another look of a city; left out, the city comes in the look picked for it.
async function showCity(id, { instant = false, source = 'picker', look = null } = {}) {
  const city = byId(id);
  if (!city) return;
  look ??= lookOf(city, looks);
  if (target()?.look === look.id) return;
  const token = ++switchToken;
  const slow = setTimeout(() => { $('#loading').hidden = false; }, 150);
  let inst;
  try {
    inst = await loadScene(id, look);
  } catch (e) {
    console.error(e);
    track('scene_load_failed', { city: id, look: look.id, error: String(e && e.message || e) });
    trackError(e, { failed: 'scene_load', city: id });
    if (target()) toast(`${city.name} isn't ready yet`, 2500);
    else if (token === switchToken) {             // nothing is on screen: sceneFailed's line stays until a city shows, where the toast left a black page
      trouble = { city: id, name: city.name, error: e };
      sceneFailed(health.fail('load'));
    }
    return;
  } finally {
    clearTimeout(slow);
    $('#loading').hidden = true;
  }
  if (token !== switchToken) return;            // a newer switch won
  display.recentre(inst);                         // a city starts at its frame, however far it was dragged last time
  const previous = target()?.id ?? null;
  if (fade) { current = fade.to; fade = null; }
  lookFade = null;                              // a city switch (or its other look) fades in in the picked weather
  if (current && !instant) fade = { from: current, to: inst, start: performance.now() };
  else current = inst;
  if (previous === id) { applyLook(inst); return; }   // the city's other look: the same city for everything below
  applyScene(inst, source);
  session.cities.add(id);
  passportCityShown();                          // the time so far goes to the city before; a returning visitor's welcome
  boardCityShown();                             // an open leaderboard: this city's fans
  track('city_viewed', { city: id, source, previous_city: previous, tonight: id === tonightId() });
  renderTonight();
  tourCityShown(source);
}
/** next (+1) or previous (-1) city, wrapping around */
function stepCity(step, source) {
  const i = SCENES.findIndex(s => s.id === target()?.id);
  showCity(SCENES[(i + step + SCENES.length) % SCENES.length].id, { source });
}

// Moving the URL (the home page goes from / to /<id>/) re-resolves relative links against the new path, so the tab
// icon, the manifest and the city links would 404: pin them to where they point now. Same rule as upOneLevel in
// tools/seo/seo.mjs. Stylesheets are left alone: they have loaded, and a new href would load them again.
function pinRelativeLinks() {
  for (const el of document.querySelectorAll('a[href], link[href]:not([rel~="stylesheet"])')) {
    const href = el.getAttribute('href');
    if (!/^(?:[a-z][a-z0-9+.-]*:|\/|#)/i.test(href)) el.setAttribute('href', new URL(href, document.baseURI).href);
  }
}

let cityPicked = false;                             // the home page keeps its URL and title until a city is picked
/** What belongs to the scene on screen, a city's look: its accent and tagline, its billboard's spot, its sounds, its own weather. */
function applyLook(inst) {
  const { scene } = inst;
  document.documentElement.style.setProperty('--accent', scene.accent);
  $('#tagline').textContent = scene.tagline;
  $('#screen').setAttribute('aria-label', `Animated pixel-art scene: ${scene.name}, ${scene.tagline}`);
  if (cityPicked) baseTitle = pageTitle(scene.name, scene.tagline);
  updateTitle();
  setContext({ look: inst.look });
  renderLooks();
  renderMoment();                                   // the countdown is this scene's: its own clock, its own moment
  renderPeople();                                   // Settings' People row, where this look can thin its crowd
  placePitchSpot();
  spotList.length = 0;                              // the old scene's spots stop answering with it
  layerUi.scene(inst);                              // and its layers go off: the new scene's things have their own
  audio.setScene(scene, sceneTime);
  renderMixer();                                    // an open mixer shows the new scene's sounds
  renderWeather();
  pip.refresh?.();                                  // the desk mini player's city and accent
}
function applyScene(inst, source) {
  const { scene } = inst;
  $('#cityName').textContent = scene.name;
  renderFavs();                                     // its heart beside the name
  cityRoll.stop();                                  // a hint rolling over the old name goes with it
  $('#cityBtnName').textContent = scene.name;
  for (const b of cards) {                          // the city on screen, lit in the picker
    if (b.dataset.id === inst.id) b.setAttribute('aria-current', 'true');
    else b.removeAttribute('aria-current');
  }
  guideBtn.hidden = !GUIDES.includes(inst.id);      // the city guide's pill, in a city that has a guide
  if (guide.open && guideBtn.hidden) guide.close();  // a tour moved on under the open guide
  else if (guide.open) renderGuide();
  const aboutCity = $('#aboutCity');                // a city page's own section of the about panel
  if (aboutCity) aboutCity.hidden = aboutCity.dataset.city !== inst.id;
  const onItsPage = source !== 'initial' || inst.id === pageCity;   // the URL is this city's page (not the home page)
  for (const a of document.querySelectorAll('#about a[data-city]')) {   // light up the city on screen in the city cards
    if (a.dataset.city === inst.id) a.setAttribute('aria-current', onItsPage ? 'page' : 'true');
    else a.removeAttribute('aria-current');
  }
  if (source !== 'initial') {
    cityPicked = true;
    pinRelativeLinks();
    const url = new URL(inst.id + '/', ROOT);
    if (OBS) url.search = location.search;          // OBS reloads the current URL when it refreshes a source
    else if (billboardsParam != null) url.searchParams.set('billboards', billboardsParam);   // a reload keeps the test
    try { history.replaceState(null, '', url.href); }
    catch { history.replaceState(null, '', '#' + inst.id); }   // file:// can't change the path
  }
  store.set('city', inst.id);
  setContext({ city: inst.id });
  reactionList.length = 0;                          // the old city's sky goes with it; the worker sends the new one's
  lanternList = [];
  resetChat();                                      // the chat stays: it's one room for every city
  live?.setCity(inst.id);
  renderLive();
  renderCardsLive();
  renderTogether();
  tickClocks();
  renderShop(inst.id);
  renderShare(inst.id);
  applyLook(inst);
}

function renderShop(city) {
  const links = shopLinks(city);
  for (const [selector, url] of [['#shopLink', links.store], ['#buyCityLoop', links.city], ['#shopCollections', links.store]]) {
    const a = $(selector);
    a.hidden = !url;
    if (url) a.href = url;
    else a.removeAttribute('href');
  }
  const name = byId(city).name;
  $('#buyCityLoop .pop-title').textContent = `Get the ${name} video loop`;
  $('#buyCityLoop').setAttribute('aria-label', `Get the ${name} video loop on Gumroad (opens in a new tab)`);
}

/* ---------- city picker: a chip for the city on screen opens every city, with previews and live local times ---------- */
// A search (site/js/ui/regions.js cityMatches) finds cities by name, country or region; the region chips under it keep
// one region. Both reset as the picker opens, and both apply to the grid and the globe.
// The previews are site/img/thumbs/<id>.webp (node tools/browser/cdp.mjs thumbs); they load the first time the panel shows. Each
// preview says how many people are in that city right now, like the live pill (renderCardsLive), and the cities with
// the most people come first (sortCards).
// A city with more than one look shows the preview of the one picked (site/img/thumbs/<look id>.webp).
const thumbOf = id => new URL(`img/thumbs/${lookOf(byId(id), looks).id}.webp`, ROOT).href;
const timeFmt = new Map();
function localTime(tz) {
  if (!timeFmt.has(tz)) timeFmt.set(tz, new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit' }));
  return timeFmt.get(tz).format(new Date());
}
const pickerEl = $('#cityPicker'), cityBtn = $('#cityBtn'), cityFind = $('#cityFind'), cityList = $('#cityList'), cityNone = $('#cityNone');
const pickerOpen = () => !pickerEl.hidden;
// Beside the search, a close button that shows while the picker lies over the dock (.over-dock, measureDock): the city
// chip that closes it is under it then.
const findRow = document.createElement('div'), pickerClose = document.createElement('button');
findRow.className = 'find-row';
pickerClose.type = 'button'; pickerClose.className = 'icon-btn pick-close'; pickerClose.title = 'Close (Esc)';
pickerClose.setAttribute('aria-label', 'Close');
pickerClose.innerHTML = '<svg aria-hidden="true"><use href="#i-close"/></svg>';
cityFind.replaceWith(findRow);
findRow.append(cityFind, pickerClose);
$('#cityCount').textContent = `${SCENES.length} cities · local times`;
const cards = SCENES.map((s, i) => {
  const li = document.createElement('li'), b = document.createElement('button');
  const key = i < 9 ? String(i + 1) : i === 9 ? '0' : '';   // 1–9, then 0
  b.type = 'button'; b.className = 'city-card'; b.dataset.id = s.id; b.title = key ? `${s.name} (${key})` : s.name;
  b.innerHTML = '<img alt="" width="320" height="180" loading="lazy" decoding="async"><span></span><b class="card-live" hidden></b><small></small><i class="card-country"></i>';
  b.querySelector('img').src = thumbOf(s.id);
  b.querySelector('span').textContent = s.name;
  b.querySelector('i').textContent = s.country;
  b.addEventListener('click', () => { showCity(s.id, { source: pickerView === 'passport' ? 'passport' : 'picker' }); closePicker(); });
  // Fetch the module on hover; building its canvases here stalls the current scene as the pointer crosses cards.
  b.addEventListener('pointerenter', () => preloadCity(s.id), { once: true });
  li.append(b);
  cityList.append(li);
  return b;
});
const cityIds = SCENES.map(s => s.id);
const favs = createFavourites({ ids: cityIds, read: () => store.get('favourites', []), write: v => store.set('favourites', v) });   // section "favourite cities"
let gridOrder = cards;                              // the cards as the grid shows them (sortCards)
// Above the grid, the city with the most people right now: a wide card with its preview, its count and its local
// time (renderFeatured). Its own card leaves the grid while it shows (filterCities). Picks from it count as city_viewed
// with source featured.
const featuredItem = document.createElement('li'), featuredCard = document.createElement('button');
let featuredId = null;
featuredItem.className = 'featured-item'; featuredItem.hidden = true;
featuredCard.type = 'button'; featuredCard.className = 'city-card city-featured';
featuredCard.innerHTML = '<img alt="" width="320" height="180" loading="lazy" decoding="async"><b class="card-live"></b>'
  + '<span class="feat-text"><em>Most people right now</em><strong></strong><small></small></span>';
featuredCard.addEventListener('click', () => { showCity(featuredCard.dataset.id, { source: 'featured' }); closePicker(); });
featuredCard.addEventListener('pointerenter', () => preloadCity(featuredCard.dataset.id));
// → and ↓ go on to the grid, ↑ back to the search; ← stays (the global arrows would switch city).
featuredCard.addEventListener('keydown', e => {
  const to = { ArrowRight: gridCards()[0], ArrowDown: gridCards()[0], ArrowUp: cityFind, ArrowLeft: featuredCard }[e.key];
  if (!to) return;
  e.preventDefault();
  e.stopPropagation();
  to.focus();
});
featuredItem.append(featuredCard);
cityList.prepend(featuredItem);
// The last card, always shown (searches and the Passport too), asks for a city that isn't here yet: a lit pixel billboard
// (site/js/ui/request-board.js) that opens Buy Me a Coffee, where a coffee's message names the city, and counts like the
// header's coffee link (author_link_clicked, network buymeacoffee, location city_picker, view grid or passport; network
// x-app 2026-09-29 to 2026-10-10, discord 2026-09-28 to 2026-09-29, x before). The globe has its own billboard in the
// corner under the Earth, across from the zoom buttons (view globe; a text link before 2026-10-11).
const REQUEST_URL = 'https://buymeacoffee.com/safaelmali';
const requestBoard = createRequestBoard({ still: calm }), globeBoard = createRequestBoard({ still: calm });
const requestCard = document.createElement('a'), globeRequest = $('#globeRequest');
requestCard.className = 'city-card city-request';
requestCard.href = REQUEST_URL; requestCard.target = '_blank'; requestCard.rel = 'noopener';
globeRequest.href = REQUEST_URL;
globeRequest.prepend(globeBoard.canvas);
requestCard.title = 'Request a city: buy me a coffee, name it in your message and get thanked by name when it arrives';
requestCard.setAttribute('aria-label', 'Request a city: buy me a coffee, name it in your message and get thanked by name when it arrives (opens in a new tab)');
requestCard.innerHTML = '<div class="req-art"></div><span>Request a city</span><small>with a coffee</small>';
requestCard.firstChild.append(requestBoard.canvas);
const reportRequest = e => {
  if (e.type === 'auxclick' && e.button !== 1) return;
  track('author_link_clicked', {
    network: 'buymeacoffee', location: 'city_picker', view: e.currentTarget === globeRequest ? 'globe' : pickerView, city: target()?.id,
    music_playing: lastState === 'playing', listened_s: listenedS(),
  });
  if (e.type === 'click') closePicker();
};
for (const a of [requestCard, globeRequest]) {
  a.addEventListener('click', reportRequest);
  a.addEventListener('auxclick', reportRequest);
}
const requestItem = document.createElement('li');
requestItem.append(requestCard);
cityList.append(requestItem);
// The Passport view's first row (renderPassport): what the stamps are and how many there are.
const passportItem = document.createElement('li');
passportItem.className = 'passport-intro'; passportItem.hidden = true;
passportItem.innerHTML = '<svg aria-hidden="true"><use href="#i-stamp"/></svg><strong>Your city passport</strong><p></p><small></small>'
  + '<button class="passport-save" type="button" hidden>Keep it on every device · Sign in</button>';   // renderAccount
cityList.prepend(passportItem);
const shownCards = () => gridOrder.filter(b => !b.parentElement.hidden);
const gridCards = () => [...shownCards(), requestCard];   // what the arrow keys move through
// The picker shows the cities on a globe (site/js/ui/globe.js) or as the grid of previews above: Globe | Grid in its
// head, kept for the next visit ('picker-view'; a first visit gets the globe). The globe is built the first time it
// shows (it works out its city lights then). Its picks count as city_viewed with source globe; a switch counts as
// city_picker_view_changed. Passport, before them, is a third view: the grid's cards as the city passport's stamps
// (renderPassport), picks from it count with source passport. Pressed, it's kept as well ('picker-passport', this
// device's), so the picker opens on it again until it's pressed once more or Globe or Grid is picked; a toast's See
// passport shows it for that open only.
const PICKER_VIEWS = ['globe', 'grid'];
const globeView = $('#globeView'), viewButtons = [...pickerEl.querySelectorAll('.view-switch button')], passportBtn = $('#passportBtn');
const boardEl = $('#board'), boardBtn = $('#boardBtn');   // the leaderboards' panel (section "leaderboards"): every popup closes the others
const savedPickerView = () => (PICKER_VIEWS.includes(store.get('picker-view', 'globe')) ? store.get('picker-view', 'globe') : 'globe');
const keptPickerView = () => (passport && store.get('picker-passport', false) === true ? 'passport' : savedPickerView());
let pickerView = savedPickerView();
let globe = null, globeLive = { counts: {}, labels: {}, top: null, on: false };
function theGlobe() {
  if (globe) return globe;
  globe = createGlobe(globeView, {
    cities: SCENES, here: () => target()?.id, accent: () => target()?.scene.accent, localTime,
    thumb: thumbOf, touch: isTouch, reduced: calm,
    onPick: id => { showCity(id, { source: 'globe' }); closePicker(); },
    onPreview: preloadCity,
    tonight: tonightId,
    stamped: id => stampedIds.has(id),              // the city passport's stamps (renderStamps)
  });
  globe.setLive(globeLive);
  return globe;
}
// The open view's Request a city billboard plays (the globe's, or the grid's and the Passport's card); the other rests.
function startRequestBoard() {
  const [on, off] = pickerView === 'globe' ? [globeBoard, requestBoard] : [requestBoard, globeBoard];
  off.stop();
  on.start(getComputedStyle(document.documentElement).getPropertyValue('--accent').trim());
}
function setPickerView(view) {
  const was = pickerView;
  pickerView = view;
  for (const b of viewButtons) b.setAttribute('aria-pressed', String(b.dataset.view === view));
  passportBtn.setAttribute('aria-pressed', String(view === 'passport'));
  const onGlobe = view === 'globe';
  pickerEl.classList.toggle('globe-on', onGlobe);
  pickerEl.classList.toggle('passport-on', view === 'passport');
  globeView.hidden = !onGlobe;
  cityList.hidden = onGlobe;
  if (!pickerOpen()) return;
  if (onGlobe) theGlobe().open();
  else globe?.close();
  startRequestBoard();
  const reorder = view === 'passport' || was === 'passport';
  if (reorder) sortCards();                         // the stamps first, or back to the grid's order
  filterCities();                                   // the search carries over
  if (!onGlobe && (reorder || was === 'globe')) scrollToHere();
}
// The grid or the passport shows the city on screen (its lit card, applyScene): from the top (the featured card, the
// passport's intro) while its card is in the first screenful, else with its card in the middle. A search or a region
// that hides it leaves the top.
function scrollToHere() {
  cityList.scrollTop = 0;
  const card = cards.find(b => b.dataset.id === target()?.id);
  if (!card || card.parentElement.hidden) return;
  const list = cityList.getBoundingClientRect(), r = card.getBoundingClientRect();
  if (r.bottom <= list.bottom) return;
  cityList.scrollTop = r.top - list.top - (list.height - r.height) / 2;
}
setPickerView(pickerView);
const headButtons = [passportBtn, ...viewButtons];
for (const b of viewButtons) {
  b.addEventListener('click', () => {
    if (b.dataset.view === pickerView) return;
    store.set('picker-view', b.dataset.view);
    if (store.get('picker-passport', false)) store.set('picker-passport', false);   // out of a kept Passport
    setPickerView(b.dataset.view);
    track('city_picker_view_changed', { view: pickerView });
  });
}
passportBtn.addEventListener('click', () => {       // kept for the next open; pressed again: back to Globe or Grid
  const on = pickerView !== 'passport';
  store.set('picker-passport', on);
  setPickerView(on ? 'passport' : savedPickerView());
  track('city_picker_view_changed', { view: pickerView });
});
for (const b of headButtons) {
  b.addEventListener('keydown', e => {              // ← → along Passport, Globe and Grid, not to another city
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    e.stopPropagation();
    const all = headButtons.filter(x => !x.hidden);
    all[(all.indexOf(b) + (e.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length].focus();
  });
}
// Most people first (byPeople), as the picker opens: never while it's open, so no card moves under the pointer. The
// featured city wins its tie, so a search that hides the featured card lists it first. Without a count, the registry's
// order (← →, 1–9). The Passport view has its own order (passportRows: the stamps first), as it opens.
function sortCards() {
  const ids = pickerView === 'passport' ? renderPassport().map(r => r.id)
    : byPeople(liveCounts, target()?.id, featuredId ? [featuredId, ...cityIds.filter(id => id !== featuredId)] : cityIds);
  gridOrder = ids.map(id => cards[cityIds.indexOf(id)]);
  requestItem.before(...gridOrder.map(b => b.parentElement));
}
function filterCities() {
  const q = fold(cityFind.value.trim()), onPassport = pickerView === 'passport';
  featuredItem.hidden = !featuredId || q !== '' || region !== null || onPassport;   // a search or a region shows the grid alone
  passportItem.hidden = !onPassport;                // the Passport view: its intro
  for (const [i, b] of cards.entries()) {            // the featured city isn't in the grid below it again
    const s = SCENES[i];
    b.parentElement.hidden = !cityMatches(s, q) || (region !== null && !inRegion(s)) || (!featuredItem.hidden && s.id === featuredId);
  }
  const none = !shownCards().length;
  cityNone.hidden = !none;
  if (none) cityNone.textContent = `No city called "${cityFind.value.trim()}"${region === FAV_FILTER ? ' in your favourites' : region ? ` in ${region}` : ''} yet`;
  // the globe dims the rest; a search picks its first match (the grid's order), a region turns to the whole region
  if (pickerView === 'globe' && pickerOpen()) theGlobe().find(q || region ? shownCards().map(b => b.dataset.id) : null, { pick: q !== '' });
}
// The region chips: All, Favourites (the hearted cities, while there are any: renderFavs), then each region with its
// number of cities (regionsOf, from the registry). Picking one counts as city_picker_region_picked.
const regionRow = $('#regionChips');
let region = null;
const inRegion = s => (region === FAV_FILTER ? favs.has(s.id) : s.region === region);
const regionChips = [{ region: null, count: SCENES.length }, { region: FAV_FILTER, name: 'Favourites', count: 0 }, ...regionsOf(SCENES)].map(r => {
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'region-chip'; b.dataset.region = r.region ?? '';
  b.append(r.name ?? r.region ?? 'All', Object.assign(document.createElement('small'), { textContent: r.count }));
  b.addEventListener('click', () => {
    if (region === r.region) return;
    setRegion(r.region);
    track('city_picker_region_picked', { region: r.region ?? 'all', view: pickerView });
  });
  b.addEventListener('keydown', e => {              // ← → along the chips, not to another city
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    e.stopPropagation();
    const all = [...regionRow.children].filter(x => !x.hidden);
    all[(all.indexOf(b) + (e.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length].focus();
  });
  regionRow.append(b);
  return b;
});
function setRegion(r) {
  region = r;
  for (const b of regionChips) b.setAttribute('aria-pressed', String((b.dataset.region || null) === r));
  filterCities();
}
function openPicker(trigger, view = keptPickerView()) {
  closeBoard();
  closeQueue();
  closeTimers();
  closeTour();
  closeWeather();
  closeVibe();
  closeMixer();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  document.body.classList.remove('ui-off');
  cityRoll.stop();
  store.set('picker-opened', true);                 // found it: the chip's name stops rolling (rollHint)
  measureDock();                                    // its room now, not at the last resize (iOS moves the dock quietly)
  cityFind.value = '';
  region = null;
  for (const b of regionChips) b.setAttribute('aria-pressed', String(!b.dataset.region));
  setPickerView(view);                              // the kept view (a pressed Passport too), or Passport from a toast; closed, it only sets it up
  renderCardsLive();                                // while it's still closed, so the featured city follows the counts
  renderTonight();                                  // on the featured card too, if that's tonight's
  renderStamps();                                   // the featured card's, and another tab's
  sortCards();
  filterCities();
  tickClocks();
  pickerEl.hidden = false;
  cityBtn.setAttribute('aria-expanded', 'true');
  if (pickerView === 'globe') theGlobe().open();
  else scrollToHere();
  startRequestBoard();
  if (trigger === 'keyboard' || !isTouch) cityFind.focus({ preventScroll: true });   // type to search; phones keep their keyboard down
  track('city_picker_opened', {
    trigger, view: pickerView, live_counts_available: liveCounts !== null,
    // the card shows, from the top (the globe lights its name)
    ...(pickerView === 'grid' && !featuredItem.hidden && cityList.scrollTop === 0 && { featured_city: featuredId }),
  });
}
function closePicker() {
  if (!pickerOpen()) return;
  const hadFocus = pickerEl.contains(document.activeElement);
  pickerEl.hidden = true;
  requestBoard.stop();
  globeBoard.stop();
  globe?.close();
  cityBtn.setAttribute('aria-expanded', 'false');
  if (hadFocus) cityBtn.focus();
}
function togglePicker(trigger) { if (pickerOpen()) closePicker(); else openPicker(trigger); }
cityBtn.addEventListener('click', e => togglePicker(e.detail === 0 ? 'keyboard' : 'button'));
pickerClose.addEventListener('click', closePicker);
cityFind.addEventListener('input', filterCities);
cityFind.addEventListener('keydown', e => {           // the global keys skip inputs, so the search keeps its own
  if (e.key === 'ArrowDown') { e.preventDefault(); if (pickerView === 'globe') globe.focus(); else (featuredItem.hidden ? gridCards()[0] : featuredCard).focus(); }
  else if (e.key === 'Enter') {                       // the first card, or the city the globe turned to
    e.preventDefault();
    if (pickerView === 'globe') globe.pick(); else (featuredItem.hidden ? shownCards()[0] : featuredCard)?.click();
  }
  else if (e.key === 'Escape') {                      // clears the search first, then closes
    e.preventDefault();
    if (cityFind.value) { cityFind.value = ''; filterCities(); } else closePicker();
  }
});
// Arrow keys move through the grid (↑ from the top row goes back to the featured card or the search, ← from the
// first card to the featured card), not to the next city.
cityList.addEventListener('keydown', e => {
  const shown = gridCards(), i = shown.indexOf(document.activeElement);
  if (i < 0) return;
  const cols = shown.filter(b => b.offsetTop === shown[0].offsetTop).length;
  const to = { ArrowRight: i + 1, ArrowLeft: i - 1, ArrowDown: i + cols, ArrowUp: i - cols, Home: 0, End: shown.length - 1 }[e.key];
  if (to === undefined) return;
  e.preventDefault();
  e.stopPropagation();
  if (to < 0) {
    if (e.key === 'ArrowUp') (featuredItem.hidden ? cityFind : featuredCard).focus();
    else if (!featuredItem.hidden) featuredCard.focus();
    return;
  }
  shown[Math.min(to, shown.length - 1)].focus();
});
// On the narrowest phones the four chips can outgrow the row: it scrolls, and a mouse wheel scrolls it sideways.
const cityRow = $('#cities');
cityRow.addEventListener('wheel', e => {
  if (cityRow.scrollWidth <= cityRow.clientWidth || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;   // it fits, or a sideways swipe
  e.preventDefault();
  cityRow.scrollBy({ left: e.deltaMode === WheelEvent.DOM_DELTA_LINE ? e.deltaY * 16 : e.deltaY, behavior: 'instant' });
}, { passive: false });
function tickClocks() {
  cards.forEach((b, i) => { b.querySelector('small').textContent = localTime(SCENES[i].timeZone); });
  if (featuredId) featuredCard.querySelector('small').textContent = `${localTime(byId(featuredId).timeZone)} local time`;
  const t = target();
  if (!t) return;
  $('#cityTime').textContent = `${localTime(t.scene.timeZone)} local time`;
  $('#cityBtnTime').textContent = localTime(t.scene.timeZone);
}
setInterval(tickClocks, 10000);
// The chip's hint (site/js/ui/city-roll.js): until the picker has been opened once in this browser, now and then the chip's
// name rolls through a few other cities and back (about 5 s), so visitors see where cities change. It starts only while
// the dock shows and nothing is open, focused or under the pointer there, and a mouse coming to the chips ends it (the
// chip widens a little and nudges its neighbours). At most ROLLS_MAX times a visit, never in OBS. Boot starts it, once
// every popup it checks exists.
const ROLL_FIRST_MS = 2500, ROLL_EVERY_MS = 15000, ROLLS_MAX = 5, ROLL_GROW_MAX = 48;
const cityRoll = createCityRoll($('#cityBtnName'), { reduced: calm });
$('#cities').addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') cityRoll.stop(); });   // touch: a tap opens or taps through
let rolls = 0, rolledAt = 0, rollTimer = 0;
function startRollHint() {
  if (OBS || store.get('picker-opened', false)) return;
  rolledAt = performance.now() - ROLL_EVERY_MS + ROLL_FIRST_MS;
  rollTimer = setInterval(rollHint, 1000);
}
function rollHint() {
  if (store.get('picker-opened', false) || rolls >= ROLLS_MAX) { clearInterval(rollTimer); return; }
  if (!target()) { rolledAt = performance.now() - ROLL_EVERY_MS + ROLL_FIRST_MS; return; }   // the first, once a city shows
  if (performance.now() - rolledAt < ROLL_EVERY_MS || document.hidden || document.body.matches('.idle, .ui-off')
    || anythingOpen() || (canHover.matches && document.querySelector('.hud-bottom:hover')) || cityRow.contains(document.activeElement)) return;
  rolls++;
  rolledAt = performance.now();
  const chips = [...cityRow.children].filter(c => !c.hidden), row = getComputedStyle(cityRow);
  const free = cityRow.clientWidth - parseFloat(row.paddingLeft) - parseFloat(row.paddingRight)
    - (chips.at(-1).getBoundingClientRect().right - chips[0].getBoundingClientRect().left);
  cityRoll.roll(SCENES.map(sc => sc.name), byId(target().id).name, { grow: Math.min(free, ROLL_GROW_MAX) });
}

/* ---------- tonight's city (site/js/ui/tonight.js): a new one at the visitor's midnight, the same for everyone that date ---------- */
// A first visit to the home page lands on it (boot, below); the picker tags it "tonight" (its grid card, or the featured
// card when that's the one, and on the globe), the title says so where it has the room, and city_viewed carries
// `tonight`. ?tonight=<id> makes that city tonight's, ?tonight=off none, for a test.
const tonightParam = new URLSearchParams(location.search).get('tonight');
const tonightId = () => tonightOf(tonightParam, new Date(), cityIds);
const gridTonight = Object.assign(document.createElement('b'), { className: 'tonight-tag', textContent: 'tonight' });
const featuredTonight = gridTonight.cloneNode(true);
const tonightNote = Object.assign(document.createElement('span'), { className: 'tonight-note', hidden: true });
tonightNote.append(Object.assign(document.createElement('span'), { className: 'sub-sep', textContent: ' · ' }), "tonight's city");
$('#cityTime').after(tonightNote);
function renderTonight() {
  const id = tonightId(), card = cards.find(b => b.dataset.id === id);
  if (card) card.append(gridTonight); else gridTonight.remove();
  if (id && featuredCard.dataset.id === id) featuredCard.querySelector('.feat-text').append(featuredTonight); else featuredTonight.remove();
  tonightNote.hidden = !id || target()?.id !== id;
}
setInterval(renderTonight, 60000);                  // midnight moves it on in a tab left open

/* ---------- a city's looks (site/js/ui/looks.js): other scenes of the same city, picked on its title ---------- */
// A city with more than one look (Rome: Trevi, Colosseum) has a chip per look under its tagline; ↑ and ↓ go through
// them (in OBS too, through Interact). The other look fades in like a new city, and the pick is kept per city for the
// next visit ('looks'; it follows an account). It's the same city for everything else: its page, its people and chat,
// its passport. Every event carries `look`, the scene's id; a pick counts as city_look_changed.
const looksEl = Object.assign(document.createElement('p'), { id: 'looks', className: 'looks', hidden: true });
looksEl.setAttribute('role', 'group');
$('.sub').after(looksEl);
looksEl.addEventListener('click', e => {
  const b = e.target.closest('button');
  if (b) setLook(b.dataset.look, e.detail === 0 ? 'keyboard' : 'chip');
});
function renderLooks() {
  const t = target(), all = t ? looksOf(byId(t.id)) : [];
  looksEl.hidden = all.length < 2;
  if (looksEl.dataset.city !== (looksEl.hidden ? '' : t.id)) {   // another city: its own chips (a pick keeps them, and the focus)
    looksEl.dataset.city = looksEl.hidden ? '' : t.id;
    looksEl.setAttribute('aria-label', looksEl.hidden ? '' : `Looks of ${t.scene.name}`);
    looksEl.replaceChildren(...(looksEl.hidden ? [] : all).map(look => {
      const b = Object.assign(document.createElement('button'), { type: 'button', textContent: look.name });
      b.dataset.look = look.id;
      return b;
    }));
  }
  for (const b of looksEl.children) b.setAttribute('aria-pressed', String(b.dataset.look === t.look));
}
/** Show the city on screen in another of its looks, and keep the pick for it. */
async function setLook(id, trigger) {
  const t = target(), city = t && byId(t.id), look = city && looksOf(city).find(l => l.id === id);
  if (!look || look.id === t.look || recording) return;
  const previous = t.look;
  await showCity(city.id, { look });
  if (target()?.look !== look.id) return;           // it didn't load, or another city came first
  saveLooks(withLook(looks, city, look.id));
  if (trigger === 'keyboard') toast(`${city.name} · ${look.name}`, 1500);
  if (trigger !== 'sync') track('city_look_changed', { city: city.id, look: look.id, previous, trigger, music_playing: lastState === 'playing' });
}
/** The picks, kept: the picker's previews of those cities follow. */
function saveLooks(next) {
  const changed = SCENES.filter(s => lookOf(s, next) !== lookOf(s, looks)).map(s => s.id);
  looks = next;
  store.set('looks', looks);
  for (const id of changed) {
    cards.find(b => b.dataset.id === id).querySelector('img').src = thumbOf(id);
    if (featuredId === id) featuredCard.querySelector('img').src = thumbOf(id);
  }
}

/* ---------- live count under the city name (site/js/live/live.js): hidden until someone else is here too ---------- */
let liveCounts = null;
const liveShownCities = new Set();
// LIVE_FEATURES=off (the build variable, js/flags.js) is a busy day's last resort: pages never connect, as on a deploy
// preview. The switches on the private admin page reach open tabs too (README "Busy days").
const liveAt = LIVE_FEATURES ? liveUrl(location, OBS) : null;
const live = liveAt && 'WebSocket' in window ? createLive(liveAt, {
  ...liveId(),
  onCounted: () => store.set('live-new', false),
  onChange: c => { liveCounts = c; renderLive(); renderCardsLive(); renderTogether(); },
  onStatus: (status, props) => {
    if (status === 'loaded') track('live_count_loaded', props);
    else if (status === 'unavailable') track('live_count_unavailable', props);
  },
}) : null;
// Random, one per browser, so its tabs count once. A new one is fresh ("live-new") until a worker counted it in "all
// time"; an id from before that was counted already, by the worker or in ALL_BEFORE (site/js/live/live.js).
function liveId() {
  const saved = store.get('live-id', null);
  if (saved) return { id: saved, fresh: store.get('live-new', false) === true };
  const id = crypto.randomUUID();
  store.set('live-id', id);
  store.set('live-new', true);
  return { id, fresh: true };
}
function renderLive() {
  const el = $('#live');                          // missing only if a page from an older deploy loads this module
  if (!el) return;
  const t = target(), parts = liveParts(liveCounts, t?.id, t?.scene.name) ?? {};
  const spans = Object.entries(parts).filter(([, text]) => text)
    .map(([part, text]) => Object.assign(document.createElement('span'), { className: `live-${part}`, textContent: text }));
  el.hidden = !spans.length;
  el.replaceChildren(...spans);                   // one span per part, so phones can put the city on a line of its own
  if (spans.length && t && !document.hidden && !document.body.classList.contains('ui-off') && !liveShownCities.has(t.id)) {
    liveShownCities.add(t.id);
    const ever = allTime(liveCounts);
    track('live_count_displayed', {
      city: t.id, display: parts.now ? 'live' : parts.today ? 'today' : 'all',
      visitors_now: liveCounts.total, visitors_today: Math.max(liveCounts.today ?? liveCounts.total, liveCounts.total),
      ...(ever !== null && { visitors_all: ever }),
    });
  }
}
document.addEventListener('visibilitychange', renderLive);

/* ---------- Help wanted: the jobs strip across the top (site/js/jobs/jobs-ticker.js), from the live worker ---------- */
// Missing only on a page from an older deploy; clicks count as content_link_clicked (placement ticker). Turned off in
// Settings or on the admin page, it waits to be turned on again before it asks the worker for the jobs.
const jobsAt = jobsUrl(location, OBS);
let jobsTicker = false;
function startJobsTicker() {
  if (jobsTicker || !stripShown() || !jobsAt || !$('#jobs .jobs-main')) return;
  jobsTicker = true;
  createJobsTicker($('#jobs'), jobsAt);
}
startJobsTicker();
// The strip as the visitor's pick and the site's switch have it; the Settings switch shows only while the site's is on.
function renderJobStrip() {
  document.documentElement.classList.toggle('jobs-off', !stripShown());
  $('#jobStripSetting').hidden = !siteSwitches.jobStrip;
  fitScreen();                                      // the title moved: a portrait view fits between it and the dock again
  startJobsTicker();                                // shown for the first time this visit: the jobs come now
}
// The admin page switched the strip, the billboards' job pages, the Billboards setting, the billboards or the sale's
// plane (worker/switches.js): every open tab follows at once (a billboard fades in or out like a new pick in Settings;
// the plane is asked every frame, planeNow), and the next visit starts from it.
live?.on('switches', m => {
  const next = Object.fromEntries(Object.keys(siteSwitches).map(k => [k, typeof m[k] === 'boolean' ? m[k] : siteSwitches[k]]));
  if (Object.keys(next).every(k => next[k] === siteSwitches[k])) return;
  const strip = next.jobStrip !== siteSwitches.jobStrip, from = sceneOpts(weather);
  const boards = next.billboardSetting !== siteSwitches.billboardSetting || next.billboards !== siteSwitches.billboards;
  Object.assign(siteSwitches, next);
  store.set('switches', siteSwitches);
  if (strip) renderJobStrip();
  if (boards) renderBillboards(from);
  placePitchSpot();                                 // the face may have changed under the click
});

// Who's in each city, on its card in the picker: hidden, like the pill's "here now", until someone else is here too.
function renderCardsLive() {
  const labels = cityLabels(liveCounts, target()?.id);
  for (const b of cards) {
    const badge = b.querySelector('.card-live'), text = labels[b.dataset.id];
    badge.hidden = !text;
    if (text) badge.textContent = text;
  }
  renderFeatured(labels);
  globeLive = { counts: cityCounts(liveCounts, target()?.id), labels, top: featuredId, on: (liveCounts?.total ?? 0) >= 2 };   // the globe's pins and card
  globe?.setLive(globeLive);
}
// The picker's featured city (topCity; a tie keeps the one shown). It follows the counts while the picker is closed,
// then stays put while it's open (only its count updates), so the grid never jumps or changes city under the pointer.
function renderFeatured(labels) {
  const id = topCity(liveCounts, target()?.id, cityIds, featuredId);
  if (!pickerOpen() && id !== featuredId) {         // filterCities shows it as the picker opens
    featuredId = id;
    if (id) {
      const s = byId(id);
      featuredCard.dataset.id = id;
      featuredCard.title = cards.find(b => b.dataset.id === id).title;
      featuredCard.querySelector('img').src = thumbOf(id);
      featuredCard.querySelector('strong').textContent = s.name;
      featuredCard.querySelector('small').textContent = `${localTime(s.timeZone)} local time`;
    }
  }
  if (!featuredId) return;
  if (id === featuredId) featuredCard.querySelector('.card-live').textContent = labels[id];
  if (featuredId === target()?.id) featuredCard.setAttribute('aria-current', 'true');
  else featuredCard.removeAttribute('aria-current');
}

/* ---------- render loop ---------- */
// The website, OBS and PNG saves show each billboard with its city's own ad (render(t)), unless the visitor turned the
// billboards off (BARE_OPTS); the loop videos leave the billboard out (VIDEO). The website's billboards take turns with
// the job face (sceneOpts, billboardFace at the top).
const VIDEO = { billboard: false };
// The weather picked in the Weather chip (W; site/js/engine/weather.js): the website, OBS and PNG saves draw it, the loop videos
// keep each city's own. A new pick fades in like a new city: the old weather's frame, copied, fades out over the new one
// in the city's own buffer, so the view (and how far a phone dragged it, site/js/engine/display.js) stays. A new billboards
// pick (Settings) fades the same way. OBS has no menu: ?obs&weather=snow picks one (W still cycles through them through
// Interact).
const WEATHER_OPTS = Object.fromEntries(WEATHERS.map(w => [w, w === 'city' ? undefined : { weather: w }]));
const BARE_OPTS = Object.fromEntries(WEATHERS.map(w => [w, { ...WEATHER_OPTS[w], billboard: false }]));
const FACE_OPTS = Object.fromEntries(['job', 'x'].map(f => [f, Object.fromEntries(WEATHERS.map(w => [w, { ...WEATHER_OPTS[w], pitch: f }]))]));
const stillOpts = w => (shownBillboards() === 'off' ? BARE_OPTS : WEATHER_OPTS)[w];   // what a PNG save shows
const sceneOpts = w => { const f = billboardFace(); return f ? FACE_OPTS[f][w] : stillOpts(w); };   // what the website (and the mini player) shows
const weatherParam = OBS ? new URLSearchParams(location.search).get('weather') : null;
let weather = [weatherParam, store.get('weather', null)].find(w => WEATHERS.includes(w)) ?? 'city';
let lookFade = null;                                // { from: the old render options, start } while a new look fades in
// Clocks in the scenes show the city's time (render(t, { clock }), core `hands`; the loop videos keep theirs at rest): its
// local minutes after midnight, read once a minute per city, and the options with it kept until the minute turns, so
// frames allocate nothing. A changed reduced-motion preference or Motion pick (calm) rebuilds these cached options immediately.
const clocks = new Map();                           // time zone -> { at, clock, opts: render options -> the same with clock }
function withClock(opts, tz) {
  const at = Math.floor(Date.now() / 60000);
  let c = clocks.get(tz);
  if (!c || c.at !== at || c.reducedMotion !== calm()) {
    const [h, m] = localTime(tz).split(':');
    clocks.set(tz, c = { at, clock: (h * 60 + +m) % 1440, opts: new Map(), reducedMotion: calm() });
  }
  let o = c.opts.get(opts);
  if (!o) c.opts.set(opts, o = { ...opts, clock: c.clock, reducedMotion: c.reducedMotion });
  return o;
}
// The People pick (Settings) in a scene that takes it: calm leaves the options alone (the scene's own crowd), busy asks
// for everyone (crowd 1), quiet for QUIET of the scene's own. The options are kept per crowd, as withClock keeps them per
// minute, so frames allocate nothing. A new pick fades in like a new weather (lookFade.people: the pick before).
const QUIET = 0.4, crowds = new Map();              // render options -> crowd -> the same with crowd
function withCrowd(opts, scene, level = people) {
  if (level === 'calm' || typeof scene.crowd !== 'number') return opts;
  const crowd = level === 'busy' ? 1 : scene.crowd * QUIET;
  let by = crowds.get(opts);
  if (!by) crowds.set(opts, by = new Map());
  let o = by.get(crowd);
  if (!o) by.set(crowd, o = { ...opts, crowd });
  return o;
}
const lookOpts = (inst, opts, level) => withClock(withCrowd(opts, inst.scene, level), inst.scene.timeZone);
const [lookBuf, lookCtx] = makeCanvas(W, H);
const ease = p => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);
function frame(now) {
  requestAnimationFrame(frame);
  try { if (!paintFrame(now)) return; } catch (e) { frameFailed(e); return; }
  drawOverlay(now);
}
// A scene that throws as it draws must not take the screen with it. A cross-fade ends at once on what it was going to,
// so a broken scene can't hold the next city back (was that one the broken one, it is alone on screen now and fails
// by itself). Each scene is reported once; with no frame for 5 s, `health` puts sceneFailed's line on the screen.
function frameFailed(e) {
  lookFade = null;
  if (fade) { current = fade.to; fade = null; return; }
  if (!current.threw) {
    current.threw = true;
    console.error(e);
    trackError(e, { failed: 'scene_render', city: current.id });
  }
  trouble ??= { city: current.id, name: current.scene.name, error: e };
  health.blame('render');
}
/** One frame of the scene; true when it went to the screen, for the overlay to go over it. */
function paintFrame(now) {
  if (!current) return;
  // A hidden page only gets frames while the mini player is open (Chrome keeps rAF alive for it); render just those.
  // Each paces itself in whole display frames (createPacer): the screen at 60 a second or more, the mini player at 30,
  // or both at what the Motion pick (Settings) leaves of them: a still is busy while a fade, a tapped spot's answer or
  // a reaction plays out over it.
  const fps = motionFps(motion, !!(fade || lookFade || spotList.length || (reactionsOn && reactionList.length)));
  const toScreen = !document.hidden && display.due(now, fps), toPip = pip.due(now, fps);
  if (!toScreen && !toPip) return;
  const present = (a, b = null, mix = 0) => {
    if (toScreen && display.present(a, b, mix)) sceneShown();
    if (toPip) pip.present(a.buf, b?.buf, mix);
  };
  if (recording) { present(current); return; }   // recorder owns the buffer (and the overlay stays off the export)
  const t = loopTime(), opts = sceneOpts(weather), face = billboardFace();
  if (face && pitchOnShow(t, face) !== onFace) placePitchSpot(t);   // the face rolled on
  if (fade) {
    const p = Math.min(1, (now - fade.start) / FADE_MS);
    fade.from.render(t, lookOpts(fade.from, opts));
    fade.to.render(t, lookOpts(fade.to, opts));
    present(fade.from, fade.to, ease(p));
    if (p >= 1) { current = fade.to; fade = null; }
  } else if (lookFade) {
    const p = Math.min(1, (now - lookFade.start) / FADE_MS);
    current.render(t, lookOpts(current, lookFade.from, lookFade.people));
    lookCtx.drawImage(current.buf, 0, 0);
    current.render(t, lookOpts(current, opts));
    const c = current.buf.getContext('2d');
    c.globalAlpha = 1 - ease(p); c.drawImage(lookBuf, 0, 0); c.globalAlpha = 1;
    present(current);
    if (p >= 1) lookFade = null;
  } else {
    current.render(t, lookOpts(current, opts));
    present(current);
  }
  return toScreen;
}
requestAnimationFrame(frame);

/* ---------- together overlay: lanterns and reactions over the scene (site/js/live/lanterns.js, site/js/live/reactions.js) ---------- */
// Scene pixels, in two transparent buffers put on #screen after the scene with its layout (display.overlay), so they
// stay pixel-crisp and line up in every crop: halos added as light, paper and icons on top. They belong to the city
// being shown (target(), also during a crossfade). PNG, MP4 and the mini player read the scene buffers: never there.
// With reactions hidden (Settings) nothing is drawn; the sky's lanterns are still kept, so showing them again shows
// the ones in flight at once. The What's new pigeon is drawn here too (pigeonNow, below), reactions hidden or not, and
// so are the tapped spots' answers (site/js/ui/spots.js, tapSpot below), under the lanterns and the reactions.
// The sale plane (planeNow) goes under them, straight onto the screen: it moves in the screen's pixels (display.sprites),
// by the frame's own time `now`, so every frame is an even step on.
const overlayBuf = () => {
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  return [c, c.getContext('2d')];
};
const [glowBuf, glowCtx] = overlayBuf(), [inkBuf, inkCtx] = overlayBuf();
let lanternList = [];                               // [{ x, at, s }] in flight over the city on screen
const reactionList = [];
const spotList = [];                                // the spots answering a tap in the scene on screen
function drawOverlay(now) {
  const v = target();                               // its layout, a phone's drag included
  const bird = v ? pigeonNow(v) : null, plane = v ? planeNow(v, now) : null;
  if (plane) display.sprites(v, plane.x, plane.y, put => drawPlane(put, plane));
  drawMoments(v);                                   // the hour's or the half hour's moment, when one is on (below)
  const sky = reactionsOn && (lanternList.length > 0 || reactionList.length > 0);
  const layerMarks = v ? layerUi.marks() : [];      // the scene's layers that are on, or about to be (site/js/ui/layers.js)
  if (!sky && !bird && !spotList.length && !layerMarks.length) return;    // hidden, or nothing in the sky: no work at all
  glowCtx.clearRect(0, 0, W, H);
  inkCtx.clearRect(0, 0, W, H);
  const answers = v ? drawSpots(glowCtx, inkCtx, spotList, performance.now()) + drawLayers(glowCtx, inkCtx, layerMarks, performance.now()) : 0;
  const lit = sky ? drawLanterns(glowCtx, inkCtx, lanternList, live ? live.serverNow() : Date.now(), v.scene.focusX) : 0;
  const shown = sky && drawReactions(inkCtx, reactionList, performance.now());
  if (bird) drawPigeon(glowCtx, inkCtx, bird, newsSpot.matches(':hover, :focus-visible'));
  if (lit || bird?.letter || answers) display.overlay(v, glowBuf, 'lighter');
  if (lit || shown || bird || answers) display.overlay(v, inkBuf);
}

/* ---------- audio (site/js/audio/player.js, see docs/AUDIO_DESIGN.md §0) ---------- */
// Until the player module loads (or if it fails), the site runs silently with this stub.
const silent = {
  state: 'locked', track: null, queue: [], sounds: [],
  async unlock() {}, play() {}, pause() {}, toggle() {}, next() {}, fadeOut() {}, cue() { return false; }, tap() { return null; }, jam() { return null; },
  setMusicVolume() {}, setAmbienceVolume() {}, setSoundTrim() {}, setScene() {}, setVibe() {}, setWeather() {}, on() { return () => {}; },
  radio: null, setRadio() {}, vote: null, setPicks() {},
};
let audio = silent, wantsMusic = false;
const musicVol = $('#musicVol'), ambVol = $('#ambVol');
musicVol.value = store.get('musicVol', 0.8);
ambVol.value = store.get('ambVol', 0.6);
// city sounds mixer: sound id → 0..2 (1 = as the city was designed, not stored), shared by every city with that sound
const trims = Object.fromEntries(Object.entries(store.get('trims', {}) || {}).filter(([, v]) => Number.isFinite(v)));
let muted = false;

import('./audio/player.js').then(({ createPlayer }) => {
  audio = createPlayer({ siteRoot: ROOT, wallClock: () => (live ? live.serverNow() : Date.now()) });   // the radio plays by the worker's clock
  audio.setMusicVolume(muted ? 0 : +musicVol.value);
  audio.setAmbienceVolume(muted ? 0 : +ambVol.value);
  for (const [id, v] of Object.entries(trims)) audio.setSoundTrim(id, v);
  audio.setVibe(vibeNow());
  audio.setRadio(radioOn);
  audio.setPicks(votePicks);                     // the votes' winners heard before the player loaded
  audio.setWeather(weather);
  audio.on('track', showTrack);
  audio.on('track', renderVibeNote);
  audio.on('track', renderRadio);
  audio.on('state', tellLiveRadio);
  audio.on('state', renderRadio);
  audio.on('track', onTrackAnalytics);
  audio.on('queue', renderQueue);
  audio.on('state', updatePlayButton);
  audio.on('state', onStateAnalytics);
  audio.on('state', () => { if (queueOpen()) renderQueue(); });
  audio.on('state', s => { if (s === 'interrupted' && !OBS) wake(); });   // its play button comes back (awaitingMusic)
  if (target()) audio.setScene(target().scene, sceneTime);
  renderMixer();
  renderRadio();
  if (wantsMusic) startMusic();                  // a gesture arrived before the module finished loading
  else if (OBS) autoplay();
  updatePlayButton();
}).catch(e => {
  console.info('[audio] unavailable:', e.message);
  track('audio_unavailable', { error: String(e.message) });
  trackError(e, { failed: 'audio_load' });
});

// a radio track says the show (or that listeners voted its style in) and who else listens instead of its key and tempo;
// a Classical track says the piece it covers ("Für Elise (Beethoven)")
const radioStyleLine = info => (info.style === info.show ? `${styleName(info.style)} hour` : `${styleName(info.style)}, picked by vote`);
// on Mix a track also says which style it is (Up next too), and a Classical track the piece it covers
const pieceLine = info => info.piece && `${info.piece.name} (${info.piece.by})`;
const trackMeta = info => (info.radio ? [info.artist, radioStyleLine(info), pieceLine(info), listeningLabel()]
  : [info.artist, info.mix && styleName(info.style), pieceLine(info), info.key, info.bpm && `${info.bpm} bpm`]).filter(Boolean).join(' · ');
function showTrack(info) {
  if (!info) return;
  $('#trackTitle').textContent = info.title;
  $('#trackMeta').textContent = trackMeta(info);
  $('#vibeBtn').hidden = info.source !== 'generative';   // the vibe shapes generated music; playlist files play as they are
  radioBtn.hidden = info.source !== 'generative';        // and so does the radio
  if ($('#vibeBtn').hidden) closeVibe();
  if ($('#vibeBtn').hidden) setJam(false, 'playlist');   // and Jam has no chords to fit
  if ('mediaSession' in navigator && window.MediaMetadata) {
    navigator.mediaSession.metadata = new MediaMetadata({ title: info.title, artist: info.artist || 'Lofi Cities', album: pieceLine(info) || 'Lofi Cities' });
  }
  pip.refresh?.();                                  // the desk mini player's track
}
function updatePlayButton() {
  const s = audio.state, playing = s === 'playing' || s === 'starting';
  $('#play').classList.toggle('pulse', s === 'locked' || s === 'interrupted');
  $('#play').setAttribute('aria-label', playing ? 'Pause music' : 'Play music');
  $('#play use').setAttribute('href', playing ? '#i-pause' : '#i-play');
  if (s === 'interrupted') $('#trackMeta').textContent = 'audio was interrupted, tap play to resume';
  pip.setPaused(s === 'paused' || s === 'interrupted');   // the mini player's own play button follows (the video one's, its video)
}
// Must run synchronously inside a click / keydown / touchend handler (browser autoplay rules).
function startMusic(trigger = 'play_button') {
  wantsMusic = true;
  if (audio.state === 'locked') startTrigger = trigger;
  if (audio === silent) return;
  ungesture();
  if (audio.state === 'locked' || audio.state === 'interrupted' || audio.state === 'starting') {   // starting: hand it this gesture too
    audio.unlock().catch(e => console.error('[audio]', e)).finally(updatePlayButton);
  }
  updatePlayButton();
}
// OBS browser sources allow sound without a gesture (obs-browser runs Chromium with autoplay-policy=no-user-gesture-required),
// so embed mode starts the music on its own. Where autoplay is blocked the first click or key press still starts it.
function autoplay() {
  wantsMusic = true;
  startTrigger = 'obs';
  audio.unlock().catch(e => console.error('[audio]', e)).finally(updatePlayButton);
}
// The first click, tap or key press anywhere starts the music (pointerdown doesn't count as a gesture on touch devices).
function firstGesture(e) {
  if (e.type === 'keydown' && (e.key === ' ' || e.metaKey || e.ctrlKey || e.altKey)) return;   // Space is handled below
  if (e.type === 'keydown' && (e.key === '?' || e.key === 'i' || e.key === 'I')) return;        // reading about the site stays quiet
  if (e.type === 'keydown' && (e.key === 'e' || e.key === 'E')) return;                          // and about the city (its guide)
  if (e.type === 'keydown' && (e.key === 'v' || e.key === 'V')) return;                          // so does picking a vibe before the first track
  if (e.type === 'keydown' && (e.key === 'o' || e.key === 'O')) return;                          // the radio starts it itself as it tunes in
  if (e.target instanceof Element && e.target.closest('#radioBtn')) return;                       // so does its chip (off, it stays quiet)
  if (e.type === 'keydown' && (e.key === 'g' || e.key === 'G' || e.key === 'Enter')) return;     // and seeing who else is here, or talking to them
  if (e.type === 'keydown' && e.key === '/') return;                                             // or looking for another city
  if (e.target instanceof Element && e.target.closest('#play, #info, #about, #guide, #guideBtn, #vibe, #vibeBtn, #togetherBtn, #chat, #chatBtn, #board, #boardBtn, #pitchSpot, #pitchCard, #newsSpot, #news')) return;   // play button handles itself; so do the panels, the billboard and the pigeon
  if (e.target instanceof Element && e.target.closest('#links, #xSpot, #planeSpot, .city-request, .globe-request')) return;   // leaving for another tab
  if (e.target instanceof Element && e.target.closest(e.type === 'keydown' ? '#cityPicker' : '#cityBtn, #cityPicker')) return;   // opening the city picker, searching it, picking a city (#96); keys on the chip once it has focus still count
  startMusic('first_gesture');
}
const GESTURES = ['click', 'keydown', 'touchend'];
function ungesture() { for (const ev of GESTURES) removeEventListener(ev, firstGesture, true); }
for (const ev of GESTURES) addEventListener(ev, firstGesture, true);

function togglePlay(trigger = 'play_button') {
  if (audio.state === 'locked' || audio.state === 'interrupted') startMusic(trigger);
  else audio.toggle();
  updatePlayButton();
}
$('#play').addEventListener('click', () => togglePlay());
$('#next').addEventListener('click', () => { startMusic('next_button'); skipTrack('button'); audio.next(); });   // hidden on the radio
$('#queueBtn').addEventListener('click', e => toggleQueue(e.detail === 0 ? 'keyboard' : 'button'));
$('#vibeBtn').addEventListener('click', e => toggleVibe(e.detail === 0 ? 'keyboard' : 'button'));
musicVol.addEventListener('input', () => { muted = false; audio.setMusicVolume(+musicVol.value); store.set('musicVol', +musicVol.value); });
ambVol.addEventListener('input', () => { muted = false; audio.setAmbienceVolume(+ambVol.value); store.set('ambVol', +ambVol.value); });
musicVol.addEventListener('change', () => track('volume_changed', { channel: 'music', value: +musicVol.value }));
ambVol.addEventListener('change', () => track('volume_changed', { channel: 'ambience', value: +ambVol.value }));
function toggleMute() {
  muted = !muted;
  audio.setMusicVolume(muted ? 0 : +musicVol.value);
  audio.setAmbienceVolume(muted ? 0 : +ambVol.value);
  toast(muted ? 'Muted' : 'Sound on', 1200);
  track('mute_toggled', { muted });
}
if ('mediaSession' in navigator) {
  const set = (action, fn) => { try { navigator.mediaSession.setActionHandler(action, fn); } catch { /* unsupported action */ } };
  set('play', () => { audio.play(); updatePlayButton(); });
  set('pause', () => { audio.pause(); updatePlayButton(); });
}
// the lock screen's and the keyboard's next-track key, which the radio takes away (the same song for everyone)
function setMediaNext() {
  if (!('mediaSession' in navigator)) return;
  try { navigator.mediaSession.setActionHandler('nexttrack', radioOn ? null : () => { skipTrack('media_keys'); audio.next(); }); }
  catch { /* unsupported action */ }
}

/* ---------- up next: the upcoming tracks, pick one to play it now ---------- */
const queueEl = $('#queue'), queueList = $('#queueList');
const queueOpen = () => !queueEl.hidden;
const clockOf = s => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
function renderQueue() {
  if (!queueOpen()) return;
  const items = audio.queue || [], focused = [...queueList.querySelectorAll('button')].indexOf(document.activeElement);
  queueList.replaceChildren();
  if (!items.length) {
    const li = document.createElement('li');
    li.className = 'q-empty';
    li.textContent = audio.state === 'locked' && !wantsMusic ? 'Press play to see what comes next' : 'tuning in…';
    queueList.append(li);
    return;
  }
  items.forEach((info, i) => {
    const li = document.createElement('li');
    if (info.radio) {                             // the radio's: when each one starts, and nothing to pick
      const row = document.createElement('div');
      row.className = 'pop-item';
      row.innerHTML = '<span class="q-n"><b></b></span><span class="pop-text"><span class="pop-title"></span><span class="pop-meta"></span></span><span class="q-dur q-at"></span>';
      row.querySelector('b').textContent = i + 1;
      row.querySelector('.pop-title').textContent = info.title;
      row.querySelector('.pop-meta').textContent = [radioStyleLine(info), pieceLine(info), info.key].filter(Boolean).join(' · ');
      row.querySelector('.q-at').textContent = atTime(info.at);
      li.append(row);
      queueList.append(li);
      return;
    }
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'pop-item'; b.dataset.id = info.id;
    b.innerHTML = '<span class="q-n"><b></b><svg><use href="#i-play"/></svg></span><span class="pop-text"><span class="pop-title"></span><span class="pop-meta"></span></span><span class="q-dur"></span>';
    b.querySelector('b').textContent = i + 1;
    b.querySelector('.pop-title').textContent = info.title;
    b.querySelector('.pop-meta').textContent = (info.source === 'generative' ? [info.mix && styleName(info.style), pieceLine(info), info.key, info.bpm && `${info.bpm} bpm`] : [info.artist]).filter(Boolean).join(' · ');
    b.querySelector('.q-dur').textContent = info.duration ? clockOf(info.duration) : '';
    b.setAttribute('aria-label', `Play ${info.title} now`);
    b.addEventListener('click', () => pickTrack(info, i));
    li.append(b);
    queueList.append(li);
  });
  if (focused >= 0) queueList.querySelectorAll('button')[Math.min(focused, items.length - 1)].focus();   // keep keyboard focus across updates
}
function openQueue(trigger) {
  closeBoard();
  closeMixer();
  closeTour();
  closeWeather();
  closePicker();
  closeTimers();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  document.body.classList.remove('ui-off');
  queueEl.hidden = false;
  $('#queueBtn').setAttribute('aria-expanded', 'true');
  renderQueue();
  if (trigger === 'keyboard') queueList.querySelector('button')?.focus();
  track('queue_opened', { trigger, items: (audio.queue || []).length });
}
function closeQueue() {
  if (!queueOpen()) return;
  const hadFocus = queueEl.contains(document.activeElement);
  queueEl.hidden = true;
  $('#queueBtn').setAttribute('aria-expanded', 'false');
  if (hadFocus) $('#queueBtn').focus();
}
function toggleQueue(trigger) { if (queueOpen()) closeQueue(); else openQueue(trigger); }
function pickTrack(info, position) {
  startMusic('queue');
  skipTrack('queue');
  track('queue_track_picked', { title: info.title, position: position + 1, source: info.source });
  audio.next(info.id);
  if (audio.state === 'paused') audio.play();
  updatePlayButton();
  closeQueue();
}
/** ↑ / ↓ move focus through a popup's visible controls, wrapping around */
function arrowKeys(list) {
  list.addEventListener('keydown', e => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const btns = [...list.querySelectorAll('button:not(:disabled), a[href]')].filter(el => !el.closest('[hidden]'));
    const i = btns.indexOf(document.activeElement);
    if (!btns.length) return;
    e.preventDefault();
    btns[(i + (e.key === 'ArrowDown' ? 1 : -1) + btns.length) % btns.length].focus();
  });
}
arrowKeys(queueList);
const popupOpen = () => queueOpen() || timersOpen() || tourOpen() || pickerOpen() || saveOpen() || shareOpen() || mixerOpen() || vibeOpen() || togetherOpen() || chatOpen() || boardOpen() || settingsOpen() || newsOpen();
let popupAtPress = false;                             // a popup was open when this press began: on the scene it only closes it
addEventListener('pointerdown', e => {                // a press outside a popup closes it
  popupAtPress = popupOpen();                         // (the scene is outside every popup: a press there closes the open one)
  if (!(e.target instanceof Element)) return;
  if (queueOpen() && !e.target.closest('#queue, #queueBtn')) closeQueue();
  if (timersOpen() && !e.target.closest('#timers, #timerBtn, #timerPill')) closeTimers();
  if (tourOpen() && !e.target.closest('#tour, #tourBtn')) closeTour();
  if (weatherOpen() && !e.target.closest('#weather, #weatherBtn')) closeWeather();
  if (pickerOpen() && !e.target.closest('#cityPicker, #cityBtn')) closePicker();
  if (saveOpen() && !e.target.closest('#saveMenu, #saveBtn')) closeSave();
  if (shareOpen() && !e.target.closest('#shareMenu, #shareBtn')) closeShare();
  if (settingsOpen() && !e.target.closest('#settings, #settingsBtn')) closeSettings();
  if (mixerOpen() && !e.target.closest('#mixer, #mixerBtn')) closeMixer();
  if (vibeOpen() && !e.target.closest('#vibe, #vibeBtn')) closeVibe();
  if (togetherOpen() && !e.target.closest('#together, #togetherBtn')) closeTogether();
  if (chatOpen() && !e.target.closest('#chat, #chatBtn')) closeChat();
  if (boardOpen() && !e.target.closest('#board, #boardBtn')) closeBoard();
  if (newsOpen() && !e.target.closest('#news, #newsSpot')) closeNews();
});

/* ---------- city sounds mixer: a slider per sound of the city (beds, then event types), 0-200 % ---------- */
const mixerEl = $('#mixer'), mixList = $('#mixList'), mixReset = $('#mixReset');
const mixerOpen = () => !mixerEl.hidden;
const mixInputs = () => [...mixList.querySelectorAll('input')];
function showTrim(input) {
  const v = +input.value, text = `${Math.round(v * 100)}%`;
  input.setAttribute('aria-valuetext', text);
  input.nextElementSibling.textContent = text;
  input.parentElement.classList.toggle('changed', v !== 1);
}
const syncReset = () => { mixReset.disabled = !mixInputs().some(i => +i.value !== 1); };
function renderMixer() {
  if (!mixerOpen()) return;
  const sounds = audio.sounds || [], focused = mixList.contains(document.activeElement) ? document.activeElement.dataset.id : null;
  mixList.replaceChildren();
  if (!sounds.length) {
    const p = document.createElement('p');
    p.className = 'mix-empty';
    p.textContent = audio === silent ? 'tuning in…' : 'this city is quiet';
    mixList.append(p);
  }
  sounds.forEach((s, i) => {
    const row = document.createElement('label');
    row.className = 'mix-row';
    row.classList.toggle('mix-sep', s.kind === 'event' && sounds[i - 1]?.kind === 'bed');
    row.innerHTML = '<span class="mix-name"></span><input type="range" min="0" max="2" step="0.05"><span class="mix-val" aria-hidden="true"></span>';
    row.firstChild.textContent = s.name;
    const input = row.querySelector('input');
    input.dataset.id = s.id; input.dataset.kind = s.kind;
    input.value = trims[s.id] ?? 1;
    input.setAttribute('aria-label', `${s.name} volume`);
    showTrim(input);
    mixList.append(row);
  });
  syncReset();
  if (focused) mixList.querySelector(`input[data-id="${focused}"]`)?.focus();   // keep keyboard focus across a city switch
}
function openMixer(trigger) {
  closeBoard();
  closeQueue();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  document.body.classList.remove('ui-off');
  mixerEl.hidden = false;
  $('#mixerBtn').setAttribute('aria-expanded', 'true');
  renderMixer();
  if (trigger === 'keyboard') mixList.querySelector('input')?.focus();
  track('mixer_opened', { trigger, sounds: (audio.sounds || []).length });
}
function closeMixer() {
  if (!mixerOpen()) return;
  const hadFocus = mixerEl.contains(document.activeElement);
  mixerEl.hidden = true;
  $('#mixerBtn').setAttribute('aria-expanded', 'false');
  if (hadFocus) $('#mixerBtn').focus();
}
function toggleMixer(trigger) { if (mixerOpen()) closeMixer(); else openMixer(trigger); }
$('#mixerBtn').addEventListener('click', e => toggleMixer(e.detail === 0 ? 'keyboard' : 'button'));
mixList.addEventListener('input', e => {
  const input = e.target, id = input.dataset.id, v = +input.value;
  if (v === 1) delete trims[id]; else trims[id] = v;
  audio.setSoundTrim(id, v);
  store.set('trims', trims);
  showTrim(input);
  syncReset();
});
mixList.addEventListener('change', e => track('sound_trim_changed', { sound: e.target.dataset.id, kind: e.target.dataset.kind, value: +e.target.value }));
mixReset.addEventListener('click', () => {
  const inputs = mixInputs();
  for (const input of inputs) {
    delete trims[input.dataset.id];
    audio.setSoundTrim(input.dataset.id, 1);
    input.value = 1;
    showTrim(input);
  }
  store.set('trims', trims);
  if (document.activeElement === mixReset) inputs[0]?.focus();   // the button is about to be disabled
  syncReset();
  track('mixer_reset', { sounds: inputs.length });
});

/* ---------- vibe: style and energy (from the next track) and band (full, no drums, chords only; from the next bar) ---------- */
const vibeEl = $('#vibe'), vibeInputs = vibeEl.elements;
const vibeOpen = () => !vibeEl.hidden;
const vibeNow = () => ({ style: vibeInputs.style.value, energy: vibeInputs.energy.value, band: vibeInputs.band.value });
const vibeLabel = name => vibeEl.querySelector(`input[name="${name}"]:checked + span`).textContent;
const savedVibe = store.get('vibe', null);
for (const k of ['style', 'energy', 'band']) if (typeof savedVibe?.[k] === 'string') vibeInputs[k].value = savedVibe[k];   // an unknown value leaves the default checked
paintStyleIcons(vibeEl);
// the view, picked with the switch beside the Style legend: the layout (#vibeStyle[data-view], css .st-picker) and the
// switch's pressed button; the style picked stays the same
const styleViewButtons = [...vibeEl.querySelectorAll('.st-views button')];
function renderStyleView() {
  $('#vibeStyle').dataset.view = styleView;
  for (const b of styleViewButtons) b.setAttribute('aria-pressed', String(b.dataset.view === styleView));
}
function setStyleView(view, trigger) {
  if (!STYLE_VIEWS.includes(view) || view === styleView) return;
  const previous = styleView;
  styleView = view;
  store.set('style-view', view);
  setContext({ style_view: view });
  renderStyleView();
  if (trigger !== 'sync') track('style_view_changed', { style_view: view, previous, trigger, style: vibeInputs.style.value });
}
renderStyleView();
for (const b of styleViewButtons) b.addEventListener('click', () => setStyleView(b.dataset.view, 'vibe'));
// what the picked style is, from its tooltip ("Bossa nova: nylon guitar …"), for screens without hover: the line under
// the tiles, or the card over them (the card view)
function renderStyleDesc() {
  const input = vibeEl.querySelector('input[name="style"]:checked'), [name, ...rest] = input.closest('label').title.split(': ');
  const line = rest.join(': ');
  $('#styleDesc').replaceChildren(Object.assign(document.createElement('b'), { textContent: name }), ` · ${line}`);
  paintStyleIcon($('#styleCardIcon'), input.value);
  $('#styleCardName').textContent = name;
  $('#styleCardLine').textContent = line;
}
renderStyleDesc();
function renderVibeNote() {
  const t = audio.track, skip = $('#vibeSkip');
  const leaving = !radioOn && t?.radio === true;       // off the radio: your own mix is already on its way (audio.setRadio)
  const heard = k => (k === 'style' && t.mix ? 'mix' : t[k]);   // a Mix track plays a style of its own
  const pending = !radioOn && !leaving && t?.source === 'generative'
    ? ['style', 'energy'].filter(k => heard(k) !== vibeInputs[k].value).map(vibeLabel) : [];
  const waiting = pending.length > 0;
  const unstarted = !radioOn && !t && audio.state === 'locked';   // no music yet: the style picked plays first
  $('#vibeNote').textContent = radioOn ? 'The radio picks the style, a new one every hour. The band is yours, from the next bar.'
    : unstarted ? `${vibeLabel('style')} plays when the music starts.`
    : waiting ? `${pending.join(' and ')} ${pending.length > 1 ? 'start' : 'starts'} with the next track.`
    : 'Style and energy start with the next track, the band on the next bar.';
  skip.textContent = unstarted ? 'Play now' : 'Play one now';
  if (!waiting && !unstarted && document.activeElement === skip) vibeEl.querySelector('input:checked:not([name="jam"])').focus();
  skip.hidden = !waiting && !unstarted;
}
function openVibe(trigger) {
  closeBoard();
  closeQueue();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeMixer();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  document.body.classList.remove('ui-off');
  vibeEl.hidden = false;
  $('#vibeBtn').setAttribute('aria-expanded', 'true');
  renderVibeNote();
  if (trigger === 'keyboard') vibeEl.querySelector('input:checked:not([name="jam"])').focus();   // the radio's switch or the picked style, as before Jam's
  track('vibe_opened', { trigger, ...vibeNow() });
}
function closeVibe() {
  if (!vibeOpen()) return;
  const hadFocus = vibeEl.contains(document.activeElement);
  vibeEl.hidden = true;
  $('#vibeBtn').setAttribute('aria-expanded', 'false');
  if (hadFocus) $('#vibeBtn').focus();
}
function toggleVibe(trigger) { if (vibeOpen()) closeVibe(); else openVibe(trigger); }
vibeEl.addEventListener('change', e => {
  if (e.target === radioInput) { setRadio(radioInput.checked, 'switch'); return; }
  if (e.target === jamInput) { setJam(jamInput.checked, 'switch'); return; }
  const vibe = vibeNow(), setting = e.target.name;
  audio.setVibe(vibe);
  store.set('vibe', vibe);
  if (setting === 'style') renderStyleDesc();
  renderVibeNote();
  track('vibe_changed', { setting, value: vibe[setting], ...vibe, music_playing: lastState === 'playing' });
});
vibeEl.addEventListener('submit', e => e.preventDefault());         // Enter on a radio must not reload the page
$('#vibeSkip').addEventListener('click', () => {
  startMusic('vibe');
  skipTrack('vibe');
  audio.next();
  if (audio.state === 'paused') audio.play();
  updatePlayButton();
});

/* ---------- Lofi Cities Radio (site/js/audio/radio.js, docs/APP_REFERENCE.md "Radio"): one station, the same song for everyone ---------- */
// Its switch heads the Vibe panel (and O), and its chip sits beside the weather's (site/js/ui/radio-chip.js: the hour's
// show while off, who listens while on). On the radio the station picks the style and energy, a new style every hour;
// skipping is gone (the Next button, N and the media keys), the band stays yours. Off again, your own mix starts at
// once, chained from the station's track. Kept for the next visit, like the vibe. The live worker counts who listens (the radio
// on and the music playing) and keeps the clock every listener plays by.
const radioInput = vibeInputs.radio, radioBtn = $('#radioBtn');
// radioListeners: the worker's last count, and radioCounted whether this page was in it (listening as it came)
let radioOn = store.get('radio', false) === true, radioListeners = null, radioCounted = false;
radioInput.checked = radioOn;
setMediaNext();
const styleName = id => vibeEl.querySelector(`input[name="style"][value="${id}"] + span`)?.textContent ?? id;
const atTime = ms => new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
// Who listens (null: no count). The worker's count comes at most every 10 s, so since this page started or stopped
// listening, it's taken on or off here.
function listenerCount() {
  if (radioListeners === null) return null;
  const n = radioListeners - (radioCounted ? 1 : 0) + (listeningNow() ? 1 : 0);
  return n >= 1 ? n : null;
}
function listeningLabel() {
  const n = listenerCount();
  if (n === null) return null;
  return n === 1 && listeningNow() ? 'just you listening' : `${n.toLocaleString('en-US')} listening`;
}
function renderRadio() {
  const r = audio.radio, listening = listeningLabel();
  $('#radioNote').textContent = !r ? 'the same song for everyone' : [
    radioOn ? `on air: ${r.name} hour` : `the same song for everyone · ${r.name} hour`,
    radioOn && `${r.next.name} at ${atTime(r.next.at)}`, listening,
  ].filter(Boolean).join(' · ');
  $('#vibeStyle').hidden = $('#vibeEnergy').hidden = radioOn;
  $('#vibeBtn').classList.toggle('on', radioOn);
  radioBtn.classList.toggle('on', radioOn);
  radioBtn.setAttribute('aria-pressed', String(radioOn));
  $('#radioState').textContent = radioChipState({ on: radioOn, show: r?.name, listeners: listenerCount(), playing: listeningNow() });
  $('#next').hidden = radioOn;
  $('#queueHint').textContent = radioOn ? 'on the radio, for everyone' : 'pick a track to play it now';
  const t = audio.track;
  if (t?.radio && audio.state !== 'interrupted') $('#trackMeta').textContent = trackMeta(t);
}
// the worker counts a page as listening while the radio is on and the music plays
function listeningNow() { return radioOn && (audio.state === 'playing' || audio.state === 'starting'); }
function tellLiveRadio() { live?.setRadio(listeningNow()); }
live?.on('radio', m => {
  radioListeners = Number.isSafeInteger(m.n) && m.n >= 0 ? m.n : null;
  radioCounted = listeningNow();                   // what this page last told the worker (the count is from after that)
  renderRadio();
});
live?.on('close', () => { radioListeners = null; voteLive = false; renderRadio(); renderVote(); });
// From an account's sync ('sync', only while no music plays: see SYNCED), the switch just changes: nothing starts, no toast.
function setRadio(on, trigger) {
  if (on === radioOn) return;
  const sync = trigger === 'sync';
  radioOn = on;
  radioInput.checked = on;
  store.set('radio', on);
  audio.setRadio(on);
  if (lastState === 'playing') skipPending = 'radio';   // the track it tunes in to (or your own, off) starts for this reason
  if (on && !sync) {                                         // tuning in is asking to hear it (this is a gesture)
    startMusic('radio');
    if (audio.state === 'paused') audio.play();
    updatePlayButton();
  }
  tellLiveRadio();
  setMediaNext();
  renderRadio();
  renderVibeNote();
  renderQueue();
  renderVote();
  if (document.activeElement?.closest('#vibeStyle, #vibeEnergy')?.hidden) radioInput.focus();   // O on a style the radio hides
  const r = audio.radio;   // O and the chip say it (the chip drops its state on phones); the panel's switch shows it there
  if (sync) return;
  if (trigger !== 'switch') toast(on ? `Radio${r ? `: ${r.name} hour` : ' on'}` : 'Radio off: your own mix', 1600);
  track('radio_changed', { on, trigger, show: r?.style ?? null, listeners: radioListeners, music_playing: lastState === 'playing' });
}
renderRadio();                                   // the dock and the Vibe panel as the radio has them, before the player loads
radioBtn.addEventListener('click', () => setRadio(!radioOn, 'chip'));

/* ---------- Jam: play along with the music (site/js/audio/jam.js, docs/APP_REFERENCE.md "Jam") ---------- */
// Its switch is under the radio's at the head of the Vibe panel (and J). While it is on, the keys 1–8 each play a note
// that fits the chord sounding, on the track's next 8th note (audio.jam), and a strip of eight pads shows over the
// chips: the keys for a finger, and for the keyboard the sign that a press was taken, since its note sounds up to an 8th
// later. A pad is lit from its press until its note has sounded; nothing else about it moves. 9 and 0 rest while Jam is
// on, so a slip of the hand doesn't change the city. Every visit starts with Jam off, like the tour: the number keys
// switch city until it is turned on. Before the first track a press starts the music, as any first key or tap does,
// and plays nothing; paused, it plays nothing and a line says so. It plays along with the radio and with your own mix
// alike, and not with a playlist's files (no chords to fit). Counted as jam_started and, when it ends (switched off, or
// the page closing), jam_ended.
const jamInput = vibeInputs.jam, jamPads = $('#jamPads'), jamPadEls = [...jamPads.querySelectorAll('.jam-pad')];
const jamLit = jamPadEls.map(() => 0);              // each pad's timer: when its light goes out
let jamOn = false, jamSince = 0, jamCount = 0, jamHinted = -Infinity;
$('#jamNote').textContent = isTouch ? 'eight pads over the dock play notes that fit the music' : 'the keys 1–8 play notes that fit the music';
const jamSeconds = () => Math.round((performance.now() - jamSince) / 1000);
function setJam(on, trigger) {
  if (on === jamOn) return;
  if (on && audio.track && audio.track.source !== 'generative') {
    jamInput.checked = false;
    toast('Jam plays along with the generated music only', 2200);
    return;
  }
  jamOn = on;
  jamInput.checked = on;
  jamPads.hidden = !on;
  fitScreen();                                      // the dock is a strip taller: a portrait view and the cards over the dock make room
  placePitchSpot();
  if (on) {
    jamSince = performance.now();
    jamCount = 0;
    track('jam_started', { trigger, style: audio.track?.style ?? vibeInputs.style.value, radio: radioOn, music_playing: lastState === 'playing' });
  } else track('jam_ended', { trigger, notes: jamCount, seconds: jamSeconds() });
  if (trigger === 'keyboard') toast(on ? 'Jam on · keys 1–8 play along' : 'Jam off', 1800);   // the panel's switch shows it there
}
/** key `i` (0–7) pressed, or its pad: its note, on the next 8th */
function jamKey(i) {
  const heard = audio.jam(i), pad = jamPadEls[i];
  if (heard) jamCount++;
  else if ((audio.state === 'paused' || audio.state === 'interrupted') && performance.now() - jamHinted > 5000) {
    jamHinted = performance.now();
    toast('Jam plays along with the music · press play', 2200);
  }
  pad.classList.add('on');
  clearTimeout(jamLit[i]);
  jamLit[i] = setTimeout(() => pad.classList.remove('on'), (heard ? heard.in * 1000 : 0) + 160);
}
jamPadEls.forEach((pad, i) => {
  pad.addEventListener('pointerdown', e => { if (e.button === 0) jamKey(i); });   // every finger its own press
  pad.addEventListener('click', e => { if (e.detail === 0) jamKey(i); });         // Enter on a pad that has the focus
});
/** the digit (0–9) a key press means for Jam: the key's place on the keyboard where the browser says it (the top row
 *  of an AZERTY keyboard types & é " without Shift), else what it typed; null for any other key */
const jamDigit = e => { const d = /^(?:Digit|Numpad)(\d)$/.exec(e.code)?.[1] ?? (/^\d$/.test(e.key) ? e.key : null); return d === null ? null : +d; };
addEventListener('pagehide', () => { if (jamOn) trackOnExit('jam_ended', { trigger: 'exit', notes: jamCount, seconds: jamSeconds() }); });

// The radio's vote (site/js/ui/radio-vote.js): while a track plays, listeners pick the style of the next one, one of
// three, on a card above the dock from a minute before the vote closes (20 s before the track) until that track starts.
// The worker counts the votes and sends the winner (a pick) to every radio page, which plans the station with it
// (player.setPicks); it sends the picks so far on connect, which is also how the page knows it takes votes.
let voteLive = false, myVote = null;
const votePicks = [], tallies = new Map(), voteBucket = createBucket(VOTE_CAP, VOTE_EVERY_MS);
const roundKey = x => `${x.h}:${x.i}`;
function keepTally(t) {
  if (!t || !Number.isSafeInteger(t.h) || !Number.isSafeInteger(t.i) || !Array.isArray(t.n)) return;
  tallies.set(roundKey(t), t);
  if (tallies.size > 8) tallies.delete(tallies.keys().next().value);
}
function addPicks(list) {
  if (!Array.isArray(list)) return;
  votePicks.push(...list);
  votePicks.splice(0, Math.max(0, votePicks.length - 64));
  audio.setPicks(list);
}
// A round's tallies as the worker counts them: every update lists all the open rounds (a round missing from it has no
// votes left), and a winner's (a pick) stays until its track starts.
function keepVotes(list, now) {
  for (const [key, t] of tallies) if (!t.won) tallies.delete(key);
  if (Array.isArray(list)) list.forEach(t => keepTally({ ...t, now }));
}
// A vote lives on the socket it went out on: a new one (the network, a worker deploy) gets it again while it's open.
function recastVote() {
  const vote = audio.vote;
  if (!myVote || !vote || roundKey(myVote) !== roundKey(vote) || vote.pick !== null || live.serverNow() >= vote.close) return;
  if (voteBucket.take(performance.now())) live.send({ vote: { h: vote.h, i: vote.i, at: Math.round(vote.at), k: myVote.k } });
}
live?.on('picks', m => { voteLive = true; addPicks(m.list); keepVotes(m.votes, m.now); recastVote(); renderVote(); });
live?.on('pick', m => { addPicks([[m.h, m.i, m.k]]); keepTally({ ...m, won: true }); renderVote(); });
live?.on('votes', m => { keepVotes(m.list, m.now); renderVote(); });
live?.on('denied', m => { if (m.what === 'vote') toast('That vote has closed', 1600); });
const voteCard = createVoteCard($('#radioVote'), { onPick: castVote });
// only for listeners (the radio on, the music playing) on a worker that takes votes, and out of the way of the panels
function renderVote() {
  const on = radioOn && voteLive && live?.connected && audio.state === 'playing' && !popupOpen();
  const vote = on ? audio.vote : null;
  voteCard.render(voteView(vote, live ? live.serverNow() : Date.now(), { tally: vote && tallies.get(roundKey(vote)), mine: myVote }));
}
setInterval(renderVote, 1000);
function castVote(k) {
  const vote = audio.vote, same = myVote && roundKey(myVote) === roundKey(vote ?? {});
  if (!vote || (same && myVote.k === k) || !live || live.serverNow() >= vote.close) return;
  if (!voteBucket.take(performance.now()) || !live.send({ vote: { h: vote.h, i: vote.i, at: Math.round(vote.at), k } })) return;
  const t = tallies.get(roundKey(vote)), counted = same && t && t.now >= myVote.at + 1000;   // (see voteView)
  myVote = { h: vote.h, i: vote.i, k, at: live.serverNow(), was: counted ? myVote.k : same ? myVote.was : null };
  renderVote();
  track('radio_voted', { style: vote.options[k].style, option: k, changed: !!same, show: audio.radio?.style ?? null });
}

/* ---------- focus (pomodoro) + sleep timers: the logic is site/js/ui/timers.js, the player voices them (cue, fadeOut) ---------- */
let focus = null, sleep = null;
const timersEl = $('#timers');
const timersOpen = () => !timersEl.hidden;
const timerClock = createClock(tickTimers, { visibleMs: 1000, hiddenMs: 1000 });   // a Worker: hidden tabs keep counting
function optButton(text, label, cls = 't-opt') {
  const b = document.createElement('button');
  b.type = 'button'; b.className = cls; b.textContent = text;
  if (label) b.setAttribute('aria-label', label);
  return b;
}
const presetBtns = FOCUS_PRESETS.map(p => optButton(`${p.focus} / ${p.rest}`, `Focus for ${p.focus} minutes, then ${p.rest} minute breaks`));
const pauseBtn = optButton('pause', '', 't-opt t-act'), skipBtn = optButton('skip', '', 't-opt t-act'), stopBtn = optButton('stop', 'Stop the focus timer', 't-opt t-act');
const sleepBtns = SLEEP_MINUTES.map(m => optButton(String(m), `Fade out and pause the music in ${m} minutes`));
const sleepOffBtn = optButton('off', 'Turn the sleep timer off', 't-opt t-act');
presetBtns.forEach((b, i) => b.addEventListener('click', () => setFocus(FOCUS_PRESETS[i])));
sleepBtns.forEach((b, i) => b.addEventListener('click', () => setSleep(SLEEP_MINUTES[i])));
pauseBtn.addEventListener('click', () => pauseOrResume('panel'));
skipBtn.addEventListener('click', skipFocus);
stopBtn.addEventListener('click', stopFocus);
sleepOffBtn.addEventListener('click', cancelSleep);
const focusActs = Object.assign(document.createElement('span'), { className: 't-acts' });
focusActs.append(pauseBtn, skipBtn, stopBtn);
$('#focusRow').append(...presetBtns, focusActs);
$('#sleepRow').append(...sleepBtns, sleepOffBtn);
$('#timerBtn').addEventListener('click', e => toggleTimers(e.detail === 0 ? 'keyboard' : 'button'));
$('#timerPill').addEventListener('click', () => toggleTimers('pill'));

function setFocus(preset, source = 'panel') {
  if (focus?.preset === preset) return;                  // already running: keep its progress (stop is explicit)
  focus = startFocus(preset, Date.now());
  track('focus_started', { preset: preset.id, source });
  renderTimers();
}
/** a running focus timer stops counting where it is, a paused one goes on (the panel's pause, the mini player's timer) */
function pauseOrResume(source) {
  if (!focus) return;
  const paused = focus.left != null;
  focus = paused ? resumeFocus(focus, Date.now()) : pauseFocus(focus, Date.now());
  track(paused ? 'focus_resumed' : 'focus_paused', { preset: focus.preset.id, phase: focus.phase, source });
  renderTimers();
}
function stopFocus() {
  if (!focus) return;
  track('focus_stopped', { preset: focus.preset.id, phase: focus.phase, rounds_done: roundsDone(focus) });
  focus = null;
  renderTimers();
}
function skipFocus() {
  if (!focus) return;
  const r = skipPhase(focus, Date.now());
  focus = r.focus;
  focusEnded(r.ended, true);
  renderTimers();
}
/** a focus or break period ended: chime into the break, bell back to focus (once, even when a late tick ended several) */
function focusEnded(ended, skipped) {
  if (!ended.length) return;
  const last = ended.at(-1);
  if (!skipped) {
    session.focusRounds += ended.filter(p => p === 'focus').length;
    audio.cue(last === 'focus' ? 'chime' : 'bell');
  }
  toast(last === 'focus' ? `Break · ${focus.preset.rest} min` : `Focus · round ${focus.round}`, 3000);
  track('focus_phase_ended', {
    phase: last, round: last === 'focus' ? focus.round : focus.round - 1, preset: focus.preset.id, skipped, caught_up: ended.length - 1,
  });
}
function setSleep(minutes) {
  if (sleep?.fading && audio.state === 'playing') audio.play();   // re-armed during the fade: bring the sound back
  sleep = startSleep(minutes, Date.now());
  track('sleep_timer_set', { minutes, music_playing: audio.state === 'playing' });
  renderTimers();
}
function cancelSleep() {
  if (!sleep) return;
  if (sleep.fading && audio.state === 'playing') audio.play();
  track('sleep_timer_cancelled', { minutes: sleep.minutes, left_s: Math.max(0, Math.round((sleep.endsAt - Date.now()) / 1000)) });
  sleep = null;
  renderTimers();
}
function tickTimers() {
  const now = Date.now();
  if (focus) {
    const r = advanceFocus(focus, now);
    focus = r.focus;
    focusEnded(r.ended, false);
  }
  if (sleep) {
    const step = sleepStep(sleep, now);
    if (step.action === 'fade' && audio.state === 'playing') {   // paused: nothing to fade yet (asks again next tick)
      sleep = { ...sleep, fading: true };
      audio.fadeOut(Math.max(3, step.seconds));
    } else if (step.action === 'end') {
      const playing = audio.state === 'playing';
      if (playing) audio.fadeOut(3);   // normally silent already and pausing itself; covers a late tick or a play() mid-fade
      track('sleep_timer_finished', { minutes: sleep.minutes, music_playing: playing, listened_s: listenedS() });
      sleep = null;
      toast('Sleep timer done · music paused', 4000);
    }
  }
  renderTimers();
}
function renderTimers() {
  const now = Date.now(), on = Boolean(focus || sleep), paused = focus?.left != null, left = focus ? countdown(focusLeft(focus, now)) : '';
  if (on) timerClock.start(); else timerClock.stop();
  const focusText = focus ? `${paused ? 'paused' : focus.phase} ${left}` : '', sleepText = sleep ? `sleep ${countdown(sleep.endsAt - now)}` : '';
  $('#timerPill').hidden = !on;
  $('#pillFocus').textContent = focusText;
  $('#pillSleep').textContent = sleepText;
  $('#timerPill').setAttribute('aria-label', `Timers: ${[focusText, sleepText].filter(Boolean).join(', ')} left`);
  $('#timerBtn').classList.toggle('on', on);
  updateTitle();
  pip.refresh?.();                                       // the desk mini player's timer
  if (!timersOpen()) return;
  const had = timersEl.contains(document.activeElement) ? document.activeElement : null;   // read first: hiding blurs it
  presetBtns.forEach((b, i) => b.setAttribute('aria-pressed', String(focus?.preset === FOCUS_PRESETS[i])));
  pauseBtn.hidden = skipBtn.hidden = stopBtn.hidden = !focus;
  if (focus) {
    pauseBtn.textContent = paused ? 'resume' : 'pause';
    pauseBtn.setAttribute('aria-label', paused ? 'Resume the focus timer' : 'Pause the focus timer');
    skipBtn.setAttribute('aria-label', focus.phase === 'focus' ? 'Skip to the break' : 'Skip the break');
  }
  $('#focusInfo').textContent = focus
    ? `round ${focus.round} · ${paused ? 'paused' : focus.phase} · ${left} left`
    : 'focus / break minutes, on repeat';
  sleepBtns.forEach((b, i) => b.setAttribute('aria-pressed', String(sleep?.minutes === SLEEP_MINUTES[i])));
  sleepOffBtn.hidden = !sleep;
  $('#sleepInfo').textContent = !sleep ? 'minutes, then fade out and pause'
    : sleep.fading ? 'fading out…' : `pauses in ${countdown(sleep.endsAt - now)}`;
  if (had?.hidden) had.closest('.t-row').querySelector('button:not([hidden])')?.focus();   // stop / off hid it: stay in its row
}
/** the tab shows a running timer ahead of the page title: "18:42 focus · <page title>" */
function updateTitle() {
  const now = Date.now();
  const lead = focus ? `${countdown(focusLeft(focus, now))} ${focus.left != null ? 'paused' : focus.phase} · ` : sleep ? `${countdown(sleep.endsAt - now)} sleep · ` : '';
  document.title = lead + baseTitle;
}
function openTimers(trigger) {
  closeBoard();
  closeQueue();
  closeTour();
  closeWeather();
  closePicker();
  closeMixer();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  document.body.classList.remove('ui-off');
  timersEl.hidden = false;
  $('#timerBtn').setAttribute('aria-expanded', 'true');
  renderTimers();
  if (trigger === 'keyboard') timersEl.querySelector('button:not([hidden])')?.focus();
  account?.sync();                                  // another device's tasks and notes, before they're changed here
  track('timers_opened', { trigger, focus_on: Boolean(focus), sleep_on: Boolean(sleep), tasks: tasks.length, tasks_done: doneCount(tasks), notes: notes.length > 0 });
}
function closeTimers() {
  if (!timersOpen()) return;
  const hadFocus = timersEl.contains(document.activeElement);
  leaveNotes();                                     // hiding the notepad doesn't blur it everywhere
  timersEl.hidden = true;
  $('#timerBtn').setAttribute('aria-expanded', 'false');
  if (hadFocus) $('#timerBtn').focus();
}
function toggleTimers(trigger) { if (timersOpen()) closeTimers(); else openTimers(trigger); }

/* ---------- tasks + notes: a checklist and a notepad under the timers (site/js/ui/notes.js) ---------- */
// For the sitting: kept in this browser (`lofi-cities:tasks`, `lofi-cities:notes`) and, signed in, with the account
// like a setting (SYNCED: the newest change wins, a device's or another's). The analytics hear counts, never the text.
const NOTES_REST_MS = 1500;                           // the notepad saves once the typing rests this long, and as it's left
let tasks = cleanTasks(store.get('tasks', [])), notes = cleanNotes(store.get('notes', ''));
let notesSave = 0, notesEdited = false;
const taskList = $('#taskList'), taskText = $('#taskText'), notesText = $('#notesText');

/** The tasks are `next` now: kept, unless another tab just kept them ('tab'), and drawn. */
function setTasks(next, how) {
  tasks = next;
  if (how !== 'tab') store.set('tasks', tasks);
  renderTasks();
}
function renderTasks() {
  const had = taskList.contains(document.activeElement) ? document.activeElement : null;   // read first: the rows are made anew
  const at = had ? [...taskList.children].indexOf(had.closest('li')) : -1, sel = had?.matches('.task-x') ? '.task-x' : 'input';
  taskList.replaceChildren(...tasks.map((task, i) => {
    const li = document.createElement('li'), label = document.createElement('label'), box = document.createElement('input');
    const text = document.createElement('span'), x = document.createElement('button');
    li.className = 'task';
    box.type = 'checkbox'; box.checked = task.d; box.dataset.i = i;
    text.textContent = task.t;
    label.append(box, text);
    x.type = 'button'; x.className = 'task-x'; x.dataset.i = i;
    x.setAttribute('aria-label', `Remove the task: ${task.t}`);
    x.innerHTML = '<svg aria-hidden="true"><use href="#i-close"/></svg>';
    li.append(label, x);
    return li;
  }));
  taskList.hidden = !tasks.length;
  $('#tasksInfo').textContent = tasksLine(tasks);
  $('#tasksClear').hidden = !doneCount(tasks);
  const full = tasksFull(tasks);
  taskText.disabled = full;
  taskText.placeholder = full ? `${TASKS_MAX} tasks at most: finish or remove one` : 'Add a task';
  if (had) (taskList.children[Math.min(at, tasks.length - 1)]?.querySelector(sel) ?? (full ? $('#timerBtn') : taskText)).focus();   // the same row, or the one that took its place
}
taskList.addEventListener('change', e => {
  const i = +e.target.dataset.i;
  setTasks(toggleTask(tasks, i));
  track('task_checked', { done: tasks[i].d, left: tasks.length - doneCount(tasks) });
});
taskList.addEventListener('click', e => {
  const x = e.target instanceof Element && e.target.closest('.task-x');
  if (!x) return;
  const gone = tasks[+x.dataset.i];
  setTasks(removeTask(tasks, +x.dataset.i));
  track('task_removed', { done: gone.d, tasks: tasks.length });
});
$('#taskForm').addEventListener('submit', e => {
  e.preventDefault();
  const next = addTask(tasks, taskText.value);
  if (next === tasks) return;                         // nothing typed (full, the box is off)
  taskText.value = '';
  setTasks(next);
  taskList.scrollTop = taskList.scrollHeight;
  track('task_added', { tasks: tasks.length });
});
$('#tasksClear').addEventListener('click', () => {
  const cleared = doneCount(tasks);
  setTasks(clearDone(tasks));
  taskText.focus();                                   // the button hid itself
  track('tasks_cleared', { cleared, left: tasks.length });
});

/** The notes are `next` now: kept, unless another tab just kept them ('tab'), and in the notepad. */
function setNotes(next, how) {
  clearTimeout(notesSave);
  notes = next;
  if (how !== 'tab') store.set('notes', notes);
  notesText.value = notes;
  $('#notesClear').hidden = !notes;
}
/** Keeps what the notepad holds, if it's new. */
function saveNotes() {
  clearTimeout(notesSave);
  const next = cleanNotes(notesText.value);
  if (next === notes) return;
  notes = next;
  store.set('notes', notes);
}
/** The notepad is left (blur, the panel closing): kept at once, and counted once however long it was written in. */
function leaveNotes() {
  saveNotes();
  if (notesEdited) track('notes_edited', { chars: notes.length });
  notesEdited = false;
}
notesText.addEventListener('input', () => {
  notesEdited = true;
  $('#notesClear').hidden = !notesText.value;
  clearTimeout(notesSave);
  notesSave = setTimeout(saveNotes, NOTES_REST_MS);
});
notesText.addEventListener('blur', leaveNotes);
document.addEventListener('visibilitychange', () => { if (document.hidden) saveNotes(); });   // before the account's own listener (window) looks for what to send
addEventListener('pagehide', saveNotes);
$('#notesClear').addEventListener('click', () => {
  const was = cleanNotes(notesText.value);
  setNotes('');
  notesEdited = false;
  notesText.focus();                                  // the button hid itself
  track('notes_cleared', { chars: was.length });
  toast('Notes cleared', 6000, null, { label: 'Undo', run: () => { if (!notesText.value) { setNotes(was); track('notes_restored', { chars: was.length }); } } });
});
// Escape closes the panel from its text boxes too (the keyboard shortcuts below leave every key to them).
timersEl.addEventListener('keydown', e => {
  if (e.key === 'Escape' && !e.isComposing && e.target instanceof Element && e.target.matches('#taskText, #notesText')) { e.preventDefault(); closeTimers(); }
});
// Another tab of this browser changed them: this one shows the same (a notepad being written in keeps its text).
addEventListener('storage', e => {
  if (e.key === 'lofi-cities:tasks') setTasks(cleanTasks(store.get('tasks', [])), 'tab');
  else if (e.key === 'lofi-cities:notes' && document.activeElement !== notesText) setNotes(cleanNotes(store.get('notes', '')), 'tab');
});
renderTasks();
setNotes(notes, 'tab');

// The tour and weather menus open above their own chips: centred on the chip, kept inside the dock (phones: full width).
function placeOver(pop, chip) {
  const dock = pop.offsetParent.getBoundingClientRect(), c = chip.getBoundingClientRect();
  pop.style.left = `${Math.round(clamp(c.left + c.width / 2 - pop.offsetWidth / 2 - dock.left, 0, dock.width - pop.offsetWidth))}px`;
}
addEventListener('resize', () => {
  if (tourOpen()) placeOver(tourEl, tourBtn);
  if (weatherOpen()) placeOver(weatherEl, weatherBtn);
});

/* ---------- city tour: a new city every few minutes, or follow the night (the city closest to 1 am, site/js/ui/tour.js) ---------- */
// Modes are the menu's data-mode values: 'off', minutes ('5', '10', …) or 'night'. A visit starts with the tour off;
// C switches on the mode last chosen. Picking a city yourself restarts a rotation's countdown, but ends a night tour,
// so the chip never says "night" over a city where it's daytime. With Favourites only on (section "favourite cities")
// and two or more favourites, both modes go through the favourites alone (tourIds); otherwise through every city.
const tourEl = $('#tour'), tourBtn = $('#tourBtn'), tourItems = [...tourEl.querySelectorAll('[data-mode]')];
const TOUR_MODES = tourItems.map(b => b.dataset.mode).filter(m => m !== 'off');
const NIGHT_CHECK_MS = 30000, RECORDING_RETRY_MS = 5000;
let tour = 'off', tourTimer = 0, tourSince = 0, tourSwitches = 0;
let lastTour = TOUR_MODES.includes(store.get('tour')) ? store.get('tour') : '10';
let tourFavs = store.get('tour-favourites', false) === true;
const tourOpen = () => !tourEl.hidden;
const tourProps = mode => (mode === 'night' ? { mode: 'night' } : { mode: 'rotate', minutes: +mode });
const tourIds = () => tourPool(favs.list, cityIds, tourFavs);
const nightPick = () => nightCity(SCENES.filter(s => tourIds().includes(s.id)), new Date(), target()?.id);

function setTour(mode, trigger) {
  if (mode === tour) return;
  if (tour !== 'off')
    track('tour_stopped', { ...tourProps(tour), trigger, tour_s: Math.round((performance.now() - tourSince) / 1000), switches: tourSwitches });
  tour = mode;
  tourBtn.classList.toggle('on', mode !== 'off');
  $('#tourState').textContent = mode === 'off' ? 'off' : mode === 'night' ? 'night' : `${mode} min`;
  tourItems.forEach(b => b.setAttribute('aria-checked', String(b.dataset.mode === mode)));
  armTour();
  if (mode === 'off') { toast('City tour off', 2000); return; }
  lastTour = mode;
  store.set('tour', mode);
  tourSince = performance.now();
  tourSwitches = 0;
  const onlyFavs = favouritesTour(favs.list, tourFavs);
  track('tour_started', { ...tourProps(mode), pool: onlyFavs ? 'favourites' : 'all', trigger });
  if (mode === 'night') {
    const pick = nightPick();
    toast(`Following the night · ${pick.name}, ${localTime(pick.timeZone)}`, 3000);
    showCity(pick.id, { source: 'tour' });        // a no-op when it's already on screen
  } else {
    toast(`City tour · a new ${onlyFavs ? 'favourite' : 'city'} every ${mode} min`, 3000);
  }
}
/** (Re)starts the countdown to the next city, or to the next look at the night. */
function armTour() {
  clearTimeout(tourTimer);
  if (tour !== 'off') tourTimer = setTimeout(tourTick, tour === 'night' ? NIGHT_CHECK_MS : +tour * 60000);
}
function tourTick() {
  if (recording) { tourTimer = setTimeout(tourTick, RECORDING_RETRY_MS); return; }   // never switch under the MP4 export
  armTour();                                      // also covers a city that fails to load
  if (tour === 'night') showCity(nightPick().id, { source: 'tour' });
  else showCity(stepPool(tourIds(), cityIds, target()?.id, 1), { source: 'tour' });   // every city: as → would
}
/** Called by showCity after every city change. */
function tourCityShown(source) {
  if (tour === 'off') return;
  if (source === 'tour') tourSwitches++;
  if (tour !== 'night') armTour();                // every city gets the full interval, one picked by hand too
  else if (source !== 'tour') setTour('off', 'city_picked');
}

function openTour(trigger) {
  closeBoard();
  closeWeather();
  closePicker();
  closeQueue();
  closeTimers();
  closeMixer();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  renderTourNight();
  tourEl.hidden = false;
  placeOver(tourEl, tourBtn);
  tourBtn.setAttribute('aria-expanded', 'true');
  if (trigger === 'keyboard') tourItems.find(b => b.dataset.mode === tour)?.focus();
}
function renderTourNight() {
  const pick = nightPick();
  $('#tourNight').textContent = `${favouritesTour(favs.list, tourFavs) ? 'favourite nearest 1 am' : 'closest to 1 am now'}: ${pick.name}, ${localTime(pick.timeZone)}`;
}
function closeTour() {
  if (!tourOpen()) return;
  const hadFocus = tourEl.contains(document.activeElement);
  tourEl.hidden = true;
  tourBtn.setAttribute('aria-expanded', 'false');
  if (hadFocus) tourBtn.focus();
}
tourBtn.addEventListener('click', e => { if (tourOpen()) closeTour(); else openTour(e.detail === 0 ? 'keyboard' : 'button'); });
tourItems.forEach(b => b.addEventListener('click', () => { setTour(b.dataset.mode, 'menu'); closeTour(); }));
arrowKeys($('#tourList'));

/* ---------- favourite cities (site/js/ui/favourites.js): a heart beside the city's name, the picker's chip, a tour through them ---------- */
// The heart shares a row with the name (.city-head) on the title card and the phone's city card; it and K heart the
// city on screen, whichever look shows, or no longer (favourite_toggled). The picker's Favourites chip shows while
// there are any. The Tour menu's last line, Favourites only ('tour-favourites'), stays open as it's switched; it has
// the tour go through the favourites (tourIds) once there are two. Both follow the account (SYNCED).
const heartBtn = document.createElement('button'), cityHead = document.createElement('div'), nameEl = $('#cityName');
heartBtn.type = 'button'; heartBtn.className = 'fav-btn'; heartBtn.hidden = true; heartBtn.innerHTML = heartSvg();
cityHead.className = 'city-head';
nameEl.replaceWith(cityHead);
cityHead.append(nameEl, heartBtn);
const favChip = regionChips.find(b => b.dataset.region === FAV_FILTER);
favChip.insertAdjacentHTML('afterbegin', heartSvg());
const tourFavItem = document.createElement('li');
tourFavItem.className = 'tour-favs'; tourFavItem.setAttribute('role', 'none');
tourFavItem.innerHTML = `<button type="button" class="pop-item" role="menuitemcheckbox" aria-checked="false">${heartSvg()}`
  + '<span class="pop-text"><span class="pop-title">Favourites only</span><span class="pop-meta"></span></span></button>';
$('#tourList').append(tourFavItem);
const tourFavBtn = tourFavItem.querySelector('button');
function renderFavs() {
  const t = target(), n = favs.list.length;
  heartBtn.hidden = !t;
  if (t) {
    const { label, title } = heartLabel(t.scene.name, favs.has(t.id));
    heartBtn.setAttribute('aria-pressed', String(favs.has(t.id)));
    heartBtn.setAttribute('aria-label', label);
    heartBtn.title = title;
  }
  favChip.hidden = !n;
  favChip.querySelector('small').textContent = n;
  tourFavBtn.setAttribute('aria-checked', String(tourFavs));
  tourFavBtn.querySelector('.pop-meta').textContent = tourFavsNote(n);
  if (tourOpen()) renderTourNight();
  if (region === FAV_FILTER && !n) setRegion(null);   // the last one gone: every city again
  else if (region === FAV_FILTER) filterCities();
}
function heartCity(trigger) {
  const t = target();
  if (!t || OBS) return;
  const on = favs.toggle(t.id), n = favs.list.length;
  track('favourite_toggled', { city: t.id, on, favourites: n, trigger });
  renderFavs();
  heartBtn.classList.remove('beat');
  if (on) requestAnimationFrame(() => heartBtn.classList.add('beat'));
  const tourThem = on && !tourFavs && favouritesTour(favs.list, true)   // the second: a tour of them is one tap away
    && { label: 'Tour them', run: () => { setTourFavs(true, 'heart_toast'); setTour(tour === 'off' ? lastTour : tour, 'heart_toast'); } };
  toast(heartToast(t.scene.name, on, n), tourThem ? 5000 : 2500, null, tourThem);
}
function setTourFavs(on, trigger) {
  if (on === tourFavs) return;
  tourFavs = on;
  store.set('tour-favourites', on);
  renderFavs();
  if (trigger === 'sync') return;                   // an account's change: no event, no toast
  track('tour_favourites_toggled', { on, favourites: favs.list.length, tour: tour === 'off' ? 'off' : tourProps(tour).mode, trigger });
  toast(tourFavsToast(on, favs.list.length), 3000);
  if (tour === 'night') showCity(nightPick().id, { source: 'tour' });   // the night among them now, not in 30 s
}
heartBtn.addEventListener('click', () => heartCity('button'));
tourFavBtn.addEventListener('click', () => setTourFavs(!tourFavs, 'menu'));

/* ---------- together: reactions and lanterns (README "Together"; the socket is site/js/live/live.js) ---------- */
// Only pages that connect get any of it: the button shows once the worker has sent the city's sky, so OBS sources,
// deploy previews, file://, a worker from before Together and one with Together switched off never show it; nor does
// a visitor who hid reactions (Settings). A hidden button is no Together: the keys, a tap on the scene and anything
// from the others all stop there.
const togetherEl = $('#together'), togetherBtn = $('#togetherBtn');
const togetherOpen = () => !togetherEl.hidden;
const reactBucket = createBucket();                 // the worker's limit per page: what it would drop isn't sent
const count = x => x.toLocaleString('en-US');
const inSeconds = ms => `${Math.max(1, Math.ceil(ms / 1000))} s`;
let togetherTick = 0, band = null;
let lastLantern = null, lanternBefore = null;       // local ms of this page's last release (and the one before it)
let togetherUp = false;                             // the worker has sent a sky: Together is on there (and still counts through a reconnect)
const reactBtns = [...togetherEl.querySelectorAll('.tg-react')];
let pick = pickedKind(store.get('react-kind', 'heart'));   // what a tap on the scene drops: the popup's pressed reaction

// The Settings switch for reactions shows while Together is on at the worker (switched off there, there's nothing to hide).
const renderTogetherBtn = () => {
  togetherBtn.hidden = !togetherUp || !reactionsOn;
  $('#reactionsSetting').hidden = !togetherUp;
};
live?.on('close', renderTogether);
live?.on('react', m => { if (REACTIONS.includes(m.k)) floatReaction(m.k, spotOf(m)); });   // from its spot, if it has one
live?.on('lantern', m => { lanternList = addLantern(lanternList, m.l, live.serverNow()); });
live?.on('lanterns', m => {                        // on connect and after every city switch: that city's sky
  if (!togetherUp) { togetherUp = true; renderTogetherBtn(); renderTogether(); }   // Together is on at this worker
  if (m.city !== target()?.id || !Array.isArray(m.list)) return;   // a list from before a quicker switch
  const now = live.serverNow();
  lanternList = m.list.reduce((list, l) => addLantern(list, l, now), []);
});
live?.on('denied', m => {
  if (m.what !== 'lantern') return;
  if (m.why === 'full') lastLantern = lanternBefore;  // a refusal doesn't count as this page's lantern
  lanternRefused(m.why === 'full' ? 'full' : 'soon');
});
// Switched off at the worker (a busy day, README "Busy days"): the button goes, as on a page that never connected, and
// the sky empties; an open panel closes with a word. The city's sky, sent again once it's back on, brings it all back.
live?.on('switches', m => {
  if (m.together !== false || !togetherUp) return;
  togetherUp = false;
  if (togetherOpen()) {
    closeTogether();
    toast('Together is taking a break · back soon', 3000);
  }
  renderTogetherBtn();
  lanternList = [];
});

// A popup button picks its reaction (pressed, remembered) and sends one, which rises from the bottom right of every
// page in the city; a tap on the scene drops the picked one at that scene pixel for all of them (dropReaction).
function pickReaction(kind) {
  pick = pickedKind(kind);
  store.set('react-kind', pick);
  renderPick();
}
function renderPick() {
  for (const b of reactBtns) b.setAttribute('aria-pressed', String(b.dataset.kind === pick));
  const hint = $('#tgHint'), btn = reactBtns.find(b => b.dataset.kind === pick);
  if (!hint || !btn) return;                        // a page from an older deploy
  const icon = hint.querySelector('svg');
  hint.firstChild.textContent = `${isTouch ? 'tap' : 'click'} the scene to send `;
  hint.style.setProperty('--c', COLOURS[pick][0]);  // in the picked one's colour and drawing
  icon.setAttribute('aria-label', `a ${pick}`);
  icon.firstElementChild.setAttribute('href', btn.querySelector('use').getAttribute('href'));
}
function sendReaction(kind) {
  if (!live?.connected) { toast('Not connected right now · try again in a moment', 2500); return; }
  if (!reactBucket.take(performance.now()) || !live.send({ react: kind })) return;
  floatReaction(kind);                              // the worker passes it to everyone else; this page shows its own
  track('reaction_sent', { kind, where: 'panel' });
}
// A tap on the scene (the drag handling below tells it from a drag): the picked reaction at that scene pixel, for
// everyone in the city. Momentarily offline it only shows here; over the page's limit nothing happens at all, as the
// worker would drop it. Off the scene (an ambient bar, a band's sky or ground) a tap drops nothing.
function dropReaction(cx, cy) {
  const t = target();
  if (togetherBtn.hidden || !t || document.hidden) return;   // never connected (OBS, previews) or hidden: no Together here
  const spot = sceneSpot(display.view(t), cx, cy);
  if (!spot || !reactBucket.take(performance.now())) return;
  if (live.connected && live.send({ react: pick, ...spot })) track('reaction_sent', { kind: pick, where: 'scene' });
  floatReaction(pick, spot);
}
// Scene layers (site/js/ui/layers.js, issue #38): a few things in a scene are switches that add a part to the music
// while they are on (Paris: the turntable a shaker, the desk lamp a warm pad, the painting twinkling notes). A tap on
// one flips it (tapSpot, below), and so does its switch at the foot of the Vibe panel, for a keyboard or a screen
// reader; the part joins on the track's next bar line and stops on the next one, and the thing shows it is on, or
// waiting, over the scene (drawOverlay). They go off with the scene; none in OBS or ?creator, nor on the radio.
const layerUi = createLayerUi({
  form: vibeEl, player: () => audio, radio: () => radioOn, off: OBS || CREATOR, still: calm,
  playing: () => lastState === 'playing', toast, track, touch: isTouch,
});
// A tap on one of the scene's spots (site/js/ui/spots.js: the landmark, a window, a cat…) is the spot's: it answers
// with its kind's sound on the track's next beat (audio.tap) and its small show right there, and the tap drops no
// reaction. Whoever is connected or not, reactions hidden or not, in every look; not in OBS, which takes no taps. Before
// the music has started, and while it is paused, the answer is silent (the first tap anywhere starts the music, as
// ever). A spot that is still answering takes the tap and does nothing more.
// So is a tap on one of the scene's hidden things (site/js/ui/secrets.js), while it is on show, or was when the press
// began at `pressed` (ms on the page's clock): it answers the same way and, the first time, is found (secretFound,
// below). Under a mouse (`hover`) only the found ones count: the hand never gives one away.
const spotUnder = (cx, cy, touch, pressed = performance.now(), hover = false) => {
  const t = target(), v = t && display.view(t), at = v && sceneSpot(v, cx, cy);
  const list = !at ? null : !finds ? spotsOf(t.scene)
    : tappable(t.scene, loopTime(), loopAt(pressed), hover ? s => finds.has(t.id, s.id) : null);
  const spot = list && hitSpot(list, at.x, at.y, padOf(v, touch));
  return spot ? { t, at, spot } : null;
};
function tapSpot(cx, cy, touch, pressed) {
  const hit = !OBS && !recording && !document.hidden && spotUnder(cx, cy, touch, pressed);   // (a loop being recorded holds the overlay)
  if (!hit) return false;
  const { t, at, spot } = hit, now = performance.now();
  if (KINDS[spot.kind].layer) return layerUi.tap(spot);   // a thing that is a switch: its layer, on or off
  if (answering(spotList, spot, now)) return true;
  const heard = audio.tap(KINDS[spot.kind].sound, { x: at.x });
  answerSpot(spotList, spot, at, now, { hit: now + (heard ? heard.in * 1000 : 0), still: calm() });
  if (spot.kind === SECRET) secretFound(t, spot);
  else track('spot_tapped', { city: t.id, look: t.look, kind: spot.kind, music_playing: lastState === 'playing' });
  return true;
}
/** shows a reaction on this page: rising from `spot` (scene px) when it has one, else from the bottom-right band */
function floatReaction(kind, spot = null) {
  const t = target();
  if (!reactionsOn || !t || document.hidden) return;   // hidden, or nobody would see it and it would be over by the next look
  if (spot) { spawnReaction(reactionList, kind, { x0: spot.x, x1: spot.x, y: spot.y, still: calm() }, performance.now()); return; }
  const v = display.view(t);                        // what's on screen now, a phone's drag included
  if (!band || performance.now() - band.at > 500 || band.view !== t || band.sx !== v.x) band = { at: performance.now(), view: t, sx: v.x, ...reactionBand(v) };
  spawnReaction(reactionList, kind, { ...band, still: calm() }, performance.now());
}
// Where reactions start: the bottom right of what's on screen (v: display.view), just above whatever of the interface
// is there (the dock, the chips, an open popup: on a phone that's most of the width).
function reactionBand(v) {
  const x1 = v.x + v.w - 12, x0 = x1 - 46;
  const cssX = x => v.left + (x - v.x) * v.width / v.w, sceneY = y => v.y + (y - v.top) * v.h / v.height;
  let top = innerHeight;
  if (!document.body.matches('.idle, .ui-off')) {
    const l = cssX(x0 - 4), r = cssX(x1 + 4);
    for (const el of document.querySelectorAll('#player, #cities .chip, #jamPads, .dock .pop:not([hidden])')) {
      const b = el.getBoundingClientRect();
      if (b.width && b.right > l && b.left < r) top = Math.min(top, b.top);
    }
  }
  return { x0, x1, y: clamp(Math.round(sceneY(top)) - 6, 110, H - 10) };
}

function releaseLantern(trigger) {
  const t = target();
  if (togetherBtn.hidden || !t) return;             // never connected, or hidden: no Together on this page
  if (!live.connected) { toast('Not connected right now · try again in a moment', 2500); return; }
  if (lanternWait(lastLantern, performance.now())) { lanternRefused('soon'); return; }
  if (roomIn(lanternList, live.serverNow())) { lanternRefused('full'); return; }
  if (!live.send({ lantern: { x: releaseX(display.view(t)) } })) return;
  lanternBefore = lastLantern;
  lastLantern = performance.now();
  track('lantern_released', { trigger });
  closeTogether();                                  // it's lit mid-screen, behind the popup on a phone: watch it go
}
function lanternRefused(why) {
  const name = target()?.scene.name ?? 'the city';
  if (why === 'full') {
    const wait = roomIn(lanternList, live.serverNow());
    toast(`The sky over ${name} is full of lanterns · ${wait ? `room for one more in ${inSeconds(wait)}` : 'try again in a moment'}`, 3500);
  } else {
    const wait = lanternWait(lastLantern, performance.now());
    toast(wait ? `Your next lantern is ready in ${inSeconds(wait)}` : 'Your next lantern is almost ready', 3000);
  }
  track('lantern_denied', { why });
  renderTogether();
}

function renderTogether() {
  if (!togetherOpen()) return;
  const t = target(), name = t?.scene.name ?? '', counts = live?.connected ? liveCounts : null;
  const here = counts && t ? Math.max(1, Math.min(counts.total || 1, counts.cities?.[t.id] ?? 0)) : 0;   // you're here, whatever the count says yet
  $('#togetherHere').textContent = !live?.connected ? 'reconnecting…' : !here ? 'tuning in…'
    : here > 1 ? `${count(here)} in ${name} right now` : `just you in ${name} right now`;
  const wait = lanternWait(lastLantern, performance.now());
  $('#lanternInfo').textContent = wait ? `your next one in ${inSeconds(wait)}` : `rises over ${name} for everyone here`;
}
function openTogether(trigger) {
  closeBoard();
  closeQueue();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeMixer();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeChat();
  document.body.classList.remove('ui-off');
  togetherEl.hidden = false;
  togetherBtn.setAttribute('aria-expanded', 'true');
  renderTogether();
  clearInterval(togetherTick);
  togetherTick = setInterval(renderTogether, 1000); // the lantern countdown, a dropped connection
  if (trigger === 'keyboard') togetherEl.querySelector('button').focus();
  track('together_opened', { trigger });
}
function closeTogether() {
  if (!togetherOpen()) return;
  clearInterval(togetherTick);
  const hadFocus = togetherEl.contains(document.activeElement);
  togetherEl.hidden = true;
  togetherBtn.setAttribute('aria-expanded', 'false');
  if (hadFocus) togetherBtn.focus();
}
function toggleTogether(trigger) {
  if (togetherBtn.hidden) return;                   // not connected yet (or never on this page, or hidden)
  if (togetherOpen()) closeTogether(); else openTogether(trigger);
}
togetherBtn.addEventListener('click', e => toggleTogether(e.detail === 0 ? 'keyboard' : 'button'));
for (const b of reactBtns) b.addEventListener('click', () => { pickReaction(b.dataset.kind); sendReaction(b.dataset.kind); });
renderPick();
$('#lanternBtn').addEventListener('click', () => releaseLantern('button'));
arrowKeys(togetherEl);

/* ---------- chat: short messages with everyone on the site, whichever city they watch (README "Chat"; site/js/live/chat.js, the socket site/js/live/live.js) ---------- */
// Only pages whose worker keeps the chat get any of it: the button shows once the worker has sent the chat, so OBS
// sources, deploy previews, file:// and a worker from before the chat never show it. The panel shows the last 6 hours,
// oldest first, each message under its author's two-word name and the city they were watching, and in the reader's
// language when it was written in another (with the original a tap away). The page checks what the worker would
// refuse (length, links, its bucket) before sending, and gives the text back when the worker refuses it anyway.
// A page from an older deploy (a cached copy) has no chat markup: the chat just stays away.
const chatEl = $('#chat'), chatBtn = $('#chatBtn'), chatLogEl = $('#chatLog'), chatText = $('#chatText'), chatNoteEl = $('#chatNote');
const chatOpen = () => chatEl?.hidden === false;
const sayBucket = createBucket(SAY_CAP, SAY_EVERY_MS), hideBucket = createBucket(HIDE_CAP, HIDE_EVERY_MS);
const HIDDEN_MAX = 200;                             // people hidden on this device, the latest kept
const chatHidden = new Set(store.get('chat-hidden', []).filter(isAuthor).slice(-HIDDEN_MAX));
let chatLog = [], chatCity = null, chatMe = null, chatHushed = 0, chatTick = 0, chatNoteTimer = 0, unsent = null;
let chatAnalytics = false, chatViewing = null, chatStats = null, chatSampleAt = -Infinity, chatFrame = 0;
let chatCountedIn = false;                          // whether the count in chatStats has this page's open panel (inChat)
let dingAt = -Infinity;                             // when the mention ding last rang (performance.now())
// Chosen names (docs/APP_REFERENCE.md "Accounts"): the ones the worker has said, by author key, this page's own included
// (the chat's `nick`). They replace the random names everywhere in the panel, with a mark after them.
const chatNicks = new Map();
const nickList = () => [...chatNicks.values()];
const myNick = () => chatNicks.get(chatMe) ?? null;
// Translation (site/js/live/translate.js): into the first of the browser's languages the worker translates into; messages
// in any of them stay as written. What the worker sent, by message id, the messages asked for (when, how many times) and
// the ones shown as written by a tap on "original". Only asked for with the panel open, from a worker that says `tr`.
const readers = readersOf(navigator.languages?.length ? [...navigator.languages] : [navigator.language]);
const chatTr = new Map(), chatTrAsked = new Map(), chatOriginals = new Set(), trBucket = createBucket(TR_CAP, TR_EVERY_MS);
let chatTrOn = false, trTimer = 0, chatSrId = null;
// Reactions (site/js/live/chat.js RE_EMOJI): only offered by a worker that says `re`; the message whose six emoji are open
// to pick from, if any, by its id (the rows are drawn again at every change).
const reBucket = createBucket(RE_CAP, RE_EVERY_MS), HOLD_MS = 500;
let chatReOn = false, chatReFor = null;
const chatReceipts = new Set();
const chatActivity = createChatActivity({ emit: (props, exit) => (exit ? trackOnExit : track)('chat_activity', props) });
const chatVisible = () => chatOpen() && !document.hidden && !document.body.classList.contains('ui-off') && Boolean(chatCity);

function measureChat(reason = 'interval', exit = false) {
  const active = !exit && chatVisible();
  chatActivity.update(active ? chatCity : null, reason, exit);
  const viewing = active && live?.connected;
  if (chatAnalytics && viewing !== chatViewing && live?.send({ chat_view: viewing })) chatViewing = viewing;
  if (!active || chatFrame) return;
  chatFrame = requestAnimationFrame(() => {
    chatFrame = 0;
    if (!chatVisible()) return;
    const box = chatLogEl.getBoundingClientRect();
    chatActivity.view([...chatLogEl.querySelectorAll('li:not(.mine, .chat-post)')]   // other people's messages, not the site's post
      .filter(li => messageVisible(li.getBoundingClientRect(), box, innerHeight)).map(li => Number(li.dataset.id)));
  });
}
function sampleChat() {
  const snapshot = chatSnapshot(chatStats, chatCity);
  if (!snapshot || document.hidden || (chatSampleAt !== -Infinity && (!chatVisible() || performance.now() - chatSampleAt < 60000))) return;
  track('chat_stats_sampled', snapshot);
  chatSampleAt = performance.now();
}
chatLogEl?.addEventListener('scroll', () => measureChat(), { passive: true });
addEventListener('resize', () => measureChat());
document.addEventListener('visibilitychange', () => { measureChat('visibility', document.hidden); sampleChat(); });
addEventListener('pagehide', () => measureChat('pagehide', true));
addEventListener('pageshow', () => measureChat('pageshow'));
live?.on('chat_stats', m => {
  chatStats = m;
  chatCountedIn = chatViewing === true;             // what the worker had heard from this page
  sampleChat();
  renderWho();
});
live?.on('chat_accepted', m => {
  if (!Number.isSafeInteger(m.id) || !cityIds.includes(m.city) || !Number.isInteger(m.chars) || m.chars < 1 || m.chars > SAY_MAX) return;
  const key = `${m.city}:${m.id}`;
  if (chatReceipts.has(key)) return;
  chatReceipts.add(key);
  if (chatReceipts.size > 256) chatReceipts.delete(chatReceipts.values().next().value);
  track('chat_accepted', { city: m.city, chars: m.chars });
});
// The newest message the panel has shown (unreadIn), kept so a reload or a reconnect doesn't light the dot on the Chat
// button again for the same messages. Pages from before the one room kept one per city: the newest of those.
const savedSeen = store.get('chat-seen', 0) ?? 0;
let chatSeen = Math.max(0, ...[savedSeen, ...Object.values(savedSeen)].filter(Number.isSafeInteger));
const chatUnread = () => unreadIn(chatLog, serverNow(), { me: chatMe, hidden: chatHidden, seen: chatSeen });
const clockAt = at => new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// Everyone's name as the panel shows it: you and the people in it (who wrote a message there, or put an emoji under
// one), those who share a name told apart (namesOf), the chosen names instead of the random ones.
const chatNames = () => namesOf([chatMe, ...shown(chatLog, serverNow(), chatHidden)
  .flatMap(m => [m.a, ...chipsOf(m, { hidden: chatHidden }).flatMap(c => c.who)])], chatNicks);
const chatName = (a, names = chatNames()) => (a === SITE ? SITE_NAME : names.get(a) ?? chatNicks.get(a) ?? nameOf(a));
const toMe = m => mentionsMe(m, chatMe, { nick: myNick(), nicks: nickList() });
// What a screen reader hears for someone else's new message, as written or in its translation `text`.
const chatSaid = (m, text) => `${chatName(m.a)}${toMe(m) ? ' mentioned you' : ''}: ${text}`;
const serverNow = () => live?.serverNow() ?? Date.now();
// Emoji (site/js/live/emoji.js): the smiley button's popup, and ":" with a name in the text box. The recent ones are kept here.
const emojiBtn = $('#chatEmojiBtn');
const emoji = chatText && emojiBtn ? createEmojiKit({
  input: chatText, btn: emojiBtn, picker: $('#chatEmoji'), suggest: $('#chatSuggest'), touch: isTouch,
  recent: store.get('emoji-recent', []), onRecent: list => store.set('emoji-recent', list), onChange: renderCount,
  onAdd: (via, e) => track('chat_emoji_added', { via, emoji: e }),
  onToggle: open => {                               // the log gives the popup some of its room, and keeps its end in view
    chatEl.classList.toggle('picking', open);
    chatLogEl.scrollTop = chatLogEl.scrollHeight;
  },
}) : null;

live?.on('chat', m => {                            // on connect and after every city switch: the last 6 hours
  if (!chatBtn || m.city !== target()?.id) return;  // one from before a quicker switch
  chatLog = historyOf(m.list);
  chatCity = m.city;
  chatMe = isAuthor(m.me) ? m.me : null;
  chatNicks.clear();                                // as they are now: the worker sends each message's author's
  for (const x of chatLog) if (x.n) chatNicks.set(x.a, x.n);
  for (const x of Array.isArray(m.list) ? m.list : []) for (const [a, n] of reactorNicks(x)) chatNicks.set(a, n);
  if (chatMe && nickOf(m.nick)) chatNicks.set(chatMe, m.nick);
  if (chatMe && 'nick' in m) accountNick = nickOf(m.nick);   // a worker from before chosen names never says
  renderNick();
  chatHushed = Number.isFinite(m.hushed) ? m.hushed : 0;
  chatAnalytics = m.analytics === 1;
  chatViewing = null;                               // a reconnect or city switch resets the worker's panel state
  chatTrOn = m.tr === 1 && readers.to !== null;
  chatReOn = m.re === 1;
  if (!chatLog.some(x => x.id === chatReFor)) chatReFor = null;
  for (const id of chatTr.keys()) if (!chatLog.some(x => x.id === id)) chatTr.delete(id);   // gone with their messages
  chatTrAsked.clear();                              // what didn't come back before, asked again
  chatBtn.hidden = false;
  $('#mentionSoundSetting').hidden = false;
  if (!chatTick) chatTick = setInterval(renderChat, 20000);   // the times, messages whose time is over, a dropped connection, the dot
  renderChat({ stick: true });
});
// Switched off at the worker (README "Busy days"): the button goes, dot and all, as on a page whose worker never
// chatted; an open panel closes with a word. The chat, sent again once it's back on, brings it all back.
live?.on('switches', m => {
  if (m.chat !== false || !chatBtn || chatBtn.hidden) return;
  if (chatOpen()) {
    closeChat();
    toast('Chat is taking a break · back soon', 3000);
  }
  chatBtn.hidden = true;
  $('#mentionSoundSetting').hidden = true;
  chatLog = [];
  renderUnread();
});
live?.on('msg', ({ m }) => {                       // from any city
  const msg = messageOf(m);
  if (!msg) return;
  const fresh = !chatLog.some(x => x.id === msg.id);
  chatLog = withMessage(chatLog, msg);
  if (msg.n) chatNicks.set(msg.a, msg.n); else chatNicks.delete(msg.a);
  const mine = msg.a === chatMe;
  if (mine) unsent = null;                          // its echo is its receipt
  else if (!chatHidden.has(msg.a) && chatOpen()) {
    $('#chatSr').textContent = chatSaid(msg, msg.text);
    chatSrId = msg.id;                              // said again in the reader's language when its translation comes
  }
  renderChat({ stick: mine });                      // closed, it lights the dot
  if (fresh) dingFor(msg);
});
// A new message that mentions you, while you're away from the chat (its panel shut, or the page out of view or not the
// window in front), plays the mention ding, at most one every DING_GAP_MS; only while music plays (audio.tap).
function dingFor(msg) {
  const now = performance.now(), away = !chatOpen() || document.hidden || !document.hasFocus();
  if (!mentionSoundOn || !dingsFor(msg, chatMe, { nick: myNick(), nicks: nickList(), hidden: chatHidden, away, last: dingAt, now })) return;
  if (audio.tap(MENTION_DING)) dingAt = now;
}
live?.on('re', m => {                              // someone put an emoji under a message, or took theirs back: its reactions, whole
  if (!Number.isSafeInteger(m.id)) return;
  chatLog = withReactions(chatLog, m.id, reactionsOf(m.r));
  for (const [a, n] of reactorNicks(m)) chatNicks.set(a, n);
  renderChat();
});
live?.on('hush', m => {                            // a few people hid someone: their messages go for everyone, and their reactions
  chatLog = withoutReactor(chatLog.filter(x => x.a !== m.a), m.a);
  chatNicks.delete(m.a);                            // and their chosen name
  if (m.a === chatMe) { accountNick = null; renderNick(); }
  if (m.a === chatMe && Number.isFinite(m.until)) {
    chatHushed = m.until;
    chatRefused('hushed');
  }
  renderChat();
});
live?.on('nick', m => {                            // someone picked a name, or went back to their random one
  if (!isAuthor(m.a)) return;
  if (nickOf(m.n)) chatNicks.set(m.a, m.n); else chatNicks.delete(m.a);
  if (m.a === chatMe) { accountNick = nickOf(m.n); renderNick(); }
  renderChat();
});
live?.on('translated', m => {
  if (m.to !== readers.to || !Array.isArray(m.list)) return;
  for (const t of m.list.map(translationOf)) {
    if (!t) continue;
    chatTr.set(t.id, t);
    chatTrAsked.delete(t.id);
    const msg = chatLog.find(x => x.id === t.id);
    if (t.id === chatSrId && msg && shownTranslation(t)) $('#chatSr').textContent = chatSaid(msg, t.text);
  }
  renderChat();
});
live?.on('denied', m => {
  if (m.what === 'say') chatRefused(m.why, 'worker');
  else if (m.what === 're') reRefused(m.why, 'worker');
});
live?.on('close', () => { chatAnalytics = false; chatViewing = null; chatStats = null; renderChat(); });

/** a city switch: the messages stay, and the worker sends them again under the new city (which the page's time counts for) */
function resetChat() {
  measureChat('city_switch', true);                 // flush the old city's time before context changes
  chatCity = null;
  renderChat();
}

function sendSay() {
  const text = cleanSay(chatText.value);
  if (!text) return;
  if (!live?.connected) { chatSays('Not connected right now · try again in a moment', true); return; }
  const why = chatHushed > serverNow() ? 'hushed' : sayLength(text) > SAY_MAX ? 'long' : linkIn(text, nickAt(nickList())) ? 'link'
    : !sayBucket.take(performance.now()) ? 'soon' : null;
  if (why) { chatRefused(why, 'page'); return; }
  if (!live.send({ say: text })) return;
  unsent = text;                                    // until its echo comes back, or the worker says why not
  chatText.value = '';
  emoji?.close();
  renderCount();
  track('chat_sent', { chars: sayLength(text), emojis: emojiCount(text), mentions: mentionsIn(text, nickList()).length });
}
// Why a message wasn't sent, under the text box for a few seconds; a refused message goes back into the box.
function chatRefused(why, by = 'worker') {
  const says = {
    soon: 'Slow down a little · one message every few seconds',
    long: `${SAY_MAX} characters at most`,
    link: 'No links, emails, @handles or phone numbers',
    words: 'Let\'s keep it kind · that wasn\'t sent',
    same: 'You just said that',
    busy: 'Lots of talk right now · try again in a moment',
    hushed: `A few people hid your messages · you can chat again at ${clockAt(chatHushed)}`,
  }[why];
  if (!says) return;
  if (by === 'worker' && unsent !== null && !chatText.value) chatText.value = unsent;
  unsent = null;
  chatSays(says, true);
  renderChat();
  track('chat_denied', { why, by });
}
function chatSays(text, warn = false) {
  clearTimeout(chatNoteTimer);
  chatNoteEl.textContent = text;
  chatNoteEl.classList.toggle('warn', warn);
  chatNoteTimer = setTimeout(renderCount, 5000);
}
// Under the text box: the house rules, or how many characters are left once it gets close.
function renderCount() {
  clearTimeout(chatNoteTimer);
  const left = SAY_MAX - sayLength(cleanSay(chatText.value) ?? '');
  chatNoteEl.classList.toggle('warn', left < 0);
  chatNoteEl.textContent = left < 0 ? `${-left} too long` : left <= 15 ? `${left} left` : 'be kind · no links · gone after 6 hours';
}

function hidePerson(m) {
  const name = chatName(m.a);                       // before they're hidden: as their messages said
  chatHidden.delete(m.a);                           // set again, last: the store keeps the latest HIDDEN_MAX
  chatHidden.add(m.a);
  if (chatHidden.size > HIDDEN_MAX) chatHidden.delete(chatHidden.values().next().value);
  store.set('chat-hidden', [...chatHidden]);
  if (hideBucket.take(performance.now())) live?.send({ hide: m.id });   // counts towards hiding them for everyone
  chatSays(`${name} is hidden on this device`);
  renderChat();
  track('chat_hidden');
}

// One of the six emoji under message `m`, or yours taken back. Nothing is drawn until the worker says so (`re`, which
// everyone hears): its answer is the receipt.
function reactToMessage(m, e, on, via) {
  if (!live?.connected) { chatSays('Not connected right now · try again in a moment', true); return; }
  const why = chatHushed > serverNow() ? 'hushed' : !reBucket.take(performance.now()) ? 'soon' : null;
  if (why) { reRefused(why, 'page'); return; }
  if (!live.send({ re: m.id, e, on })) return;
  track('chat_reacted', { emoji: e, on, via, own: m.a === chatMe });
}
// Why a reaction wasn't put, under the text box for a few seconds.
function reRefused(why, by = 'worker') {
  const says = {
    soon: 'Slow down a little · one reaction every second or so',
    busy: 'Lots of reactions right now · try again in a moment',
    full: 'That one is full · try another emoji',
    hushed: `A few people hid your messages · you can react again at ${clockAt(chatHushed)}`,
  }[why];
  if (!says) return;
  chatSays(says, true);
  track('chat_reaction_denied', { why, by });
}
// The six emoji of message `id` open to pick from (one message at a time), or shut with null.
function openReactions(id) {
  chatReFor = id;
  renderChat();
}

// "@" beside a message starts a reply to its author: their name at the start of the text box, once, and the caret after
// what's there.
function replyToPerson(m) {
  const nick = chatNicks.get(m.a) ?? null, mention = replyTo(m.a, nick);
  if (!mentionsIn(chatText.value, nickList()).includes((nick ?? nameOf(m.a)).toLowerCase())) chatText.value = mention + chatText.value.trimStart();
  chatText.focus();
  chatText.setSelectionRange(chatText.value.length, chatText.value.length);
  renderCount();
  track('chat_reply_started');
}

// The dot on the Chat button: someone else has said something the panel hasn't shown you, while you were here or in the
// 6 hours before you came (the history, on arrival). It rings when one of those mentions you. The open panel shows it
// all: seen.
function renderUnread() {
  if (!chatBtn) return;
  const newest = chatLog.at(-1)?.id ?? 0;
  if (chatOpen() && newest > chatSeen) {
    chatSeen = newest;
    store.set('chat-seen', chatSeen);
  }
  const unread = chatOpen() ? [] : chatUnread();
  chatBtn.classList.toggle('unread', unread.length > 0);
  chatBtn.classList.toggle('mentioned', unread.some(toMe));
}

// A translation to show instead of what was written: one that differs, from a language the reader doesn't read.
const shownTranslation = t => Boolean(t?.text) && !readers.known.has(t.lang);
// The messages the open panel shows that still need their translation: asked for with a short wait (a burst of new
// ones goes as one request), TR_IDS_MAX at a time, the newest first, within the worker's bucket. One the worker left
// out is asked again after TR_RETRY_MS, TR_TRIES times at most.
function askTranslations(list) {
  if (!chatTrOn || trTimer) return;
  const now = performance.now();
  const due = list.filter(m => {
    if (isPost(m) || chatTr.has(m.id) || !wantsTranslation(m, chatMe)) return false;   // the site's posts aren't the worker's to translate
    const asked = chatTrAsked.get(m.id);
    return !asked || (asked.n < TR_TRIES && now - asked.at > TR_RETRY_MS);
  }).map(m => m.id).slice(-TR_IDS_MAX);
  if (!due.length) return;
  const again = () => askTranslations(shown(chatLog, serverNow(), chatHidden));
  trTimer = setTimeout(() => {
    trTimer = 0;
    if (!chatOpen() || !live?.connected) return;
    if (!trBucket.take(performance.now())) { trTimer = setTimeout(() => { trTimer = 0; again(); }, TR_EVERY_MS); return; }
    if (!live.send({ translate: due, to: readers.to })) return;
    for (const id of due) chatTrAsked.set(id, { at: performance.now(), n: (chatTrAsked.get(id)?.n ?? 0) + 1 });
    again();                                        // the older ones, if there were more than one request's worth
  }, 150);
}

function renderChat({ stick = false } = {}) {
  renderUnread();
  measureChat();
  if (!chatOpen()) return;
  const now = serverNow(), list = shown(chatLog, now, chatHidden), name = byId(target()?.id)?.name ?? '';   // short, as the tags say it
  const atEnd = stick || chatLogEl.scrollHeight - chatLogEl.scrollTop - chatLogEl.clientHeight < 24;
  // A redraw keeps the keyboard where it was: the same button of the same message (or the link of the site's post),
  // else another of its buttons (a city tag you're now in is text), else the text box (not on touch screens, which
  // keep their keyboard down).
  const was = document.activeElement?.closest?.('#chatLog li :is(button, a)'), focused = was?.closest('li').dataset.id, names = chatNames();
  if (!list.some(m => m.id === chatReFor)) chatReFor = null;   // its message went meanwhile
  chatLogEl.replaceChildren(...list.map(m => (isPost(m) ? postRow(m, now) : chatRow(m, now, names))));
  askTranslations(list);
  if (focused) {
    const row = chatLogEl.querySelector(`li[data-id="${focused}"]`), same = `:is(button, a).${was.classList[0]}`;   // of the emoji buttons, the same emoji's
    const to = (was.dataset.e && row?.querySelector(`${same}[data-e="${was.dataset.e}"]`)) || row?.querySelector(same)
      || row?.querySelector('button, a') || (isTouch ? null : chatText);
    to?.focus({ preventScroll: true });
  }
  if (atEnd) chatLogEl.scrollTop = chatLogEl.scrollHeight;
  $('#chatEmpty').hidden = list.length > 0;
  $('#chatEmpty').textContent = 'Nothing said in the last 6 hours · say hello';
  renderWho();
  const hushed = chatHushed > now;
  chatText.disabled = hushed;
  chatText.placeholder = hushed ? `Paused for you until ${clockAt(chatHushed)}` : name ? `Say hi from ${name}` : 'Say something kind';
  $('#chatSend').disabled = hushed;
  if (emojiBtn) emojiBtn.disabled = hushed;
  if (hushed) emoji?.close();
  const unhide = $('#chatUnhide');
  unhide.hidden = !chatHidden.size;
  unhide.textContent = `show ${chatHidden.size} hidden`;
  sampleChat();
}
// The header: how many other people have the chat open right now, in any city, lit like the live pill when anyone
// does, and your name.
function renderWho() {
  if (!chatOpen()) return;
  const who = $('#chatWho'), k = inChat(chatStats, chatCountedIn);
  if (!live?.connected || !chatMe) { who.textContent = live?.connected ? 'tuning in…' : 'reconnecting…'; return; }
  const me = document.createElement('span');
  me.className = 'chat-me';
  me.textContent = `you're ${chatName(chatMe)}`;
  if (k === null) { who.replaceChildren(me); return; }
  const online = document.createElement('span');
  online.className = k ? 'chat-online on' : 'chat-online';
  online.textContent = inChatLabel(k);
  who.replaceChildren(online, me);
}
// The ✓ after a name its owner chose (signed in), which nobody else has: in the chat and on the leaderboards.
function chosenMark() {
  const mark = document.createElement('span');
  mark.className = 'chat-mark';
  mark.textContent = '✓';
  mark.title = 'A name they chose · signed in';
  mark.setAttribute('role', 'img');
  mark.setAttribute('aria-label', 'chosen name');
  return mark;
}
// One of the site's own posts (site/js/live/chat.js POSTS): under the site's name, what it says and its link, which
// opens in a new tab and counts as content_link_clicked (placement chat). Nothing to reply to, hide or translate.
function postRow(m, now) {
  const li = document.createElement('li'), who = document.createElement('b'), text = document.createElement('span'), link = document.createElement('a'),
    time = document.createElement('time'), post = POSTS[m.kind];
  li.className = 'chat-msg chat-post';
  li.dataset.id = m.id;
  who.className = 'chat-name';
  who.textContent = SITE_NAME;
  text.className = 'chat-text';
  text.textContent = post.text;
  link.className = 'chat-link';
  link.href = post.url;
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = `${post.label} ↗`;
  link.setAttribute('aria-label', `${post.label} (opens in a new tab)`);
  const report = e => {
    if (e.type === 'auxclick' && e.button !== 1) return;
    track('content_link_clicked', {
      source_page: 'player', city: target()?.id, music_playing: lastState === 'playing', listened_s: listenedS(),
      destination: post.destination, placement: 'chat',
    });
  };
  link.addEventListener('click', report);
  link.addEventListener('auxclick', report);
  time.dateTime = new Date(m.at).toISOString();
  time.textContent = ago(now - m.at);
  time.title = clockAt(m.at);
  li.append(who, text, link, time);
  return li;
}
function chatRow(m, now, names) {
  const li = document.createElement('li'), who = document.createElement('b'), text = document.createElement('span'), time = document.createElement('time');
  const mine = m.a === chatMe, name = chatName(m.a, names), city = byId(m.city)?.name;
  li.className = mine ? 'chat-msg mine' : toMe(m) ? 'chat-msg to-me' : 'chat-msg';
  li.dataset.id = m.id;
  who.className = 'chat-name';
  who.textContent = name;
  who.style.setProperty('--h', hueOf(m.a));
  if (chatNicks.has(m.a)) who.append(chosenMark());
  if (mine) who.append(' (you)');
  li.append(who);
  if (city) {                                       // where they were watching when they said it: another city takes you there
    const here = m.city === target()?.id, from = document.createElement(here ? 'small' : 'button');
    from.className = 'chat-city';
    from.textContent = city;
    if (!here) {
      from.type = 'button';
      from.title = `Go to ${city}`;
      from.setAttribute('aria-label', `Go to ${city}`);
      from.addEventListener('click', () => showCity(m.city, { source: 'chat' }));
    }
    li.append(from);
  }
  // In the reader's language when it was written in another, until "original" shows what they wrote (and back).
  const tr = mine ? null : chatTr.get(m.id), translated = shownTranslation(tr), original = chatOriginals.has(m.id);
  text.className = 'chat-text';
  const myName = isAuthor(chatMe) ? (myNick() ?? nameOf(chatMe)).toLowerCase() : null;   // the translator keeps the names, so its text has them too
  text.append(...partsOf(translated && !original ? tr.text : m.text, nickList()).map(part => {
    if (!part.name) return part.text;
    const at = document.createElement('span');
    at.className = part.name === myName ? 'chat-at me' : 'chat-at';
    at.textContent = part.text;
    return at;
  }));
  if (translated) text.lang = original ? tr.lang : readers.to;
  time.dateTime = new Date(m.at).toISOString();
  time.textContent = ago(now - m.at);
  time.title = clockAt(m.at);
  li.append(text, time);
  if (translated) {
    const flip = document.createElement('button'), from = langName(tr.lang) ?? 'another language';
    flip.type = 'button';
    flip.className = 'chat-tr';
    flip.textContent = original ? 'translate' : 'original';
    flip.title = original ? `Show it in ${langName(readers.to)}` : `Translated from ${from} · show what ${name} wrote`;
    flip.setAttribute('aria-label', original ? `Show ${name}'s message in ${langName(readers.to)}` : `Translated from ${from}. Show what ${name} wrote`);
    flip.addEventListener('click', () => {
      if (original) chatOriginals.delete(m.id); else chatOriginals.add(m.id);
      track('chat_translation_flipped', { shows: original ? 'translation' : 'original', from: tr.lang, to: readers.to });
      renderChat();
    });
    li.append(flip);
  }
  // What can be done with the message, the smiley, "@" and "hide": one small bar over the row's corner (out of the
  // text's flow, so a full line never wraps for them, or leaves an empty one while they're hidden).
  const acts = document.createElement('span');
  acts.className = 'chat-acts';
  // Reactions: the smiley opens the six emoji to pick from, under the message; the chips under it say who put which.
  const chips = chipsOf(m, { me: chatMe, hidden: chatHidden }), picking = chatReFor === m.id;
  const canReact = chatReOn && !(chatHushed > now), reactTitle = mine ? 'React to your message' : `React to ${name}'s message`;
  acts.classList.toggle('open', canReact && picking);   // the bar stays while its six emoji are open
  if (canReact) {
    const react = document.createElement('button');
    react.type = 'button';
    react.className = 'chat-react';
    react.innerHTML = '<svg aria-hidden="true"><use href="#i-smile"/></svg>';
    react.title = reactTitle;
    react.setAttribute('aria-label', reactTitle);
    react.setAttribute('aria-expanded', String(picking));
    react.addEventListener('click', () => {
      openReactions(picking ? null : m.id);
      if (!picking) chatLogEl.querySelector(`li[data-id="${m.id}"] .chat-re-opt`)?.focus({ preventScroll: true });
    });
    acts.append(react);
  }
  if (!mine && !(chatHushed > now)) {                 // a hushed page can't write, so it can't reply
    const reply = document.createElement('button');
    reply.type = 'button';
    reply.className = 'chat-reply';
    reply.textContent = '@';
    reply.title = `Reply to ${name}`;
    reply.setAttribute('aria-label', `Reply to ${name}`);
    reply.addEventListener('click', () => replyToPerson(m));
    acts.append(reply);
  }
  if (!mine) {
    const hide = document.createElement('button');
    hide.type = 'button';
    hide.className = 'chat-hide';
    hide.textContent = 'hide';
    hide.title = `Hide ${name}'s messages on this device`;
    hide.setAttribute('aria-label', `Hide ${name}'s messages`);
    hide.addEventListener('click', () => hidePerson(m));
    acts.append(hide);
  }
  if (acts.childElementCount) li.append(acts);
  if (canReact && picking) {
    const picker = document.createElement('span');
    picker.className = 'chat-re-pick';
    picker.setAttribute('role', 'group');
    picker.setAttribute('aria-label', reactTitle);
    picker.append(...RE_EMOJI.map(e => {
      const opt = document.createElement('button'), on = chips.some(c => c.e === e && c.mine);
      opt.type = 'button';
      opt.className = 'chat-re-opt';
      opt.dataset.e = e;
      opt.textContent = e;
      opt.title = on ? 'Take yours back' : 'Add yours';
      opt.setAttribute('aria-pressed', String(on));
      opt.addEventListener('click', () => {
        li.querySelector('.chat-react').focus({ preventScroll: true });   // where the redraw keeps the keyboard
        reactToMessage(m, e, !on, 'picker');
        openReactions(null);
      });
      opt.addEventListener('keydown', ev => {          // ← → along the six, not to another city
        if (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') return;
        ev.preventDefault();
        ev.stopPropagation();
        const all = [...picker.children];
        all[(all.indexOf(opt) + (ev.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length].focus();
      });
      return opt;
    }));
    li.append(picker);
  }
  if (chips.length) {
    const under = document.createElement('span');
    under.className = 'chat-res';
    under.append(...chips.map(c => reChip(m, c, names)));
    li.append(under);
  }
  return li;
}
// A chip under a message: an emoji and how many people put it there, yours lit. A tap adds yours or takes it back; who
// they are is its title, and on touch screens, which have no hover, a long press says it under the text box.
function reChip(m, { e, who, mine }, names) {
  const chip = document.createElement('button'), face = document.createElement('span'), n = document.createElement('span');
  const people = whoLabel([...(mine ? ['you'] : []), ...who.filter(a => a !== chatMe).map(a => chatName(a, names))]);
  let hold = 0, held = false;
  chip.type = 'button';
  chip.className = 'chat-re';
  chip.dataset.e = e;
  chip.title = people;
  chip.setAttribute('aria-pressed', String(mine));
  chip.setAttribute('aria-label', `${e} ${who.length}: ${people}. ${mine ? 'Take yours back' : 'Add yours'}`);
  face.className = 'chat-re-e';
  face.textContent = e;
  n.textContent = who.length;
  chip.append(face, n);
  chip.addEventListener('pointerdown', ev => {
    held = false;
    if (ev.pointerType === 'mouse') return;
    hold = setTimeout(() => { held = true; chatSays(`${e} ${people}`); }, HOLD_MS);
  });
  for (const type of ['pointerup', 'pointercancel', 'pointerleave']) chip.addEventListener(type, () => clearTimeout(hold));
  chip.addEventListener('contextmenu', ev => { if (held) ev.preventDefault(); });   // a long press's menu
  chip.addEventListener('click', () => {
    if (held) { held = false; return; }                // the long press said who: not a tap
    reactToMessage(m, e, !mine, 'chip');
  });
  return chip;
}

function openChat(trigger) {
  closeBoard();
  closeQueue();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeMixer();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeTogether();
  document.body.classList.remove('ui-off');
  const unread = chatUnread().length;               // what the dot was for
  chatEl.hidden = false;
  chatBtn.setAttribute('aria-expanded', 'true');
  renderCount();
  renderChat({ stick: true });
  if (trigger === 'keyboard' || !isTouch) chatText.focus({ preventScroll: true });   // phones keep their keyboard down
  track('chat_opened', { trigger, messages: shown(chatLog, serverNow(), chatHidden).length, unread, reads: readers.to });
}
function closeChat() {
  if (!chatOpen()) return;
  const hadFocus = chatEl.contains(document.activeElement);
  chatReFor = null;
  emoji?.close();
  chatEl.hidden = true;
  measureChat('close');
  chatBtn.setAttribute('aria-expanded', 'false');
  if (hadFocus) chatBtn.focus();
}
function toggleChat(trigger) {
  if (!chatBtn || chatBtn.hidden) return;           // no chat from this page's worker (or no worker at all)
  if (chatOpen()) closeChat(); else openChat(trigger);
}
chatBtn?.addEventListener('click', e => toggleChat(e.detail === 0 ? 'keyboard' : 'button'));
$('#chatForm')?.addEventListener('submit', e => { e.preventDefault(); sendSay(); });
chatText?.addEventListener('input', renderCount);
chatText?.addEventListener('keydown', e => {        // the global keys skip text boxes, so this one keeps its own Escape
  if (emoji?.keydown(e)) return;                    // the emoji list or popup took it (its Escape closes itself first)
  if (e.key === 'Escape') { e.preventDefault(); closeChat(); }
});
// The six emoji to pick from shut on a click anywhere else in the panel, and on Escape (which then leaves the panel open).
chatEl?.addEventListener('click', e => {
  if (chatReFor !== null && !e.target.closest('.chat-react, .chat-re-pick')) openReactions(null);
});
chatLogEl?.addEventListener('keydown', e => {
  if (e.key !== 'Escape' || chatReFor === null) return;
  e.preventDefault();
  e.stopPropagation();
  const id = chatReFor;
  openReactions(null);
  chatLogEl.querySelector(`li[data-id="${id}"] .chat-react`)?.focus({ preventScroll: true });
});
$('#chatUnhide')?.addEventListener('click', () => {
  track('chat_unhidden', { people: chatHidden.size });
  chatHidden.clear();
  store.set('chat-hidden', []);
  renderChat();
});

/* ---------- weather: the city's own, drizzle, rain, storms, snow, autumn leaves or clear, on every city (site/js/engine/weather.js) ---------- */
// The pick is kept for the next visit, like the vibe. The city sounds follow it (player.setWeather, weatherScene in
// site/js/audio/ambience.js): wet picks adjust rain and wind; storms add thunder, dry picks remove rain.
const weatherEl = $('#weather'), weatherBtn = $('#weatherBtn'), weatherItems = [...weatherEl.querySelectorAll('[data-weather]')];
const weatherOpen = () => !weatherEl.hidden;
const weatherName = w => weatherItems.find(b => b.dataset.weather === w).querySelector('.pop-title').textContent;
// The chip's icon is the pick: a cloud and the moon for the city's own, then what falls (or the moon alone, clear).
const WEATHER_ICONS = { city: '#i-weather', drizzle: '#i-rain', rain: '#i-rain', thunderstorm: '#i-storm', snow: '#i-snow', leaves: '#i-leaf', clear: '#i-moon' };
function renderWeather() {
  weatherBtn.classList.toggle('on', weather !== 'city');
  weatherBtn.querySelector('use').setAttribute('href', WEATHER_ICONS[weather]);
  $('#weatherState').textContent = weather === 'thunderstorm' ? 'storm' : weather;
  weatherItems.forEach(b => b.setAttribute('aria-checked', String(b.dataset.weather === weather)));
  const t = target();
  if (t) $('#weatherOwn').textContent = `as ${t.scene.name} was drawn`;
}
function setWeather(w, trigger) {
  if (!WEATHERS.includes(w) || w === weather) return;
  const previous = weather;
  weather = w;
  if (current && !fade && !recording) lookFade = { from: sceneOpts(previous), start: performance.now() };
  store.set('weather', w);
  audio.setWeather(w);
  renderWeather();
  renderMixer();                                    // an open mixer gains or loses the rain
  if (trigger === 'keyboard') toast(`Weather · ${weatherName(w)}`, 1500);
  if (trigger !== 'sync') track('weather_changed', { weather: w, previous, trigger, city: target()?.id, music_playing: lastState === 'playing' });
}
function openWeather(trigger) {
  closeBoard();
  closeTour();
  closeQueue();
  closeTimers();
  closeMixer();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closePicker();
  closeTogether();
  closeChat();
  weatherEl.hidden = false;
  placeOver(weatherEl, weatherBtn);
  weatherBtn.setAttribute('aria-expanded', 'true');
  if (trigger === 'keyboard') weatherItems.find(b => b.dataset.weather === weather)?.focus();
  track('weather_opened', { trigger, weather, music_playing: lastState === 'playing' });
}
function closeWeather() {
  if (!weatherOpen()) return;
  const hadFocus = weatherEl.contains(document.activeElement);
  weatherEl.hidden = true;
  weatherBtn.setAttribute('aria-expanded', 'false');
  if (hadFocus) weatherBtn.focus();
}
weatherBtn.addEventListener('click', e => { if (weatherOpen()) closeWeather(); else openWeather(e.detail === 0 ? 'keyboard' : 'button'); });
weatherItems.forEach(b => b.addEventListener('click', () => { setWeather(b.dataset.weather, 'menu'); closeWeather(); }));
arrowKeys($('#weatherList'));
renderWeather();

/* ---------- settings (the gear): reactions and the job strip shown or hidden, the billboards all, city ads only or off, the people busy, calm or quiet, the motion smooth, light or still, kept in this browser ---------- */
// Each setting shows only where it does something: reactions while Together is on at the live worker
// (renderTogetherBtn), the job strip and the billboards while the admin page hasn't switched them off (renderJobStrip,
// renderBillboards). The gear always shows (OBS has no interface).
// Job strip: off, the title, links and pills move back up to the top (css .jobs-off), so a portrait view is fitted again.
// Billboards: B moves on to the next pick without opening it (in OBS, where both show the city's own ad, it goes between
// all and off). A new pick fades in like a new weather; only 'all' has something to click (placePitchSpot), and 'off'
// lets a portrait view frame the landmark alone (display.frameBillboard).
// People: only in a city that can thin its crowd (renderPeople, as each city or look is shown); a new pick fades in too.
// Motion: how many frames the scene gets, for a slow computer (MOTION_MODES at the top); a still stays on the moment it
// was picked at, so nothing jumps.
const settingsInputs = settingsEl.elements;
const REACTION_NOTES = {
  on: 'Hearts, notes, stars, moons and lanterns from people in this city.',
  off: 'Hidden, along with the Together button that sends them.',
};
const JOB_STRIP_NOTES = {
  on: 'Jobs from companies, and how to post one, across the top.',
  off: 'Hidden. Every job is still on the jobs page.',
};
const BILLBOARD_NOTES = {
  all: 'City artwork, project updates, and sponsor messages.',
  city: 'Only fictional signs that belong to the scene.',
  off: 'Hide billboards completely.',
};
const PEOPLE_NOTES = {
  busy: 'Everyone is out: the fullest streets and squares.',
  calm: 'An easy evening, as the city is drawn.',
  quiet: 'Only a few people about.',
};
const MENTION_SOUND_NOTES = {
  on: 'A soft ding on an @mention, with music or city sounds on.',
  off: 'Silent. The Chat button still rings.',
};
const MOTION_NOTES = {
  smooth: 'Every frame, as the city is drawn.',
  light: 'A third of the frames: easier on a slow computer or a battery.',
  still: 'A still picture: nothing moves. The lightest.',
};
const billboardsName = m => settingsEl.querySelector(`input[name="billboards"][value="${m}"] + span`).textContent;
const nextBillboards = () => (OBS ? (billboards === 'off' ? 'all' : 'off')
  : BILLBOARD_MODES[(BILLBOARD_MODES.indexOf(billboards) + 1) % BILLBOARD_MODES.length]);
function renderSettings() {
  settingsInputs.reactions.checked = reactionsOn;
  $('#reactionsNote').textContent = REACTION_NOTES[reactionsOn ? 'on' : 'off'];
  settingsInputs.jobStrip.checked = jobStripOn;
  $('#jobStripNote').textContent = JOB_STRIP_NOTES[jobStripOn ? 'on' : 'off'];
  settingsInputs.mentionSound.checked = mentionSoundOn;
  $('#mentionSoundNote').textContent = MENTION_SOUND_NOTES[mentionSoundOn ? 'on' : 'off'];
  settingsInputs.billboards.value = billboards;
  $('#billboardsNote').textContent = BILLBOARD_NOTES[billboards];
  settingsInputs.people.value = people;
  $('#peopleNote').textContent = PEOPLE_NOTES[people];
  settingsInputs.motion.value = motion;
  $('#motionNote').textContent = MOTION_NOTES[motion];
}
function setReactions(on, trigger) {
  if (on === reactionsOn) return;
  reactionsOn = on;
  store.set('reactions', on ? 'on' : 'off');
  if (!on) reactionList.length = 0;                 // the ones on their way up go too; the sky's lanterns wait in lanternList
  renderTogetherBtn();
  renderSettings();
  if (trigger !== 'sync') track('reactions_changed', { reactions: on ? 'on' : 'off', trigger, city: target()?.id });
}
function setJobStrip(on, trigger) {
  if (on === jobStripOn) return;
  jobStripOn = on;
  store.set('job-strip', on ? 'on' : 'off');
  setContext({ job_strip: on ? 'on' : 'off' });
  renderJobStrip();
  renderSettings();
  if (trigger !== 'sync') track('job_strip_changed', { job_strip: on ? 'on' : 'off', trigger, city: target()?.id });
}
function setMentionSound(on, trigger) {
  if (on === mentionSoundOn) return;
  mentionSoundOn = on;
  store.set('mention-sound', on ? 'on' : 'off');
  renderSettings();
  if (trigger !== 'sync') track('mention_sound_changed', { mention_sound: on ? 'on' : 'off', trigger, music_playing: lastState === 'playing' });
}
function setBillboards(mode, trigger) {
  if (!BILLBOARD_MODES.includes(mode) || mode === billboards) return;
  const from = sceneOpts(weather), previous = billboards;
  billboards = mode;
  store.set('billboards', mode);
  setContext({ billboards: mode });
  renderBillboards(from);
  renderSettings();
  if (trigger === 'keyboard') toast(`Billboards · ${billboardsName(mode)}`, 1500);
  if (trigger !== 'sync') track('billboards_changed', { billboards: mode, previous, trigger, city: target()?.id, music_playing: lastState === 'playing' });
}
function setPeople(mode, trigger) {
  if (!PEOPLE_MODES.includes(mode) || mode === people) return;
  const previous = people;
  people = mode;
  store.set('people', mode);
  if (current && !fade && !recording) lookFade = { from: sceneOpts(weather), people: previous, start: performance.now() };
  renderSettings();
  if (trigger !== 'sync') track('people_changed', { people: mode, previous, trigger, city: target()?.id, music_playing: lastState === 'playing' });
}
function setMotion(mode, trigger) {
  if (!MOTION_MODES.includes(mode) || mode === motion) return;
  const previous = motion;
  if (mode === 'still') stillAt = loopTime();       // the moment on screen stays
  motion = mode;
  store.set('motion', mode);
  setContext({ motion: mode });
  root.classList.toggle('motion-still', mode === 'still');
  renderSettings();
  track('motion_changed', { motion: mode, previous, trigger, city: target()?.id, music_playing: lastState === 'playing' });
}
/** The People row shows only in a city that can thin its crowd (applyLook, as each city or look comes on). */
function renderPeople() {
  $('#peopleSetting').hidden = typeof target()?.scene.crowd !== 'number';
}
// The billboards as the visitor's pick and the site's switch have them (shownBillboards), faded in from the look before
// (from); the Settings pick shows only while the site's two switches are on.
function renderBillboards(from) {
  if (current && !fade && !recording) lookFade = { from, start: performance.now() };
  display.frameBillboard(shownBillboards() !== 'off');
  placePitchSpot();
  $('#billboardsSetting').hidden = !billboardsPick();
}
function openSettings(trigger) {
  closeBoard();
  closeQueue();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeVibe();
  closeMixer();
  closeSave();
  closeShare();
  closeTogether();
  closeChat();
  closeNews();
  settingsEl.hidden = false;
  settingsBtn.setAttribute('aria-expanded', 'true');
  alerts.render();                                  // the browser's permission may have changed meanwhile
  if (trigger === 'keyboard') settingsEl.querySelector(':not([hidden]) > [role="switch"], fieldset:not([hidden]) input:checked')?.focus();   // the first shown
  track('settings_opened', { trigger, reactions: reactionsOn ? 'on' : 'off', job_strip: jobStripOn ? 'on' : 'off', billboards, people, motion, mention_sound: mentionSoundOn ? 'on' : 'off' });
}
function closeSettings() {
  if (!settingsOpen()) return;
  const hadFocus = settingsEl.contains(document.activeElement);
  settingsEl.hidden = true;
  settingsBtn.setAttribute('aria-expanded', 'false');
  if (hadFocus) settingsBtn.focus();
}
settingsBtn.addEventListener('click', e => { if (settingsOpen()) closeSettings(); else openSettings(e.detail === 0 ? 'keyboard' : 'button'); });
settingsEl.addEventListener('change', e => {
  if (e.target.name === 'reactions') setReactions(e.target.checked, 'menu');
  else if (e.target.name === 'jobStrip') setJobStrip(e.target.checked, 'menu');
  else if (e.target.name === 'mentionSound') setMentionSound(e.target.checked, 'menu');
  else if (e.target.name === 'alerts') alerts.toggle(e.target.checked, 'settings');   // in the change itself: the browser asks only from a gesture
  else if (e.target.name === 'billboards') setBillboards(e.target.value, 'menu');
  else if (e.target.name === 'people') setPeople(e.target.value, 'menu');
  else if (e.target.name === 'motion') setMotion(e.target.value, 'menu');
});
settingsEl.addEventListener('submit', e => e.preventDefault());      // Enter on a radio must not reload the page
renderSettings();
$('#jobStripSetting').hidden = !siteSwitches.jobStrip;
$('#billboardsSetting').hidden = !billboardsPick();   // reactions' row waits for Together (renderTogetherBtn)
// New city alerts (site/js/ui/alerts.js): the Settings row where the live worker is, and its one-time offer.
const alerts = createAlerts({ url: LIVE_FEATURES ? alertsUrl(location, OBS) : null, row: $('#alertsSetting'), input: settingsInputs.alerts,
  note: $('#alertsNote'), store, track, trackError, toast });

/* ---------- fullscreen, idle UI, toast ---------- */
const root = document.documentElement;
function toggleFS() {
  if (document.fullscreenElement || document.webkitFullscreenElement) (document.exitFullscreen || document.webkitExitFullscreen).call(document);
  else { const req = root.requestFullscreen || root.webkitRequestFullscreen; if (req) Promise.resolve(req.call(root)).catch(() => {}); }
}
if (!(root.requestFullscreen || root.webkitRequestFullscreen)) $('#fs').hidden = true;
$('#fs').addEventListener('click', toggleFS);
$('#screen').addEventListener('dblclick', toggleFS);
// Author links: also count middle-click opens (auxclick), and whether the visitor was listening at the time.
for (const a of document.querySelectorAll('#links a[data-network]')) {
  const report = e => {
    if (e.type === 'auxclick' && e.button !== 1) return;
    track('author_link_clicked', { network: a.dataset.network, location: 'header', music_playing: lastState === 'playing', listened_s: listenedS() });
  };
  a.addEventListener('click', report);
  a.addEventListener('auxclick', report);
}
for (const a of document.querySelectorAll('[data-shop-product]')) {
  const report = e => {
    if (e.type === 'auxclick' && e.button !== 1) return;
    track('shop_link_clicked', {
      product: a.dataset.shopProduct, placement: a.dataset.shopLocation,
      music_playing: lastState === 'playing', listened_s: listenedS(),
    });
  };
  a.addEventListener('click', report);
  a.addEventListener('auxclick', report);
}
for (const ev of ['fullscreenchange', 'webkitfullscreenchange'])
  document.addEventListener(ev, () => track('fullscreen_toggled', { on: Boolean(document.fullscreenElement || document.webkitFullscreenElement) }));

let idleTimer = 0;
const canHover = matchMedia('(hover: hover)');   // on touch screens :hover sticks to the last tapped element
function wake() {
  document.body.classList.remove('idle');
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    const busy = (canHover.matches && document.querySelector('.ui:hover')) || document.activeElement?.matches('input') || anythingOpen() || awaitingMusic();
    if (busy) wake(); else document.body.classList.add('idle');
  }, 3500);
}
// Until the music has started in this visit, or while something else stopped it (a phone call: interrupted), the
// interface stays, play prompt and all: without it a phone shows a silent scene and nothing to tap. OBS plays by itself.
const awaitingMusic = () => !OBS && ((audio.state === 'locked' && !wantsMusic) || audio.state === 'interrupted');
// Every popup, the about panel or the billboard's card: the interface stays awake (and the chip's name still).
const anythingOpen = () => queueOpen() || timersOpen() || tourOpen() || weatherOpen() || pickerOpen() || vibeOpen() || mixerOpen() || saveOpen() || shareOpen() || togetherOpen() || chatOpen() || boardOpen() || settingsOpen() || newsOpen() || about.open || guide.open || pitchCard.open;

/* ---------- a dialog's tabs (the about panel, the city guide): one section shows, ← → move between the tabs ---------- */
// Without JS the sections simply stack; tabbed() makes them tabs. The tabs are looked up on every use, since the city
// guide writes new ones for each city.
const shownTab = dialog => dialog.querySelector('[role="tab"][aria-selected="true"]');
function selectTab(dialog, tab) {
  for (const t of dialog.querySelectorAll('[role="tab"]')) {
    const on = t === tab;
    t.setAttribute('aria-selected', String(on));
    t.tabIndex = on ? 0 : -1;
    document.getElementById(t.getAttribute('aria-controls')).hidden = !on;
  }
  dialog.querySelector('.about-body').scrollTop = 0;
}
/** The dialog's tabs work as tabs (roving tabindex); `picked(tab, source)` hears each one a visitor picks. */
function tabbed(dialog, picked) {
  dialog.classList.add('tabbed');
  dialog.addEventListener('click', e => {
    const tab = e.target.closest('[role="tab"]');
    if (tab && tab.getAttribute('aria-selected') !== 'true') { selectTab(dialog, tab); picked(tab, 'click'); }
  });
  dialog.addEventListener('keydown', e => {
    const tabs = [...dialog.querySelectorAll('[role="tab"]')], i = tabs.indexOf(document.activeElement);
    const to = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[e.key];
    if (i < 0 || to === undefined) return;
    e.preventDefault();
    const tab = tabs[(to + tabs.length) % tabs.length];
    selectTab(dialog, tab);
    tab.focus();
    picked(tab, 'keyboard');
  });
}
/** Close the player's popups and panels, for a dialog (the about panel, the city guide) that opens over them. */
function closePopups() {
  closeQueue();
  closeMixer();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  closeBoard();
}

/* ---------- about panel: tabs for about, cities, music, shortcuts, FAQ (content written by tools/seo/seo.mjs) ---------- */
const about = $('#about');
tabbed(about, (tab, source) => track('about_tab_selected', { tab: tab.id.replace('about-tab-', ''), source }));
selectTab(about, about.querySelector('[role="tab"]'));
function toggleAbout(source, { shortcuts = false } = {}) {
  if (about.open) { about.close(); return; }
  closePopups();
  const keysTab = $('#about-tab-keys');
  if (shortcuts && keysTab) selectTab(about, keysTab);              // ? is the usual "show me the shortcuts" key
  about.showModal();
  const open = shownTab(about);
  open?.focus();                                                    // the open tab, so the arrow keys work right away
  track('about_opened', { source, tab: open?.id.replace('about-tab-', '') });
}
$('#info').addEventListener('click', () => toggleAbout('button'));
about.addEventListener('click', e => {
  if (e.target === about || e.target.closest('[data-close]')) { about.close(); return; }   // backdrop or close button
  const link = e.target.closest('a[data-city]');                  // switch in place (keeps the music going)
  if (link && e.button === 0 && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
    e.preventDefault();
    about.close();
    showCity(link.dataset.city, { source: 'about' });
  }
});
about.addEventListener('close', wake);

/* ---------- city guide (site/js/ui/guide.js): places, restaurants and things to do in the city on screen ---------- */
// The pill under the live count on the city's title (or E) opens it, in a city that has a guide (GUIDES; the others show
// no pill, and E says so). A city page has its own guide written in by
// tools/seo/seo.mjs, for crawlers; any other city's is fetched from guide/<id>.json as the guide opens, or as the city
// changes under it (a tour), and kept for the visit. Every place opens a search in a new tab, so the music plays on: on
// Google Maps or OpenStreetMap, as picked with the switch in its head (MAPS, kept as 'maps'; it follows the account).
// Opening it and anything inside it stay quiet (firstGesture).
const guide = $('#guide'), guideContent = guide.querySelector('.guide-content');
const guides = new Map();                           // city id -> its guide, fetched this visit
const savedMaps = store.get('maps', null);
let maps = MAPS.some(m => m.id === savedMaps) ? savedMaps : MAPS[0].id;
tabbed(guide, (tab, source) => track('guide_tab_selected', { tab: tab.id.replace('guide-tab-', ''), source }));
if (guide.dataset.city) selectTab(guide, guide.querySelector('[role="tab"]'));
showMaps();                                         // a city page's own guide comes written for Google Maps
const guideBtn = Object.assign(document.createElement('button'), {
  type: 'button', id: 'guideBtn', className: 'guide-btn', title: 'Places, restaurants and things to do (E)', hidden: true,
  innerHTML: '<svg aria-hidden="true"><use href="#i-pin"/></svg><span>City guide</span>',
});
($('#live') ?? looksEl).after(guideBtn);            // no #live only on a page from an older deploy
guideBtn.addEventListener('click', () => toggleGuide('button'));
/** A line in the guide in place of the lists, while they load or when they didn't. */
function guideNote(city, text) {
  guideContent.innerHTML = '<div class="about-body"><h2 id="guideTitle"></h2><p class="guide-note"></p></div>';
  guideContent.querySelector('h2').textContent = `${city.name} city guide`;
  guideContent.querySelector('p').textContent = text;
}
/** The guide's switch and links on the maps picked, each place relinked from its query on them (data-google, data-osm). */
function showMaps() {
  for (const b of guideContent.querySelectorAll('[data-maps]')) b.setAttribute('aria-pressed', String(b.dataset.maps === maps));
  for (const a of guideContent.querySelectorAll(`.guide-list a[data-${maps}]`)) {
    const link = placeLink(a.firstChild.textContent, a.dataset[maps], maps);
    a.href = link.href;
    a.setAttribute('aria-label', link.label);
  }
}
/** Where the guide's places open (a MAPS id), kept for the next visit. */
function setMaps(id, trigger) {
  if (!MAPS.some(m => m.id === id) || id === maps) return;
  const previous = maps;
  maps = id;
  store.set('maps', id);
  showMaps();
  if (trigger !== 'sync') track('guide_maps_changed', { maps: id, previous, trigger, city: guide.dataset.city || null });
}
/** The guide of the city on screen: kept as it is when it shows that city already, else fetched (or kept) and written in. */
async function renderGuide() {
  const t = target();
  if (!t || guide.dataset.city === t.id) return;
  const id = t.id, city = { name: t.scene.name, country: byId(id).country };
  guide.dataset.city = id;
  if (!guides.has(id)) guideNote(city, 'Loading…');
  try {
    if (!guides.has(id)) {
      const res = await fetch(new URL(`guide/${id}.json`, ROOT));
      if (!res.ok) throw new Error(`guide/${id}.json: HTTP ${res.status}`);
      guides.set(id, await res.json());
    }
    if (guide.dataset.city !== id) return;          // another city came first
    guideContent.innerHTML = guideHtml(city, guides.get(id), maps);
    selectTab(guide, guide.querySelector('[role="tab"]'));
    if (guide.open) shownTab(guide).focus();         // as when it opens on a guide it has: the arrow keys work right away
  } catch (e) {
    if (guide.dataset.city !== id) return;
    guide.dataset.city = '';                        // the next open tries again
    guideNote(city, navigator.onLine ? 'The guide didn\'t load. Close it and try again.' : 'The guide needs a connection.');
    track('guide_load_failed', { city: id, error: String(e && e.message || e) });
  }
}
function toggleGuide(source) {
  if (guide.open) { guide.close(); return; }
  const t = target();
  if (!t) return;                                   // nothing on screen yet
  if (!GUIDES.includes(t.id)) { toast(`${t.scene.name} has no guide yet`, 1800); return; }
  closePopups();
  renderGuide();
  guide.showModal();
  const open = shownTab(guide);
  open?.focus();                                    // the open tab, so the arrow keys work right away
  track('guide_opened', { source, tab: open?.id.replace('guide-tab-', '') ?? null });
}
guide.addEventListener('click', e => {
  if (e.target === guide || e.target.closest('[data-close]')) { guide.close(); return; }   // backdrop or close button
  const pick = e.target.closest('[data-maps]');
  if (pick) { setMaps(pick.dataset.maps, 'guide'); return; }
  const place = e.target.closest('.guide-list a');
  if (place) track('guide_place_clicked', { list: place.closest('.guide-list').dataset.list, place: place.firstChild.textContent, maps });
});
guide.addEventListener('close', wake);

/* ---------- billboard: every city has one for rent, and on the website clicking it invites people to follow on X ---------- */
// The face shows the city's own made-up ad until a business takes it, so the scene stays calm. With PITCH on, this
// invisible button sits over the face, says "Your business here?" on hover and opens the card; the About tab and the
// FAQ link the form either way. With PITCH off (hidden from visitors for now, SPONSOR_PITCH) a link to the site's X
// account (#xSpot, content_link_clicked, destination x-app, placement billboard; the Lofi Cities Discord on 2026-09-29)
// takes the button's place, and the face's "new cities on X" opens it too. In OBS, and with the billboards set to city ads or off (Settings), there
// is nothing to click. The card's text is written by tools/seo/seo.mjs.
// While the face shows "post a job for free", a link (#jobSpot) takes the button's place and opens the Post a job page
// (content_link_clicked, placement billboard). The frame loop swaps them as the face rolls on.
const pitchSpot = $('#pitchSpot'), pitchCard = $('#pitchCard'), pitchLink = $('#pitchCardLink'), formUrl = pitchLink.href;
const jobSpot = $('#jobSpot'), xSpot = $('#xSpot'), SPOTS = [pitchSpot, jobSpot, xSpot];
let onFace = null;                                  // what the face shows right now: 'x', 'job' or null (the city's own ad)
function placePitchSpot(t = loopTime()) {
  const face = billboardFace();
  onFace = pitchOnShow(t, face);
  const ownAd = !face ? null : PITCH ? pitchSpot : xSpot;   // no face: OBS, or the billboards set to city ads or off
  const v = target(), spot = onFace === 'job' ? jobSpot : onFace === 'x' ? xSpot : ownAd;
  const r = spot && v?.scene.billboard ? display.spot(v, v.scene.billboard) : null;
  const focused = SPOTS.includes(document.activeElement);
  for (const el of SPOTS) el.hidden = el !== spot || !r;   // also when the view is dragged off most of the face
  if (r) Object.assign(spot.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });
  if (focused && r && document.activeElement !== spot) spot.focus({ preventScroll: true });   // a keyboard visitor stays on the billboard
}
addEventListener('resize', () => placePitchSpot());
placePitchSpot();
function openPitchCard(trigger) {
  closeBoard();
  const { id, scene } = target();
  closeQueue();
  closeMixer();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  for (const el of pitchCard.querySelectorAll('.pitch-city')) el.textContent = scene.name;
  const url = new URL(formUrl);
  url.searchParams.set('city', id);                  // a hidden "city" field on the form picks it up
  pitchLink.href = url.href;
  pitchCard.showModal();
  track('billboard_opened', { city: id, trigger });
}
pitchSpot.addEventListener('click', e => openPitchCard(e.detail === 0 ? 'keyboard' : 'click'));
pitchCard.addEventListener('click', e => {
  if (e.target === pitchCard || e.target.closest('[data-close]')) pitchCard.close();   // backdrop or close button
});
pitchCard.addEventListener('close', wake);

/* ---------- the sale plane: a banner across the sky while the video loops are on sale (site/js/ui/plane.js) ---------- */
// While the shop has a sale (SHOP.sale, site/js/ui/shop.js) a plane tows its banner across the sky every few minutes,
// drawn after the scene, under the lanterns and the pigeon (drawOverlay). A link (#planeSpot) follows it and opens the
// shop in a new tab (shop_link_clicked, placement plane). The keyboard's way to the shop is Get video loops, so the
// link takes no focus.
// Not in OBS, and not for a visitor who set the billboards to city ads or off (Settings): they asked for no site ads.
// That pick counts only while Settings offers it (billboardsPick): with the admin page's billboard switches off nobody
// can change theirs, so the plane flies for everyone, and its own Sale plane switch (siteSwitches.salePlane) grounds it.
// The sale ends by itself (SHOP.sale.until): a page opened after has none, and one still open grounds the plane then.
const planeSpot = $('#planeSpot'), sale = shopSale();
if (sale) {
  planeSpot.href = shopLinks().store;
  planeSpot.querySelector('span').textContent = `${sale.label} ↗`;
  planeSpot.setAttribute('aria-label', `${sale.label} on Gumroad (opens in a new tab)`);
}
// The plane over view v at the frame's time `now`, for drawOverlay (null: none in the sky); its link follows it.
function planeNow(v, now) {
  const flies = saleOn(sale) && !OBS && siteSwitches.salePlane && (!billboardsPick() || billboards === 'all');
  const pose = flies ? planePose(now, display.view(v), { words: sale.banner, clear: titleFoot, still: calm() }) : null;
  placePlaneSpot(pose ? display.spot(v, planeRect(pose)) : null);   // null too while most of it is off the view
  return pose;
}
// The link over it: page rect `r` (null: none), at least 44 px tall.
let planeSpotAt = '';
function placePlaneSpot(r) {
  const height = r ? Math.max(44, r.height) : 0;
  const at = r ? [r.left, r.top + (r.height - height) / 2, r.width, height].map(Math.round).join() : '';
  if (at === planeSpotAt) return;                    // called every frame: only a change touches the page
  planeSpotAt = at;
  planeSpot.hidden = !r;
  if (!r) return;
  const [left, top, width, tall] = at.split(',');
  Object.assign(planeSpot.style, { transform: `translate(${left}px, ${top}px)`, width: `${width}px`, height: `${tall}px` });
}

/* ---------- shared moments: on the hour and at half past, the same for everyone (site/js/ui/moments.js) ---------- */
// Twice an hour something happens in the city on screen, at the same instant in every browser, with nothing sent: when
// the city's own clock reads :00 its landmark lights up, or small fireworks, lanterns or shooting stars come by it, and
// at :30 its birds or a night flight cross the sky (momentNear: a pure function of the clock and the scene). The clock
// is the live worker's where the page has it, as the radio's is, else the device's. In the last minutes before one, a
// line under the tagline counts down ("Fireworks in 3 min"): plain text that changes once a minute (renderMoment).
// The moment is drawn after the scene and the sale plane, under the lanterns and the reactions (drawOverlay): its light
// and sparks into two scene-pixel buffers of its own, what crosses the sky straight onto the screen in the screen's
// pixels, so it glides (site/js/ui/moments-draw.js). Never into a scene: PNG, MP4 and the mini player stay as they were.
// Not in OBS or ?creator. A visitor who hid Reactions (Settings) asked for nothing shared over the scene, and gets
// neither the moment nor its countdown. With reduced motion nothing flashes, flaps or blinks (a kind that flashes
// gives way to the landmark's glow), and a kind that needs a dry sky gives way to it under a picked rain or snow.
// To try one, ?moments=hour or ?moments=half moves this page's clock to 20 s before the city's next one (no event is
// sent for a moment seen that way).
const MOMENTS_OFF = OBS || CREATOR;
const momentsParam = new URLSearchParams(location.search).get('moments');
let momentSkew = 0, momentTried = momentsParam !== 'hour' && momentsParam !== 'half';
const momentClock = () => (live ? live.serverNow() : Date.now()) + momentSkew;
const momentNote = Object.assign(document.createElement('p'), { id: 'momentNote', className: 'moment-note', hidden: true });
$('.sub').after(momentNote);                        // under the tagline and the time, over the looks and the live count
const momentsCounted = new Set(), momentsSeen = new Set();   // "look:slot" of the countdowns this page showed, and of the moments it drew
let momentBufs = null;                              // [light, its context, paint, its context], made for the first hour moment drawn
/** The moment near `ms` over view v (on now, or within its countdown), or null: none near, or none wanted. */
function momentOver(v, ms) {
  if (MOMENTS_OFF || !reactionsOn || !v) return null;
  return momentNear(v.look, v.scene, ms, slipOf(v.scene.timeZone, ms), { still: calm(), weather });
}
function tryMoment(v) {                             // ?moments=hour|half: this page's clock goes to 20 s before that moment
  momentTried = true;
  const ms = momentClock();
  let { n, at } = slotAt(ms, slipOf(v.scene.timeZone, ms));
  do { n++; at += HALF_MS; } while ((n % 2 === 0) !== (momentsParam === 'hour'));
  momentSkew = at - 20_000 - ms;
}
// The countdown, once a second and as a scene is shown; the page is only touched when its words change.
function renderMoment() {
  const v = target();
  if (v && !momentTried) tryMoment(v);
  const ms = momentClock(), m = momentOver(v, ms), text = countdownOf(m, ms);
  if (text !== momentNote.textContent) {
    momentNote.textContent = text;
    momentNote.hidden = !text;
    tickClocks();                                   // the local time above it turns with it, not up to ten seconds later
  }
  const key = m && `${v.look}:${m.n}`;
  if (!text || momentSkew || momentsCounted.has(key) || document.hidden || document.body.matches('.idle, .ui-off')) return;
  momentsCounted.add(key);                          // once per moment, the first time its countdown is on a card that shows
  track('moment_countdown_shown', {
    city: v.id, kind: m.kind, slot: m.slot, minutes: minutesTo(m, ms), arrival_s: Math.round(performance.now() / 1000),
    music_playing: lastState === 'playing',
  });
}
setInterval(renderMoment, 1000);
// The moment over view v, for drawOverlay: nothing at all between moments.
function drawMoments(v) {
  if (MOMENTS_OFF || !v) return;
  const ms = momentClock(), m = momentOver(v, ms);
  if (!m || ms < m.from) return;
  const age = (ms - m.at) / 1000;
  if (m.slot === 'half') {
    const pose = passerPose(m, age);
    if (!pose) return;
    display.sprites(v, pose.x, pose.y, put => drawPasser(put, pose));
    const [x0, x1] = passerSpan(m, pose), view = display.view(v);
    if (x1 < view.x || x0 > view.x + view.w) return;   // not in what this screen shows of the scene (a phone's crop) yet
  } else {
    momentBufs ??= [...makeCanvas(W, H), ...makeCanvas(W, H)];
    const [light, lightCtx, paint, paintCtx] = momentBufs;
    lightCtx.clearRect(0, 0, W, H);
    paintCtx.clearRect(0, 0, W, H);
    if (!drawMoment(lightCtx, paintCtx, m, age)) return;
    display.overlay(v, light, 'lighter');
    display.overlay(v, paint);
  }
  const key = `${v.look}:${m.n}`;
  if (momentSkew || momentsSeen.has(key)) return;
  momentsSeen.add(key);                             // once per moment, with the first frame of it that reached this screen
  track('moment_seen', {
    city: v.id, kind: m.kind, slot: m.slot, late_s: Math.floor((ms - m.from) / 1000), countdown: momentsCounted.has(key),
    music_playing: lastState === 'playing', listened_s: listenedS(),
  });
}

/* ---------- What's new: a pigeon brings the updates since the last visit (README "What's new") ---------- */
// tools/seo/seo.mjs publishes the latest lines of the What's new page as whats-new/news.json (site/js/ui/news.js). A
// few seconds after the first city is up, and whenever the tab is in view (at most every NEWS_CHECK_MS), the page reads
// it. When this browser has lines it hasn't read, a pigeon flies in with a letter and sits on the billboard
// (site/js/ui/pigeon.js; after a city switch it flies in again there), a badge over it naming the most notable kind
// of update. Tapping it (#newsSpot) opens the card (#news) with those lines, each tagged with its kind, marks them read
// and sends it off. Lines that went live after this page loaded came with a deploy it doesn't have, so the card offers
// a reload for them (the music would stop, so never on its own). A first visit only notes what there is. Not in OBS.
// With the billboards off (the visitor's pick, the admin page's switch) it sits on the player instead: on its right-hand
// end, on the last chip where the chips reach that far, on a card over that spot (dockPerch), drawn into #newsBird, a
// canvas in the dock, so it idles away with it. It is gone, like those cards, while a panel is open (css #newsBird).
const newsEl = $('#news'), newsList = $('#newsList'), newsSpot = $('#newsSpot');
const newsBird = $('#newsBird'), birdCtx = newsBird.getContext('2d');
newsBird.width = DOCK.w; newsBird.height = DOCK.h;
const birdDock = { hud: $('.hud-bottom'), player: $('#player'), chips: [...document.querySelectorAll('#cities .chip')], cards: [...document.querySelectorAll('.rv, #jamPads')] };
const newsOpen = () => !newsEl.hidden;
const returning = store.get('city', null) != null;   // came before (read before this visit's first city is kept)
let newsAll = [], newsUnseen = [], newsBoot = null, newsLate = false, newsReadAt = -Infinity;
let pigeon = null;                                   // { roost, perch, at, left, type } while one is here (pigeonPose)
// where it sits over view v: its look's billboard, or 'dock' (the player) with none on show
const roostOf = v => (shownBillboards() === 'off' || !v.scene.billboard ? 'dock' : v.look);
const perchAt = (v, roost) => (roost === 'dock' ? DOCK : perchOf(v.scene));
async function checkNews(reason) {
  newsReadAt = performance.now();
  let lines;
  try {
    const res = await fetch(new URL('whats-new/news.json', ROOT), { cache: 'no-cache' });
    lines = res.ok ? newsLines(await res.json()) : [];
  } catch { return; }                                // offline: the next check tries again
  if (!lines.length) return;
  newsBoot ??= new Set(lines.map(l => l.id));
  const seen = seenNow(store.get('newsSeen', null), lines, returning);
  if (!seen) { store.set('newsSeen', seenOf(lines)); return; }   // a first visit: news from here on
  newsAll = lines;
  if (newsOpen()) return;                            // the card keeps what it shows until it closes
  newsUnseen = unseenLines(lines, seen);
  newsLate = cameLater(newsUnseen, newsBoot);
  renderNewsSpot();
  if (pigeon && pigeon.left == null) pigeon.type = topType(newsUnseen);   // a later check can bring a more notable kind
  if (newsUnseen.length && !pigeon) sendPigeon(reason);
  else if (!newsUnseen.length && pigeon && pigeon.left == null) pigeon.left = performance.now();   // read in another tab
}
function sendPigeon(reason) {
  const v = target();
  if (!v) return;
  const type = topType(newsUnseen), n = newsUnseen.length, roost = roostOf(v), on = roost === 'dock' ? 'player' : 'billboard';
  pigeon = { roost, perch: perchAt(v, roost), at: performance.now(), left: null, type };
  $('#newsSr').textContent = `A pigeon landed on the ${on} with ${n === 1 ? 'an update' : `${n} updates`}${type ? ` (${NEWS_TYPES[type].label})` : ''}: the What's new button there opens ${n === 1 ? 'it' : 'them'}.`;
  track('whats_new_arrived', { reason, unseen: n, late: newsLate, city: v.id, billboards, perch: on, badge: type });
}
// The pigeon's pose over view v, for drawOverlay: until it has been read it follows a city switch, a city's other look
// and the billboards going off or coming back (in again on that billboard, or on the player). On the player it is
// drawn here, into #newsBird, and drawOverlay gets nothing. Its button follows it.
function pigeonNow(v) {
  const roost = roostOf(v), now = performance.now();
  let pose = null;
  if (pigeon && pigeon.roost !== roost) pigeon = pigeon.left == null ? { ...pigeon, roost, perch: perchAt(v, roost), at: now } : null;
  if (pigeon) {
    pose = pigeonPose(pigeon, now, { reduced: calm() });
    if (!pose) pigeon = null;                        // flown off
  }
  const dock = drawDockPigeon(v, roost === 'dock' ? pose : null);
  const r = !pose?.perched ? null : pigeonRect(pigeon.perch, pose.type);
  if (!r) placeNewsSpot(null);
  else if (dock) placeNewsSpot({ left: dock.left + r.x * dock.k, top: dock.top + r.y * dock.k, width: r.w * dock.k, height: r.h * dock.k }, true);
  else placeNewsSpot(display.spot(v, r));            // null too when a phone's drag crops it off
  return dock ? null : pose;
}
// On the player: `pose` (null: not there) into #newsBird, laid in the dock with the feet at dockPerch. Returns where
// that canvas is on the page ({ left, top }, CSS px) and its scale k, or null.
let newsBirdAt = '';
function drawDockPigeon(v, pose) {
  if (!pose) {
    if (!newsBird.hidden) newsBird.hidden = true;
    return null;
  }
  const { hud, player, chips, cards } = birdDock, shown = display.view(v), k = dockScale(shown.width / shown.w);
  const rects = els => els.map(el => el.getBoundingClientRect()).filter(r => r.width);   // the ones on show
  const feet = dockPerch({ player: player.getBoundingClientRect(), chip: rects(chips).at(-1) ?? null, cards: rects(cards), k, width: innerWidth });
  const from = hud.getBoundingClientRect();          // the canvas is laid out in the dock's own box
  const left = Math.round(feet.x - from.left - (DOCK.x + 0.5) * k), top = Math.round(feet.y - from.top - (DOCK.y + 1) * k);
  const at = `${left},${top},${k}`;
  if (at !== newsBirdAt) {                           // called every frame: only a change touches the page
    newsBirdAt = at;
    Object.assign(newsBird.style, { left: `${left}px`, top: `${top}px`, width: `${DOCK.w * k}px`, height: `${DOCK.h * k}px` });
  }
  if (newsBird.hidden) newsBird.hidden = false;
  birdCtx.clearRect(0, 0, DOCK.w, DOCK.h);
  drawPigeon(birdCtx, birdCtx, pose, newsSpot.matches(':hover, :focus-visible'));
  return { left: from.left + left, top: from.top + top, k };
}
// The button over the sitting pigeon: page rect `r` (null: none), at least 44 px each way. On the player (`docked`) it
// grows upwards only, so what the pigeon stands on keeps its own taps.
let newsSpotAt = '';
function placeNewsSpot(r, docked = false) {
  let box = null;
  if (r) {
    const width = Math.max(44, r.width), height = Math.max(44, r.height);   // bird-sized, but tappable
    box = { left: r.left + (r.width - width) / 2, top: r.top + (r.height - height) / (docked ? 1 : 2), width, height };
  }
  const at = box ? `${Object.values(box).map(Math.round).join()},${docked}` : '';
  if (at === newsSpotAt) return;                     // called every frame: only a change touches the page
  newsSpotAt = at;
  newsSpot.hidden = !box;
  newsSpot.classList.toggle('docked', docked);
  if (box) for (const [k, px] of Object.entries(box)) newsSpot.style[k] = `${Math.round(px)}px`;
}
function renderNewsSpot() {
  const n = newsUnseen.length;
  newsSpot.querySelector('span').textContent = `What's new · ${n}`;
  const type = topType(newsUnseen);
  newsSpot.setAttribute('aria-label', `What's new: ${n === 1 ? 'an update' : `${n} updates`} the pigeon brought${type ? ` (${NEWS_TYPES[type].label})` : ''}`);
}
function newsTag(type) {
  const tag = Object.assign(document.createElement('span'), { className: 'news-tag', textContent: NEWS_TYPES[type].label });
  tag.dataset.type = type;
  return tag;
}
function newsThanks(name) {   // after the line, as on whats-new/ (tools/seo/seo.mjs)
  const thanks = Object.assign(document.createElement('span'), { className: 'news-thanks' });
  const cup = Object.assign(document.createElement('span'), { textContent: '☕' });
  cup.setAttribute('aria-hidden', 'true');
  thanks.append(cup, `\u00a0${thanksTo(name)}`);   // the cup stays with its words
  return thanks;
}
function renderNews() {
  $('#newsInfo').textContent = newsLate ? 'new since this tab opened' : `${newsUnseen.length} since your last visit`;
  newsList.replaceChildren(...byDay(newsUnseen).map(({ date, lines }) => {   // a heading per day, its lines as bullets
    const day = document.createElement('section'), h = document.createElement('h3'), time = document.createElement('time');
    const ul = document.createElement('ul');
    time.dateTime = date;
    time.textContent = shortDay(date);
    h.append(time);
    ul.append(...lines.map(({ type, text, thanks }) => {    // each tagged with its kind, as on whats-new/
      const li = document.createElement('li');
      li.append(...(type ? [newsTag(type), ' '] : []), text, ...(thanks ? [' ', newsThanks(thanks)] : []));
      return li;
    }));
    day.append(h, ul);
    return day;
  }));
  $('#newsReload').hidden = !newsLate;
}
function openNews(trigger) {
  closeBoard();
  if (!newsUnseen.length) return;
  closeQueue();
  closeMixer();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeTogether();
  closeChat();
  document.body.classList.remove('ui-off');
  renderNews();
  newsEl.hidden = false;
  newsSpot.setAttribute('aria-expanded', 'true');
  newsList.scrollTop = 0;
  newsEl.focus({ preventScroll: true });             // its button is about to fly off
  store.set('newsSeen', seenOf(newsAll));            // read
  if (pigeon && pigeon.left == null) pigeon.left = performance.now();
  track('whats_new_opened', { trigger, unseen: newsUnseen.length, late: newsLate, city: target()?.id });
}
function closeNews() {
  if (!newsOpen()) return;
  const hadFocus = newsEl.contains(document.activeElement);
  newsEl.hidden = true;
  newsSpot.setAttribute('aria-expanded', 'false');
  newsUnseen = [];
  newsLate = false;
  if (hadFocus) $('#play').focus({ preventScroll: true });   // the pigeon has gone: back to the player
}
newsSpot.addEventListener('click', e => openNews(e.detail === 0 ? 'keyboard' : 'pigeon'));
$('#newsClose').addEventListener('click', closeNews);
$('#newsReload').addEventListener('click', () => {
  trackOnExit('whats_new_reloaded', { unseen: newsUnseen.length, city: target()?.id, music_playing: lastState === 'playing' });
  location.reload();
});
function startNews() {
  if (OBS) return;
  setTimeout(() => checkNews('load'), 4000);         // once the city has settled in
  const due = () => !document.hidden && performance.now() - newsReadAt >= NEWS_CHECK_MS;
  document.addEventListener('visibilitychange', () => { if (due()) checkNews('visible'); });
  setInterval(() => { if (due()) checkNews('timer'); }, 60 * 1000);
}
// the card's "Get in touch" and the FAQ's form link, middle-clicks too, and whether the visitor was listening at the time
for (const a of document.querySelectorAll('a[data-sponsor]')) {
  const report = e => {
    if (e.type === 'auxclick' && e.button !== 1) return;
    track('billboard_link_clicked', { city: target()?.id, source: a.dataset.sponsor, music_playing: lastState === 'playing', listened_s: listenedS() });
  };
  a.addEventListener('click', report);
  a.addEventListener('auxclick', report);
}

/* ---------- suggestion cards: the weather, then the tour, early in a listen (site/js/ui/nudge.js, docs/APP_REFERENCE.md) ---------- */
// A card in the vote card's place above the dock (waiting while the vote card, a panel or H has the room): 20 s into a
// listen it offers three weathers, after 2 minutes the tour. A pick goes the menus' way (setWeather, setTour: trigger
// nudge). Each shows once per browser ('nudged', kept as it shows), never to one that has picked a weather or started a
// tour ('weather' and 'tour' are only kept once it has), nor in the holdout, OBS or ?creator (nudges, at the top).
const nudgeSeen = () => { const seen = store.get('nudged', []); return Array.isArray(seen) ? seen : []; };
const nudgeUsed = () => ({ weather: store.get('weather', null) != null, tour: store.get('tour', null) != null });
let nudgeState = nudgeClock(), nudgeAt = performance.now(), nudgeTimer = 0;
const nudgeCard = createNudgeCard($('#nudge'), $('#nudgeSr'), {
  hover: () => canHover.matches,
  onPick(kind, choice) {
    nudgeState = closeNudge(nudgeState);
    hideNudge(kind);
    if (kind === 'weather') setWeather(choice, 'nudge'); else setTour(choice, 'nudge');
    track('nudge_used', { kind, choice });
  },
  onClose(kind) {
    nudgeState = closeNudge(nudgeState);
    hideNudge(kind);
    track('nudge_dismissed', { kind, how: 'close' });
  },
});
function hideNudge(kind) {
  if (nudgeCard.hide()) (kind === 'weather' ? weatherBtn : tourBtn).focus({ preventScroll: true });   // a keyboard carries on at its chip
}
function tickNudge() {
  const now = performance.now(), seen = nudgeSeen(), vote = voteCard.shown !== null;
  const r = stepNudge(nudgeState, {
    dt: now - nudgeAt, group: nudges, seen, used: nudgeUsed(), playing: audio.state === 'playing', visible: !document.hidden,
    open: anythingOpen(), blocked: vote || document.body.classList.contains('ui-off'), held: nudgeCard.held,
  });
  nudgeAt = now;
  nudgeState = r.state;
  if (r.end) {
    hideNudge(r.end.kind);
    track('nudge_dismissed', { kind: r.end.kind, how: r.end.how });
  }
  if (r.show) {
    store.set('nudged', [...seen, r.show]);          // shown: it doesn't come back, whatever comes of it
    const choices = r.show === 'weather' ? weatherChoices(target()?.scene) : [TOUR_MINUTES];
    nudgeCard.show(r.show, choices);
    track('nudge_shown', { kind: r.show, ...(r.show === 'weather' && { choices }) });
  }
  nudgeCard.wait(vote);                              // a panel or H hide it through css (.rv)
  if (!nudgeState.card && !nudgesLeft({ group: nudges, seen: nudgeSeen(), used: nudgeUsed() })) clearInterval(nudgeTimer);
}
if (nudgesLeft({ group: nudges, seen: nudgeSeen(), used: nudgeUsed() })) nudgeTimer = setInterval(tickNudge, 1000);

let toastTimer = 0, toastRuns = [];
const toastButtons = [$('#toastAction'), $('#toastAction2')];
/**
 * A line at the top: gone after `ms` (0: stays), with an export's `progress` (0 to 1) or an `action` ({ label, run },
 * or two of them in a list), a button after it.
 */
function toast(text, ms = 0, progress = null, action = null) {
  $('#toastText').textContent = text;
  $('#toast .progress').hidden = progress == null;      // progress means an export, which the Cancel button next to the bar stops
  if (progress != null) $('#toastBar').style.width = `${(progress * 100).toFixed(1)}%`;
  const actions = [action].flat().filter(Boolean);
  toastButtons.forEach((b, i) => { b.hidden = !actions[i]; b.textContent = actions[i]?.label ?? ''; });
  toastRuns = actions.map(a => a.run);
  $('#toast').hidden = false;
  clearTimeout(toastTimer);
  if (ms) toastTimer = setTimeout(() => { $('#toast').hidden = true; }, ms);
}
toastButtons.forEach((b, i) => b.addEventListener('click', () => {
  clearTimeout(toastTimer);
  $('#toast').hidden = true;
  toastRuns[i]?.();                                 // in the click itself: a sign-in's popup needs the gesture
}));

/* ---------- mini player: the scene floats in a Picture-in-Picture window over other apps ---------- */
// The desk mini player in Chrome and Edge (site/js/ui/miniplayer.js: the scene, the music's and the cities' controls and
// the timers), the scene alone as a video elsewhere (site/js/engine/pip.js).
// Computers only: iOS stops rendering background pages (the window would freeze there), mobile browsers are untested,
// and a phone's dock has no room for another button.
const pipAvailable = (deskPlayer || pipSupported()) && !isTouch;
let pipOpenedAt = 0;
async function togglePip(trigger) {
  if (pip.active) { pip.close(); return; }
  if (!pipAvailable || !current) return;
  try {
    if (!await pip.open(current.buf)) return;
    if (deskPlayer) track('mini_player_opened', { city: target()?.id, trigger });
    else track('pip_opened', { trigger });
  } catch (e) {
    console.error(e);
    toast('The mini player could not open', 3000);
    track('pip_failed', { error: String(e && e.message || e) });
  }
}
function onPipChange(on) {
  $('#pip').setAttribute('aria-pressed', String(on));
  if (on) { pipOpenedAt = performance.now(); return; }
  const seconds = Math.round((performance.now() - pipOpenedAt) / 1000);
  if (deskPlayer) track('mini_player_closed', { city: target()?.id, seconds_open: seconds });
  else track('pip_closed', { seconds });
}
/** what the desk mini player's bar shows (miniView in site/js/ui/miniplayer.js): the dock's music, city and track, and the timers */
function miniState() {
  return { playing: audio.state === 'playing' || audio.state === 'starting', city: $('#cityName').textContent, track: audio.track?.title, focus, sleep };
}
/** the mini player's timer button: starts a focus timer, then pauses and resumes it */
function miniTimer() {
  if (focus) pauseOrResume('mini_player');
  else setFocus(MINI_PRESET, 'mini_player');
}
if (!pipAvailable) $('#pip').hidden = true;
else if (deskPlayer) $('#pip').title = 'Mini player: the scene, the music and your timer on top of other windows (P)';
$('#pip').addEventListener('click', e => togglePip(e.detail === 0 ? 'keyboard' : 'button'));

/* ---------- creator tools: S = PNG, also in the save menu for touch screens; R = export one loop as MP4 (only with ?creator) ---------- */
async function recordLoop(trigger) {
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  if (recording || !target()) return;
  if (fade) { current = fade.to; fade = null; }
  recording = true; cancelRec = false;
  $('#saveBtn').disabled = true;
  const inst = current, started = performance.now(), props = { city: inst.id, trigger };
  track('export_started', props);
  try {
    const res = await exportLoop({
      render: t => inst.render(t, VIDEO), buf: inst.buf, name: `lofi-cities-${inst.look}-loop-4min-1080p60`,
      onStatus: (text, p) => toast(text, 0, p), isCancelled: () => cancelRec,
    });
    toast(res ? `Saved ${res.ext.toUpperCase()} · ${(res.size / 1048576).toFixed(0)} MB` : 'Recording cancelled', 4000);
    const seconds = Math.round((performance.now() - started) / 1000);
    if (res) track('export_finished', { ...props, format: res.ext, size_mb: Math.round(res.size / 1048576), seconds });
    else track('export_cancelled', { ...props, seconds });
  } catch (e) {
    console.error(e);
    toast(`Export failed: ${e.message || e}`, 6000);
    track('export_failed', { ...props, error: String(e && e.message || e) });
  } finally {
    recording = false;
    $('#saveBtn').disabled = false;
  }
}
function snapshot(trigger) {
  closeSave();
  closeShare();
  if (recording || !current) return;
  const inst = current, opts = lookOpts(inst, stillOpts(weather));
  savePNG({ render: t => inst.render(t, opts), buf: inst.buf, t: loopTime(), name: `lofi-cities-${inst.look}` });
  track('screenshot_saved', { city: current.id, trigger });
}
// The save menu's postcard (site/js/ui/postcard.js): the frame a PNG save gives (the same picks) on a card with the
// city's name, its time, the track that plays and loficities.com. A touch screen whose share sheet takes files gets it
// there, with the city's link and line; elsewhere it downloads, as it does where the sheet won't open. Made within the
// tap, which the sheet needs. postcard_saved once it is shared or saved; a sheet closed without sharing counts nothing.
async function postcard() {
  closeSave();
  closeShare();
  if (recording || !current) return;
  const inst = current, city = byId(inst.id);
  inst.render(loopTime(), lookOpts(inst, stillOpts(weather)));
  const card = drawPostcard({ frame: inst.buf, city: city.name, time: localTime(inst.scene.timeZone), track: trackLine(audio.track) });
  const sent = await sendPostcard({ file: pngFile(card, postcardName(inst.look)), share: shareOf(city), touch: isTouch });
  if (sent) track('postcard_saved', { city: inst.id, shared: sent.shared });
}
$('#toastCancel').addEventListener('click', () => { cancelRec = true; });

function openSave(trigger) {
  closeShare();
  closeBoard();
  closeQueue();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeVibe();
  closeMixer();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  document.body.classList.remove('ui-off');
  saveMenu.hidden = false;
  $('#saveBtn').setAttribute('aria-expanded', 'true');
  if (trigger === 'keyboard') saveMenu.querySelector('button').focus();
}
function closeSave() {
  if (!saveOpen()) return;
  const hadFocus = saveMenu.contains(document.activeElement);
  saveMenu.hidden = true;
  $('#saveBtn').setAttribute('aria-expanded', 'false');
  if (hadFocus) $('#saveBtn').focus();
}
$('#saveBtn').addEventListener('click', e => { if (saveOpen()) closeSave(); else openSave(e.detail === 0 ? 'keyboard' : 'button'); });
$('#savePng').addEventListener('click', () => snapshot('menu'));
const postcardRow = $('#savePostcard');             // a page kept from before the postcard has no such row
postcardRow?.addEventListener('click', postcard);
if (postcardRow && sharesPostcards({ touch: isTouch })) postcardRow.querySelector('.pop-title').textContent = 'Share a postcard';
$('#saveLoop').addEventListener('click', () => recordLoop('menu'));
$('#saveLoop').hidden = !CREATOR;
arrowKeys(saveMenu);

/* ---------- share this city (site/js/ui/share.js): the system's share sheet on touch screens, a small menu elsewhere ---------- */
// The menu's rows for the city on screen: its link under Copy link, and each network's share page with it filled in
function renderShare(city) {
  const s = shareOf(byId(city));
  $('#shareTitle').textContent = `Share ${byId(city).name}`;
  $('#shareUrl').textContent = shownLink(s.url);
  for (const a of shareMenu.querySelectorAll('a[data-share]')) a.href = SHARE_TARGETS.find(t => t.id === a.dataset.share).href(s);
}
const sharedProps = via => ({ city: target().id, via, music_playing: lastState === 'playing', listened_s: listenedS() });
async function toggleShare(trigger) {
  if (shareOpen()) { closeShare(); return; }
  if (!target()) return;
  if (isTouch && navigator.share) {                 // phones and tablets: their own sheet, with every app on it
    const props = sharedProps('sheet');
    track('share_opened', { city: props.city, trigger, mode: 'sheet' });
    try {
      await navigator.share(shareOf(byId(props.city)));
      track('city_shared', props);
      return;
    } catch (e) {
      if (e.name === 'AbortError') return;          // closed without sharing
      console.error(e);                             // the sheet didn't open: the menu does
    }
  }
  openShare(trigger);
}
function openShare(trigger) {
  closeSave();
  closeBoard();
  closeQueue();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeVibe();
  closeMixer();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  document.body.classList.remove('ui-off');
  shareMenu.hidden = false;
  $('#shareBtn').setAttribute('aria-expanded', 'true');
  if (trigger === 'keyboard') $('#shareCopy').focus();
  track('share_opened', { city: target().id, trigger, mode: 'menu' });
}
function closeShare() {
  if (!shareOpen()) return;
  const hadFocus = shareMenu.contains(document.activeElement);
  shareMenu.hidden = true;
  $('#shareBtn').setAttribute('aria-expanded', 'false');
  if (hadFocus) $('#shareBtn').focus();
}
$('#shareBtn').addEventListener('click', e => toggleShare(e.detail === 0 ? 'keyboard' : 'button'));
$('#shareCopy').addEventListener('click', async () => {
  const props = sharedProps('copy'), { url } = shareOf(byId(props.city));
  closeShare();
  try {
    await navigator.clipboard.writeText(url);
    toast('Link copied', 2000);
    track('city_shared', props);
  } catch (e) {                                     // no clipboard here (an http page, or the browser said no): the link to copy by hand
    console.error(e);
    toast(`Couldn't copy the link: ${shownLink(url)}`, 6000);
  }
});
shareMenu.addEventListener('click', e => {          // a network's row: its share page opens in a new tab, the music plays on
  const a = e.target.closest('a[data-share]');
  if (!a) return;
  track('city_shared', sharedProps(a.dataset.share));
  closeShare();
});
arrowKeys(shareMenu);

/* ---------- analytics: listening time, tracks, session summary (see site/js/analytics.js) ---------- */
const session = { listenMs: 0, playingSince: 0, tracks: 0, cities: new Set(), milestones: [5, 15, 30, 60, 120], focusRounds: 0 };
let lastState = 'locked', startTrigger = 'first_gesture', skipPending = false, trackStartedAt = 0, trackTitle = null;
const listenedS = () => Math.round((session.listenMs + (session.playingSince ? performance.now() - session.playingSince : 0)) / 1000);
function onStateAnalytics() {
  const s = audio.state, was = lastState;
  if (s === was) return;
  lastState = s;
  notePassport();                                   // the city passport counts only while it plays
  if (s === 'playing') {
    session.playingSince = performance.now();
    if (was === 'locked' || was === 'starting') track('music_started', { trigger: startTrigger });
    else if (was === 'paused' || was === 'interrupted') track('music_resumed', { from: was });
  } else if (session.playingSince) {
    session.listenMs += performance.now() - session.playingSince;
    session.playingSince = 0;
    if (s === 'paused') track('music_paused', { listened_s: listenedS() });
    else if (s === 'interrupted') track('music_interrupted', { listened_s: listenedS() });
  }
}
function onTrackAnalytics(info) {
  if (!info) return;
  const reason = session.tracks === 0 ? 'first' : skipPending === 'queue' ? 'picked' : skipPending === 'vibe' ? 'vibe'
    : skipPending === 'radio' ? 'radio' : skipPending ? 'skip' : 'natural';
  skipPending = false;
  session.tracks++;
  trackStartedAt = performance.now();
  trackTitle = info.title;
  track('track_started', { title: info.title, key: info.key, bpm: info.bpm, source: info.source, style: info.style, energy: info.energy, band: info.band, reason,
    radio: info.radio === true, mix: info.mix === true, ...(info.piece && { piece: info.piece.id }) });
}
function skipTrack(trigger) {
  if (lastState === 'playing' && trackTitle)
    track('track_skipped', { title: trackTitle, style: audio.track?.style, ...(audio.track?.piece && { piece: audio.track.piece.id }), trigger,
      listened_s: Math.round((performance.now() - trackStartedAt) / 1000) });
  skipPending = trigger;
}
setInterval(() => {                                   // listening milestones in minutes
  while (session.milestones.length && listenedS() >= session.milestones[0] * 60)
    track('listening_milestone', { minutes: session.milestones.shift() });
}, 15000);
let summarySent = false;
addEventListener('pagehide', () => {
  if (summarySent) return;
  summarySent = true;
  trackOnExit('session_summary', {
    listened_s: listenedS(), tracks: session.tracks, cities: [...session.cities], cities_count: session.cities.size, focus_rounds: session.focusRounds,
  });
});

/* ---------- city passport: listening per city in this browser, a stamp for 5 min in one (site/js/ui/passport.js) ---------- */
// Counted while music plays (the radio too) for the city on screen, so a tour counts for each city it shows; saved at
// most every 15 s, as the tab hides and on pagehide. A stamp shows a toast once (as the tab comes back, if it was hidden),
// marks the city in the picker and counts as city_stamped. Passport in the picker's head (a stamp's toast too) opens
// its view: what the stamps are, and every city's stamp or progress. A first minute in a city with no stamp yet shows
// a hint, once per browser ('passport-hint'): passport_hint_shown. Never in OBS or with ?creator: a stream or an export
// isn't a visit. To try it on localhost, ?passport=fast stamps a city after 10 s (the hint after 2 s).
const STAMP_AT = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname) && new URLSearchParams(location.search).get('passport') === 'fast' ? 10 : STAMP_S;
const passport = OBS || CREATOR ? null : createPassport({
  load: () => store.get('passport', null), save: p => store.set('passport', p), stampS: STAMP_AT, onStamp: cityStamped,
});
let stampedIds = new Set(), stampLater = null, welcomed = false, hinted = store.get('passport-hint', false) === true;
// Hidden things (site/js/ui/secrets.js): a tap on one of a scene's `secrets` while it shows (tapSpot, above) finds it,
// once: a toast with its sticker ("Found a red balloon · Paris 2 of 3", See passport), secret_found, and its sticker
// on the city's card in the Passport view beside "2 of 3 found" (renderHidden). Kept in this browser ('finds') and,
// signed in, in the account (pushFinds and SYNCED.secrets, below). Nothing hints at them until a city has its stamp's
// 5 minutes: then the stamp's toast says how many it hides, and its card shows a "?" for each one left (secretHint).
// Where the passport is, so never in OBS or with ?creator. A city's hidden things are known once its scenes' modules
// have loaded (learnHidden: the city on screen's as it shows, another one's when the Passport view needs them).
const finds = passport ? createFinds({
  load: () => store.get('finds', null), save: f => store.set('finds', f),
  sent: () => store.get('secrets', null), send: f => store.set('secrets', f),   // the copy the account syncs
}) : null;
const hidden = new Map(), hiddenAsked = new Map();  // city id -> its hidden things (its looks' together); -> when it was last asked for
function learnHidden(id) {
  if (hidden.has(id)) return Promise.resolve(hidden.get(id));
  hiddenAsked.set(id, performance.now());
  return Promise.all(looksOf(byId(id)).map(look => look.load().then(m => m.default, () => null))).then(scenes => {
    if (scenes.every(Boolean)) hidden.set(id, citySecrets(scenes));   // a look that didn't load is asked for again later
    return hidden.get(id) ?? citySecrets(scenes.filter(Boolean));
  });
}
/** How many hidden things city `id` has left to find, once it may say so (its 5 minutes); else null. */
const hiddenLeft = id => {
  const list = hidden.get(id) ?? [];
  return secretHint(passport.view.s[id] ?? 0, foundOf(finds.view, id, list).length, list.length, STAMP_AT);
};
/** A hidden thing's sticker, or the "?" of one not found yet: a pixel drawing on a canvas (the CSP allows no data: images). */
const stickerOf = secret => {
  const c = Object.assign(document.createElement('canvas'), { width: ART, height: ART, className: secret ? 'sticker' : 'sticker unfound' });
  paintSticker(c, secret ? secret.art : UNFOUND);
  return c;
};
function secretFound(t, secret) {
  if (!finds.add(t.id, secret.id)) return;          // found before: it still answers, and counts once
  const list = hidden.get(t.id) ?? citySecrets([t.scene]), n = foundOf(finds.view, t.id, list).length;
  track('secret_found', { city: t.id, look: t.look, id: secret.id, found: n, of: list.length, music_playing: lastState === 'playing' });
  toast(`Found ${secret.name} · ${byId(t.id).name} ${n} of ${list.length}`, 6000, null, { label: 'See passport', run: () => openPassport('find_toast') });
  $('#toastText').prepend(stickerOf(secret));       // the next toast's text takes it away
  pushFinds();
}
// Signed in, the finds reach the account in two steps, so that two devices' finds add up (an account keeps a setting's
// newest change, whole). First a sync: this device's copy for the account ('secrets') still has its old time and
// replaces nothing there, while finds the account has from elsewhere come back and are merged in (SYNCED.secrets).
// Only then is the copy stored with both, which the account hears like any setting. Signing in forgets the copy, so
// the first sync with an account only asks.
function pushFinds() {
  if (!account?.signedIn || !finds.due) return;
  account.sync().then(ok => { if (ok && finds.due) finds.keep(); });
}
function findsSignedIn() {
  try { localStorage.removeItem('lofi-cities:secrets'); } catch { /* storage unavailable */ }
  pushFinds();
}
const refreshPassport = () => { if (pickerOpen() && pickerView === 'passport') renderPassport(); };
addEventListener('storage', e => {                  // another tab found one
  if (e.key !== 'lofi-cities:finds' || !finds) return;
  finds.reload();
  refreshPassport();
});
const stampIcon = () => Object.assign(document.createElement('i'), { innerHTML: '<svg class="stamp-icon" aria-hidden="true"><use href="#i-stamp"/></svg>' }).firstChild;
const cardTitles = cards.map(b => b.title);         // a stamped card's adds its time there
const minutesText = s => (s >= 60 ? `${s / 60} minutes` : `${s} seconds`);
function notePassport() {
  passport?.at(target()?.id, lastState === 'playing');
  if (pickerOpen() && pickerView === 'passport') renderPassport();   // the city on screen's bar fills as it plays
  hintPassport();
}
setInterval(notePassport, 5000);
addEventListener('pagehide', () => passport?.flush());
document.addEventListener('visibilitychange', () => {
  if (document.hidden) passport?.flush();          // phones may never send pagehide
  else if (stampLater) stampToast(stampLater);
});
function cityStamped(id) {
  renderStamps();
  track('city_stamped', { city: id, stamps: stampedIds.size });
  stampToast(id);
}
function stampToast(id) {
  stampLater = document.hidden ? id : null;
  if (stampLater) return;
  const see = { label: 'See passport', run: () => openPassport('stamp_toast') };
  const save = account && !account.signedIn && stampedIds.size === 1 && { label: 'Save it', run: () => account.signIn('stamp_toast') };   // the first stamp
  const left = finds ? hiddenLeft(id) : null;       // from its 5 minutes on a city may say it hides things: how many, never where
  toast(`${byId(id).name} stamped · ${stampedIds.size} of ${SCENES.length}${left ? ` · ${left} hidden ${left === 1 ? 'thing' : 'things'} to find` : ''}`,
    save ? 9000 : 6000, null, save ? [see, save] : see);
  $('#toastText').prepend(stampIcon());             // the next toast's text takes it away
}
// The hint: a first minute of music in a city, before any stamp, says what the stamps are and how far this one is.
function hintPassport() {
  const t = target();
  if (!passport || hinted || !t || lastState !== 'playing' || document.hidden) return;
  const left = hintOf(passport.view, t.id, cityIds, STAMP_AT);
  if (left == null) return;
  hinted = true;
  store.set('passport-hint', true);
  toast(`${Math.ceil(left / 60)} more min to stamp ${byId(t.id).name} in your passport`, 8000, null, { label: 'See passport', run: () => openPassport('hint_toast') });
  $('#toastText').prepend(stampIcon());
  track('passport_hint_shown', { city: t.id, city_s: passport.view.s[t.id] ?? 0 });
}
function openPassport(trigger) {
  if (!pickerOpen()) { openPicker(trigger, 'passport'); return; }
  if (pickerView === 'passport') return;
  setPickerView('passport');
  track('city_picker_view_changed', { view: pickerView });
}
/** The stamps on the picker's cards and the globe (a postmark on the preview, a separate element), and Passport's count. */
function renderStamps() {
  if (!passport) return;                            // OBS, ?creator: "25 cities · local times", no Passport
  const p = passport.view;
  stampedIds = new Set(stampsOf(p, cityIds, STAMP_AT));
  for (const b of [...cards, featuredCard]) {
    const on = stampedIds.has(b.dataset.id), i = cityIds.indexOf(b.dataset.id);
    let mark = b.querySelector('.card-stamp');
    if (!mark && on) {
      mark = Object.assign(document.createElement('i'), { className: 'card-stamp', innerHTML: '<b class="postmark" role="img" aria-label="stamped"><svg aria-hidden="true"><use href="#i-check"/></svg><span>stamped</span></b>' });
      b.append(mark);
    }
    if (mark) mark.hidden = !on;
    b.classList.toggle('stamped', on);
    if (b !== featuredCard) b.title = on ? `${cardTitles[i]} · stamped, ${duration(p.s[b.dataset.id])} listened` : cardTitles[i];
  }
  const n = stampedIds.size;
  $('#cityCount').hidden = true;
  passportBtn.hidden = false;
  $('#passportCount').textContent = `${n}/${SCENES.length}`;
  passportBtn.setAttribute('aria-label', `City passport: ${n} of ${SCENES.length} cities stamped`);
  if (pickerOpen() && pickerView === 'passport') renderPassport();
}
/**
 * The Passport view: its intro (the rule, the count, the time in all) and each card's progress, a bar across its
 * preview and "2 of 5 min" (a stamped one: its time there). Returns passportRows, the order its cards take.
 */
function renderPassport() {
  const p = passport?.view ?? { s: {} }, rows = passportRows(p, cityIds, STAMP_AT);
  const n = rows.filter(r => r.stamped).length, total = rows.reduce((t, r) => t + r.s, 0), stuck = finds ? renderHidden() : { stickers: 0, any: false };
  passportItem.querySelector('p').textContent = `Listen for ${minutesText(STAMP_AT)} in a city to get its stamp.`
    + (stuck.any ? ' Some cities hide things: tap one in its scene to keep its sticker.' : '');
  passportItem.querySelector('small').textContent = [
    `${n} of ${SCENES.length} stamped`, ...(stuck.stickers ? [`${stuck.stickers} sticker${stuck.stickers === 1 ? '' : 's'}`] : []),
    ...(total >= 60 ? [`${duration(total)} listened`] : []), account?.signedIn ? 'kept in your account' : 'kept in this browser',
  ].join(' · ');
  for (const { id, s, stamped } of rows) {
    const b = cards[cityIds.indexOf(id)];
    let line = b.querySelector('.card-pass');
    if (!line) {
      line = Object.assign(document.createElement('em'), { className: 'card-pass' });
      b.append(line, Object.assign(document.createElement('u'), { className: 'card-bar' }));
    }
    line.textContent = progressLine(s, STAMP_AT);
    b.querySelector('.card-bar').style.setProperty('--p', stamped ? '1' : (s / STAMP_AT).toFixed(3));
  }
  return rows;
}
/**
 * The Passport view's hidden things: under a city's name the stickers found there and "2 of 3 found", with a "?" for
 * each one left once the city may say so (hiddenLeft), and under them where the rest are ("tap them in the scene",
 * whereLine: a press on a "?" only picks the city); a city with neither has no row. Only a city with a find or its
 * 5 minutes can have one, so only those cities' scenes are asked for. Returns { stickers: found in all, any: a row shows }.
 */
function renderHidden() {
  const found = finds.view, p = passport.view, now = performance.now();
  let stickers = 0, any = false;
  for (const [i, id] of cityIds.entries()) {
    const b = cards[i], list = hidden.get(id);
    let row = b.querySelector('.card-finds');
    if (!list && (found[id] || (p.s[id] ?? 0) >= STAMP_AT) && !(now - hiddenAsked.get(id) < 30000)) learnHidden(id).then(refreshPassport);
    const got = list ? foundOf(found, id, list) : [], left = list ? hiddenLeft(id) : null;
    const tiles = (list ?? []).filter(s => left != null || got.includes(s)).map(s => (got.includes(s) ? s : null));
    if (!tiles.length) { if (row) row.hidden = true; continue; }
    stickers += got.length;
    any = true;
    if (!row) {
      row = Object.assign(document.createElement('u'), { className: 'card-finds' });
      row.setAttribute('role', 'img');
      b.append(row);
    }
    row.hidden = false;
    const key = tiles.map(s => s?.id ?? '?').join();
    if (row.dataset.key === key) continue;          // as it was: its canvases stay (the view renders again as the music plays)
    row.dataset.key = key;
    const where = whereLine(list.length - got.length), names = got.map(s => s.name).join(', ');
    row.replaceChildren(...tiles.map(stickerOf), Object.assign(document.createElement('em'), { textContent: foundLine(got.length, list.length) }),
      ...(where ? [Object.assign(document.createElement('i'), { textContent: where })] : []));
    row.setAttribute('aria-label', `Hidden things: ${foundLine(got.length, list.length)}${got.length ? ` (${names})` : ''}${where ? `, ${where}` : ''}`);
    row.title = [...(got.length ? [`Found: ${names}`] : []), ...(where ? ['Hidden in the scene: tap one when you spot it'] : [])].join(' · ');
  }
  return { stickers, any };
}
renderStamps();

// Welcome back: before the music starts, a returning visitor (a minute or more listened, the last of it half an hour
// ago or more) sees their time in the city on screen and their stamps under the play prompt, the longest line that fits
// (site/js/ui/passport.js welcomeBack), until the first track replaces it. Once a visit it counts as welcome_back_shown;
// a city picked before playing changes the line to that city's.
function passportCityShown() {
  notePassport();
  renderWelcome();
  if (finds && target()) learnHidden(target().id);  // what this city hides, for a find's count and its stamp's toast
}
function renderWelcome() {
  const t = target();
  if (!passport || !t || session.tracks || audio.state !== 'locked') return;
  const w = welcomeBack(passport.view, t.id, SCENES, Date.now(), STAMP_AT), meta = $('#trackMeta');
  if (!w) return;
  meta.textContent = w.lines.find(line => { meta.textContent = line; return meta.scrollWidth <= meta.clientWidth; }) ?? w.lines.at(-1);
  if (welcomed) return;
  welcomed = true;
  track('welcome_back_shown', { city: t.id, city_s: w.city_s, total_s: w.total_s, stamps: w.stamps });
}
addEventListener('resize', () => { if (welcomed) renderWelcome(); });

/* ---------- account: sign in with Google to keep the passport, settings and chat name on every device (site/js/live/account.js) ---------- */
// Where the page has a live worker (not OBS, deploy previews or local pages without ?live) and a passport (not
// ?creator). Sign in from the header's person icon (it opens Settings at Account), Settings' Account row, the
// Passport view's line and the first stamp's toast (Save it): a popup to Google, or this tab if popups are blocked.
// The settings below follow the account, the newest change of each winning, applied as they come (an account's
// change isn't the visitor's: no analytics event, no toast). The radio waits while music plays, so a sync never
// switches what's playing. The city (its looks follow), the reaction picked and the chat's hidden people stay this
// device's. The chat name is the account's live id (the first device's); signing out gives this browser a new one. Signed in, Settings'
// Account row also picks a chosen chat name instead (Chat name, below), which signing in offers when there's none.
const syncVolume = (input, set, key) => v => {
  if (!Number.isFinite(v)) return true;
  input.value = v;
  if (!muted) set(+input.value);
  store.set(key, +input.value);
  return true;
};
const SYNCED = {
  vibe: v => {
    if (!v || typeof v !== 'object') return true;
    for (const k of ['style', 'energy', 'band']) if (typeof v[k] === 'string') vibeInputs[k].value = v[k];   // an unknown value: no change
    const now = vibeNow();
    audio.setVibe(now);
    store.set('vibe', now);
    renderStyleDesc();
    renderVibeNote();
    return true;
  },
  weather: v => { setWeather(v, 'sync'); return true; },
  musicVol: syncVolume(musicVol, x => audio.setMusicVolume(x), 'musicVol'),
  ambVol: syncVolume(ambVol, x => audio.setAmbienceVolume(x), 'ambVol'),
  trims: v => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return true;
    const next = Object.fromEntries(Object.entries(v).filter(([, x]) => Number.isFinite(x) && x >= 0 && x <= 2 && x !== 1));
    for (const id of new Set([...Object.keys(trims), ...Object.keys(next)])) {
      const x = next[id] ?? 1;
      if ((trims[id] ?? 1) === x) continue;
      if (x === 1) delete trims[id]; else trims[id] = x;
      audio.setSoundTrim(id, x);
    }
    store.set('trims', trims);
    renderMixer();
    return true;
  },
  radio: v => {
    if (typeof v !== 'boolean' || v === radioOn) return true;
    if (lastState === 'playing') return false;       // the next sync tries again
    setRadio(v, 'sync');
    return true;
  },
  tour: v => { if (TOUR_MODES.includes(v)) { lastTour = v; store.set('tour', v); } return true; },
  'tour-favourites': v => { if (typeof v === 'boolean') setTourFavs(v, 'sync'); return true; },
  favourites: v => { favs.set(v); renderFavs(); return true; },   // the newest list wins, like the looks
  billboards: v => { setBillboards(v, 'sync'); return true; },
  people: v => { setPeople(v, 'sync'); return true; },
  reactions: v => { setReactions(v !== 'off', 'sync'); return true; },
  'job-strip': v => { setJobStrip(v !== 'off', 'sync'); return true; },
  'mention-sound': v => { setMentionSound(v !== 'off', 'sync'); return true; },
  'picker-view': v => {
    if (!PICKER_VIEWS.includes(v)) return true;
    store.set('picker-view', v);
    if (pickerView !== 'passport') setPickerView(v);   // an open Passport view stays; pressed again, it goes back to this one
    return true;
  },
  board: v => {                                      // shown on the leaderboards or not (renderBoard)
    store.set('board', v === 'off' ? 'off' : 'on');
    if (boardOpen()) renderBoard();
    return true;
  },
  'style-view': v => { setStyleView(v, 'sync'); return true; },
  maps: v => { setMaps(v, 'sync'); return true; },
  tasks: v => { setTasks(cleanTasks(v), 'sync'); return true; },
  notes: v => {
    if (document.activeElement === notesText) return false;   // being written in: the next sync tries again (what's typed here is newer by then)
    setNotes(cleanNotes(v), 'sync');
    return true;
  },
  looks: v => {                                      // the city on screen changes its look too, like a synced weather
    const next = looksPicked(v, SCENES), t = target();
    saveLooks(next);
    if (t) setLook(lookOf(byId(t.id), next).id, 'sync');
    return true;
  },
  // The hidden things found (site/js/ui/secrets.js): the one setting two devices both add to, so what arrives is kept
  // with this device's finds, never in their place. Where this device has some the account lacks, its copy is stored
  // with both once this sync is done (a setting stored is a setting sent), and the account ends up with both.
  secrets: v => {
    if (finds.take(v, Date.now())) setTimeout(() => { if (finds.due) finds.keep(); });
    refreshPassport();
    return true;
  },
};
const accountAt = LIVE_FEATURES && passport ? accountUrl(location, OBS) : null;
const account = accountAt ? createAccount({
  base: accountAt, store,
  settings: { keys: Object.keys(SYNCED), apply: (key, v) => SYNCED[key](v) },
  passport: {
    flush: () => passport.flush(), load: () => passportOf(store.get('passport', null)), save: p => store.set('passport', p),
    reload: () => { passport.reload(); renderStamps(); },
  },
  liveId: () => store.get('live-id', null),
  onLiveId: id => { store.set('live-id', id); store.set('live-new', false); live?.setId(id); },   // counted already: the same person
  onChange: (state, how) => {
    if (state && state.name !== undefined) accountNick = state.name;
    if (!state) accountNick = null;
    renderAccount();
    setContext({ signed_in: !!state });
    const pick = state?.name === null && { label: 'Pick a chat name', run: () => openNick() };   // none yet: offered once, here
    if (how === 'signed_in') toast(`Signed in${state.email ? ` as ${state.email}` : ''} · your passport and settings follow you now`, pick ? 8000 : 4000, null, pick);
    else if (how === 'expired') toast('Signed out of your account · sign in again to keep syncing', 4000);
    if (how === 'signed_in') findsSignedIn();       // the hidden things found here join the account's
    if (boardOpen()) loadBoard();                   // the visitor's own rows, or the way on
  },
  onEvent: track,
}) : null;
stored = k => account?.changed(k);
let accountNick;                                    // this page's chosen chat name, as last said (undefined: not yet)
function renderAccount() {
  const on = !!account?.signedIn, email = account?.email, btn = $('#accountBtn');
  btn.hidden = $('#accountSetting').hidden = !account;
  $('#accountSignIn').hidden = on;
  $('#accountSignOut').hidden = $('#accountDelete').hidden = !on;
  if (!on) $('#accountConfirm').hidden = true;
  $('#accountNote').textContent = on ? `${email ?? 'Signed in with Google'} · your passport, settings and chat name on every device`
    : 'Keep your passport, settings and chat name on every device.';
  $('#settingsWhere').textContent = on ? 'kept in your account' : 'kept in this browser';
  $('#notesKept').textContent = `Tasks and notes are kept in ${on ? 'your account' : 'this browser'}`;
  btn.classList.toggle('signed-in', on);
  btn.querySelector('span').textContent = on ? 'Account' : 'Sign in';
  btn.title = on ? `Account: signed in${email ? ` as ${email}` : ''}` : 'Sign in: keep your passport and settings on every device';
  btn.setAttribute('aria-label', `${btn.title} (opens Settings)`);
  passportItem.querySelector('.passport-save').hidden = !account || on;
  if (pickerOpen() && pickerView === 'passport') renderPassport();
  renderNick();
}

// Chat name, in Settings' Account row while signed in: the account's own name in the chat instead of the random one,
// which the worker checks (taken, kind, once a day) and every chat page hears at once. What it shows is the latest
// word on it (accountNick): signing in, a save, or the chat (its `nick`, a change, a hush). Saved with Save or Enter;
// "Use my random name" goes back.
const nickInput = $('#nickInput'), nickNoteEl = $('#nickNote');
let nickSaving = false, nickSaid = null;            // nickSaid: the last answer's line, until the field changes
const NICK_SAYS = {
  short: `At least ${NICK_MIN} characters`, long: `${NICK_MAX} characters at most`, chars: 'Letters, digits and single spaces only',
  link: 'No links, emails or phone numbers', reserved: 'That name is reserved · try another',
  words: 'Let\'s keep it kind · try another', taken: 'Someone already has that name · try another',
  hushed: 'Not while your messages are hidden', flood: 'Slow down a little · try again in a moment',
  network: 'Not connected right now · try again', unavailable: 'That didn\'t save just now · try again', nochat: 'That didn\'t save just now · try again',
};
const myChatNick = () => accountNick ?? null;
function renderNick(keep = document.activeElement === nickInput) {
  if (!nickInput) return;
  const on = !!account?.signedIn, nick = myChatNick(), random = chatMe ? nameOf(chatMe) : null;
  $('#nickSetting').hidden = !on;
  if (!on) return;
  if (!keep) nickInput.value = nick ?? '';
  nickInput.placeholder = random ?? 'your name in the chat';
  $('#nickClear').hidden = !nick;
  $('#nickSave').disabled = nickSaving;
  nickNoteEl.classList.toggle('warn', !!nickSaid?.warn);
  nickNoteEl.textContent = nickSaid?.text ?? (nick ? `Others see “${nick} ✓” in the chat · you can change it once a day`
    : `Your own name in the chat instead of ${random ? `“${random}”` : 'a random one'} · ${NICK_MIN}–${NICK_MAX} letters or digits, one new name a day`);
}
function openNick() {
  openSettings('nick');
  renderNick(false);
  nickInput.focus();
}
async function saveNick(name) {
  if (nickSaving || !account?.signedIn) return;
  if (name !== null) {
    const why = nickRefusal(name);                  // a reserved name is the worker's to refuse: the site's owners may take one
    if (why && why !== 'reserved') { nickSaid = { text: NICK_SAYS[why], warn: true }; renderNick(); return; }
    if (name === myChatNick()) { nickSaid = null; renderNick(false); return; }
  }
  nickSaving = true;
  renderNick();
  const got = await account.setName(name);
  nickSaving = false;
  if (got.ok) {
    accountNick = got.name;
    if (chatMe) { if (got.name) chatNicks.set(chatMe, got.name); else chatNicks.delete(chatMe); }   // the worker's 'nick' says it too
    nickSaid = { text: got.name ? `Saved · others see “${got.name} ✓” in the chat` : `Back to your random name${chatMe ? `, ${nameOf(chatMe)}` : ''}` };
    renderChat();
    renderNick(false);
  } else {
    const at = Number.isFinite(got.until) && new Date(got.until).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    nickSaid = { text: got.why === 'soon' ? `One change a day · you can change it again ${at ? `on ${at}` : 'tomorrow'}` : NICK_SAYS[got.why] ?? NICK_SAYS.unavailable, warn: true };
    renderNick();
  }
}
$('#nickSave')?.addEventListener('click', () => saveNick(cleanSay(nickInput.value)));
$('#nickClear')?.addEventListener('click', () => saveNick(null));
nickInput?.addEventListener('keydown', e => {      // the global keys skip text boxes: Enter saves, Escape closes Settings
  if (e.key === 'Enter') { e.preventDefault(); saveNick(cleanSay(nickInput.value)); }
  else if (e.key === 'Escape') { e.preventDefault(); closeSettings(); }
});
nickInput?.addEventListener('input', () => { if (nickSaid) { nickSaid = null; renderNick(); } });
$('#accountBtn').addEventListener('click', () => {
  if (settingsOpen()) { closeSettings(); return; }
  openSettings('account');
  $(account?.signedIn ? '#accountSignOut' : '#accountSignIn').focus();
});
$('#accountSignIn').addEventListener('click', () => account?.signIn('settings'));
passportItem.querySelector('.passport-save').addEventListener('click', () => account?.signIn('passport'));
$('#accountSignOut').addEventListener('click', async () => {
  await account?.signOut();
  toast('Signed out · this device keeps its copy', 3000);
});
$('#accountDelete').addEventListener('click', () => {    // asks in the row first (never the browser's confirm)
  $('#accountConfirm').hidden = false;
  $('#accountConfirmNo').focus();
});
$('#accountConfirmNo').addEventListener('click', () => {
  $('#accountConfirm').hidden = true;
  $('#accountDelete').focus();
});
$('#accountConfirmYes').addEventListener('click', async e => {
  const yes = e.currentTarget;
  yes.disabled = true;
  const ok = await account?.remove();
  yes.disabled = false;
  if (ok) { $('#accountConfirm').hidden = true; toast('Account deleted · this device keeps its copy', 3500); }
  else toast('The account could not be deleted just now. Try again in a moment', 3500);
});
renderAccount();
setContext({ signed_in: !!account?.signedIn });
account?.start();
pushFinds();                                        // finds the account was never sent (found offline, or the page closed first)

/* ---------- leaderboards: the trophy in the player opens them (site/js/live/board.js, the worker's worker/board.js) ---------- */
// Signed-in listeners ranked five ways under their chat names, where accounts work (accountAt), in a panel of their own
// beside Together and Chat (board_opened): This week | All time in its head (Current | Best for streaks:
// board_tab_changed), a chip for each board under it (Listened, Stamps, Streak, the city on screen's fans, Chatty:
// board_type_changed), both kept for the visit. The panel asks the worker as it opens, again every BOARD_EVERY_MS while
// it shows and as the city changes under it (its fans). Everything counts from
// signing in: the worker credits an account's synced seconds against the clock. Signed out, the foot offers Sign in
// (location board); signed in, it says where the visitor is or how to get on, and Hide me / Show me flips the synced
// `board` setting (board_visibility_changed), on every device and every board.
boardBtn.hidden = !accountAt;
const boardOpen = () => !boardEl.hidden;
const boardTabs = [...boardEl.querySelectorAll('.board-tabs button')], boardTypesRow = $('#boardTypes');
let boardData = null, boardFailed = false, boardTypeId = BOARD_TYPES[0].id, boardTabAt = 0, boardTimer = 0, boardAsks = 0;
const boardChips = BOARD_TYPES.map(t => {
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'region-chip'; b.dataset.type = t.id; b.textContent = t.label;
  b.addEventListener('click', () => {
    if (boardTypeId === t.id) return;
    boardTypeId = t.id;
    renderBoard();
    $('#boardList').scrollTop = 0;
    track('board_type_changed', { type: t.id });
  });
  b.addEventListener('keydown', e => {              // ← → along the chips, not to another city
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    e.stopPropagation();
    const all = [...boardTypesRow.children];
    all[(all.indexOf(b) + (e.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length].focus();
  });
  boardTypesRow.append(b);
  return b;
});
function openBoard(trigger) {
  closeQueue();
  closeTimers();
  closeTour();
  closeWeather();
  closePicker();
  closeMixer();
  closeVibe();
  closeSave();
  closeShare();
  closeSettings();
  closeNews();
  closeTogether();
  closeChat();
  document.body.classList.remove('ui-off');
  boardEl.hidden = false;
  boardBtn.setAttribute('aria-expanded', 'true');
  renderBoard();
  loadBoard();
  if (trigger === 'keyboard') boardChips.find(b => b.dataset.type === boardTypeId)?.focus();
  track('board_opened', { trigger });
}
function closeBoard() {
  if (!boardOpen()) return;                         // first, so a popup opening before this section ran finds nothing to do
  stopBoard();
  const hadFocus = boardEl.contains(document.activeElement);
  boardEl.hidden = true;
  boardBtn.setAttribute('aria-expanded', 'false');
  if (hadFocus) boardBtn.focus();
}
boardBtn.addEventListener('click', e => { if (boardOpen()) closeBoard(); else openBoard(e.detail === 0 ? 'keyboard' : 'button'); });
async function loadBoard() {
  clearTimeout(boardTimer);
  const ask = ++boardAsks, data = await fetchBoard(accountAt, target()?.id);
  if (ask !== boardAsks) return;                    // closed meanwhile, or asked again
  boardFailed = !data;
  if (data) boardData = data;
  renderBoard();
  boardTimer = setTimeout(loadBoard, BOARD_EVERY_MS);
}
function stopBoard() {
  clearTimeout(boardTimer);
  boardAsks++;                                      // an answer on its way is dropped
}
function boardCityShown() {                         // another city's fans
  if (boardOpen()) loadBoard();
}
const boardHidden = () => !!account?.signedIn && store.get('board', 'on') === 'off';
function renderBoard() {
  const type = boardType(boardTypeId), [tab] = type.tabs[boardTabAt], city = target() ? byId(target().id).name : 'this city';
  for (const b of boardChips) {
    b.setAttribute('aria-pressed', String(b.dataset.type === type.id));
    if (b.dataset.type === 'fans') b.textContent = `${city} fans`;
  }
  type.tabs.forEach(([key, label], i) => {
    boardTabs[i].dataset.tab = key;
    boardTabs[i].textContent = label;
    boardTabs[i].setAttribute('aria-pressed', String(i === boardTabAt));
  });
  const stale = type.id === 'fans' && boardData?.city !== target()?.id;   // the last answer was another city's
  const board = stale ? null : boardData?.boards[type.id]?.[tab], hidden = boardHidden(), me = hidden ? null : boardData?.me ?? null;
  const place = stale ? null : me?.[type.id]?.[tab] ?? null;
  const rows = boardRows(board, me, place);
  $('#boardList').replaceChildren(...rows.map(r => boardRow(r, boardCells(type.id, tab, r))));
  const empty = $('#boardEmpty');
  empty.hidden = rows.length > 0;
  empty.textContent = !boardData || stale ? (boardFailed ? 'The leaderboard can\'t be reached right now. It tries again in a minute.' : 'Loading the leaderboard…')
    : `Nobody on this board${tab === 'week' ? ' this week' : ''} yet.`;
  $('#boardInfo').textContent = stale ? '' : boardInfo(type.id, tab, boardData, Date.now());
  const note = boardNote({ signedIn: !!account?.signedIn, hidden, place, type: type.id, tab, people: board?.people ?? 0, city });
  $('#boardNote').textContent = note.text;
  $('#boardSignIn').hidden = note.action !== 'sign-in';
  $('#boardHide').hidden = note.action !== 'hide' && note.action !== 'show';
  $('#boardHide').textContent = note.action === 'show' ? 'Show me' : 'Hide me';
}
/** A row: its place (the top three in gold, silver and bronze), the name in its chat colour (a chosen one with the chat's ✓), its board's extra and value. */
function boardRow({ place, name, chosen, hue, me, gap }, { value, extra }) {
  const li = document.createElement('li');
  li.className = 'board-row';
  li.classList.toggle('me', me);
  li.classList.toggle('gap', !!gap);
  li.innerHTML = '<b class="board-place"></b><span class="board-name"></span><small class="board-extra"></small><em class="board-value"></em>';
  const placeEl = li.querySelector('.board-place'), nameEl = li.querySelector('.board-name');
  placeEl.textContent = place;
  placeEl.dataset.place = place;
  nameEl.textContent = name;
  nameEl.style.setProperty('--h', hue);
  if (chosen) nameEl.append(chosenMark());
  if (me) nameEl.append(Object.assign(document.createElement('small'), { textContent: 'you' }));
  for (const [el, cell] of [[li.querySelector('.board-extra'), extra], [li.querySelector('.board-value'), value]]) {
    if (!cell) continue;
    el.classList.toggle('stamp', !!cell.stamp);
    if (cell.stamp) el.append(stampIcon());
    el.append(cell.text);
  }
  return li;
}
boardTabs.forEach((b, i) => b.addEventListener('click', () => {
  if (i === boardTabAt) return;
  boardTabAt = i;
  renderBoard();
  $('#boardList').scrollTop = 0;
  track('board_tab_changed', { type: boardTypeId, tab: b.dataset.tab });
}));
$('#boardSignIn').addEventListener('click', () => account?.signIn('board'));
$('#boardHide').addEventListener('click', async () => {
  const visible = boardHidden();                    // hidden now: Show me
  store.set('board', visible ? 'on' : 'off');        // the account hears it (SYNCED.board on the other devices)
  track('board_visibility_changed', { visible });
  renderBoard();
  await account?.sync();
  if (boardOpen()) loadBoard();
});

/* ---------- keyboard ---------- */
addEventListener('keydown', e => {
  if (e.metaKey || e.ctrlKey || e.altKey || (e.target instanceof Element && e.target.matches('textarea, input:not([type="range"]):not([type="radio"]):not([type="checkbox"])'))) return;
  if (e.target instanceof Element && e.target.matches('input[type="range"]') && /^(Arrow|Page|Home$|End$)/.test(e.key)) return;   // a focused slider keeps its own keys
  // a focused switch or option (the Vibe and Settings panels) keeps its arrows, Space and Enter; the letters still work
  if (e.target instanceof Element && e.target.matches('input[type="radio"], input[type="checkbox"]') && /^(Arrow|Enter$| $)/.test(e.key)) return;
  const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  if (OBS && ['?', 'i', 'e', 'q', 't', 'v', 'h', 'r', 'g', 'l', 'u', 'j', '/', 'Enter'].includes(k)) return;   // embed mode: no panels, interface or recording on screen
  if (pitchCard.open) return;                           // the billboard card keeps Tab and Escape for itself
  if (k === 'e' && !about.open) { if (!e.repeat) toggleGuide('keyboard'); return; }
  if (guide.open) return;                               // the guide keeps Tab, Escape and scrolling for itself
  if (k === '?' || k === 'i') { if (!e.repeat) toggleAbout('keyboard', { shortcuts: k === '?' }); return; }
  if (about.open) return;                               // the panel keeps Tab, Escape and scrolling for itself
  const digit = jamOn ? jamDigit(e) : null;
  if (digit !== null) {                                 // Jam: 1–8 are its notes (a held key plays once), 9 and 0 rest
    if (digit >= 1 && digit <= 8 && !e.repeat) jamKey(digit - 1);
    return;
  }
  if (k === 'ArrowRight') stepCity(1, 'keyboard');
  else if (k === 'ArrowLeft') stepCity(-1, 'keyboard');
  else if ((k === 'ArrowDown' || k === 'ArrowUp') && !e.repeat && target() && !anythingOpen()) {   // a city's other look; an open menu keeps its arrows
    setLook(stepLook(byId(target().id), target().look, k === 'ArrowDown' ? 1 : -1).id, 'keyboard');
  }
  else if (k === ' ') { e.preventDefault(); $('#play').click(); }
  else if (k === 'n' && radioOn) toast('The radio plays the same song for everyone: no skipping', 1600);
  else if (k === 'n') { startMusic('keyboard'); skipTrack('keyboard'); audio.next(); }
  else if (k === '/' && !e.repeat) { e.preventDefault(); togglePicker('keyboard'); }   // the / doesn't land in the search it focuses
  else if (k === 'q') toggleQueue('keyboard');
  else if (k === 'a') toggleMixer('keyboard');
  else if (k === 'v') toggleVibe('keyboard');
  else if (k === 'o' && !e.repeat) setRadio(!radioOn, 'keyboard');
  else if (k === 'j' && !e.repeat) setJam(!jamOn, 'keyboard');
  else if (k === 't') toggleTimers('keyboard');
  else if (k === 'g' && !e.repeat) toggleTogether('keyboard');
  else if (k === 'l' && !e.repeat) releaseLantern('keyboard');
  else if (k === 'Enter' && !e.repeat && !(e.target instanceof Element && e.target.closest('button, a, summary, [tabindex]'))) toggleChat('keyboard');   // a focused button keeps its own Enter
  else if (k === 'c' && !e.repeat) setTour(tour === 'off' ? lastTour : 'off', 'keyboard');
  else if (k === 'k' && !e.repeat) heartCity('keyboard');
  else if (k === 'w' && !e.repeat) setWeather(WEATHERS[(WEATHERS.indexOf(weather) + 1) % WEATHERS.length], 'keyboard');
  else if (k === 'b' && !e.repeat && (OBS || billboardsPick())) setBillboards(nextBillboards(), 'keyboard');
  else if (k === 'm') toggleMute();
  else if (k === 'f') toggleFS();
  else if (k === 'p' && !e.repeat) togglePip('keyboard');
  else if (k === 'h') {
    track('ui_toggled', { hidden: document.body.classList.toggle('ui-off') });
    measureChat('ui_toggle');
    renderLive();
  }
  else if (k === 'r' && !e.repeat && CREATOR) recordLoop('keyboard');
  else if (k === 's' && !e.repeat) snapshot('keyboard');
  else if (k === 'u' && !e.repeat) toggleShare('keyboard');
  else if (k === 'Escape') { cancelRec = true; closeQueue(); closeTimers(); closeTour(); closeWeather(); closePicker(); closeVibe(); closeMixer(); closeSave(); closeShare(); closeSettings(); closeTogether(); closeChat(); closeBoard(); closeNews(); }
  else if (/^[0-9]$/.test(k) && SCENES[(+k + 9) % 10]) showCity(SCENES[(+k + 9) % 10].id, { source: 'keyboard' });   // 1–9, then 0 for the tenth
});

/* ---------- drag the scene to look around; past its end, a swipe changes city (like ← / →) ---------- */
// A portrait view follows the finger (display.pan) until it reaches the end of the scene. What it can't follow is the
// pull: let go 48 px or more past the end and the next (or previous) city comes in. Landscape shows the whole scene,
// so there every sideways swipe is a pull. A mouse can drag a narrow window's view too, but only touch changes city.
// A press on the scene that never became a drag, let go within TAP_SLOP px and TAP_MS ms, is a tap: a spot of the
// scene answers it (tapSpot), and anywhere else it drops the picked reaction there (dropReaction), unless it began with
// a popup open, which it only closed. The billboard's spot keeps its own click, and a double-click (fullscreen) is two
// taps.
const tapScene = (cx, cy, touch, pressed) => tapSpot(cx, cy, touch, pressed) || dropReaction(cx, cy);   // the first to take it has it
// a mouse over a spot shows the hand (css #screen.on-spot), the only hint a spot gives
const overSpot = e => e.pointerType === 'mouse' && e.target === $('#screen') && !OBS && !popupOpen() && !!spotUnder(e.clientX, e.clientY, false, e.timeStamp, true);
let drag = null, dragClick = false;
addEventListener('pointerdown', e => {
  if (drag) { drag = null; return; }                // a second finger: a pinch, not a drag (nor a tap)
  if (!e.isPrimary || e.button !== 0 || (e.target !== $('#screen') && !SPOTS.includes(e.target) && e.target !== newsSpot && e.target !== planeSpot)) return;
  drag = { id: e.pointerId, el: e.target, x0: e.clientX, y0: e.clientY, x: e.clientX, moving: false, pull: 0, touch: e.pointerType !== 'mouse',
    t0: e.timeStamp, closing: popupAtPress };
});
addEventListener('pointermove', e => {
  $('#screen').classList.toggle('on-spot', overSpot(e));
  if (!drag || e.pointerId !== drag.id) return;
  const dx = e.clientX - drag.x0, dy = e.clientY - drag.y0;
  if (!drag.moving) {
    if (Math.abs(dy) >= TAP_SLOP && Math.abs(dy) >= Math.abs(dx)) { drag = null; return; }   // up or down: not a drag
    if (Math.abs(dx) < TAP_SLOP) return;
    drag.moving = true;
    try { drag.el.setPointerCapture(e.pointerId); } catch { /* the pointer is already gone */ }
  }
  let d = e.clientX - drag.x;
  drag.x = e.clientX;
  if (drag.pull && Math.sign(d) === -Math.sign(drag.pull)) {   // heading back: undo the pull before the view moves
    const back = Math.sign(d) * Math.min(Math.abs(d), Math.abs(drag.pull));
    drag.pull += back;
    d -= back;
  }
  const v = target(), before = v && display.view(v).x;
  drag.pull += v ? display.pan(v, d) : d;
  if (v && display.view(v).x !== before) drag.pannedCity = v.id;
  placePitchSpot();
});
addEventListener('pointerup', e => {
  if (!drag || e.pointerId !== drag.id) return;
  const { moving, pull, touch, x0, y0, t0, el, closing, pannedCity } = drag;
  drag = null;
  if (!moving) {
    if (el === $('#screen') && !closing && isTap(e.clientX - x0, e.clientY - y0, e.timeStamp - t0)) tapScene(e.clientX, e.clientY, touch, t0);
    return;
  }
  dragClick = true;                                 // the click this release fires ends a drag: it isn't a click
  setTimeout(() => { dragClick = false; });
  // Once per completed gesture, only if the view moved: taps, cancelled drags and pure edge swipes don't count.
  if (pannedCity) track('scene_panned', { city: pannedCity, input: e.pointerType, music_playing: lastState === 'playing' });
  const dx = e.clientX - x0, dy = e.clientY - y0;
  if (touch && Math.abs(pull) >= 48 && Math.abs(dx) > 1.5 * Math.abs(dy)) stepCity(pull < 0 ? 1 : -1, 'swipe');
});
addEventListener('pointercancel', e => { if (drag?.id === e.pointerId) drag = null; });
addEventListener('click', e => { if (dragClick) { dragClick = false; e.stopPropagation(); e.preventDefault(); } }, true);   // e.g. not the billboard's card, nor its links

/* ---------- boot ---------- */
// Start idle callbacks only after every popup and its state have been initialized.
for (const ev of ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart']) addEventListener(ev, wake, { passive: true });
wake();
startRollHint();
fitScreen();                                        // now that the dock has its city chip
// A city page shows its city; old #city links still work (and move to the city's page URL).
const fromHash = () => location.hash.slice(1);
addEventListener('hashchange', () => showCity(fromHash(), { source: 'link' }));
tickClocks();
const pickOnArrival = !OBS && new URLSearchParams(location.search).has('cities');
const linked = byId(fromHash()) ? fromHash() : byId(pageCity) ? pageCity : null;
// The home page shows the last city; a first visit (none kept yet) lands on tonight's city, and OBS on Paris.
const home = store.get('city', (!OBS && tonightId()) || 'paris');
(linked ? showCity(linked, { instant: true, source: 'link' })
  : showCity(byId(home) ? home : 'paris', { instant: true, source: 'initial' })
).then(() => {
  if (pickOnArrival) {
    const url = new URL(location.href);
    url.searchParams.delete('cities');
    history.replaceState(history.state, '', url.href);
    openPicker('link');
  }
  preloadScenes();
  startNews();
  alerts.start({ city: target()?.id ?? null, quiet: () => !document.hidden && $('#toast').hidden });
});
booted = true;                                      // sceneFailed: from here on, a missing scene isn't this file stopping half-way

// offline copy (sw.js): made after the first load, refreshed after every later one
if ('serviceWorker' in navigator && location.protocol !== 'file:') {
  addEventListener('load', () => {
    navigator.serviceWorker.register(new URL('sw.js', ROOT)).catch(e => console.info('[offline] unavailable:', e.message));
    navigator.serviceWorker.controller?.postMessage('refresh');
  });
}
addEventListener('appinstalled', () => track('app_installed'));
